import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Modal from 'react-modal';

// Стили для уведомления (анимация)
const notificationStyles = `
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.8);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.2);
      opacity: 0.7;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
  
  @keyframes glow {
    0% {
      box-shadow: 0 0 5px rgba(67, 233, 123, 0.5);
    }
    50% {
      box-shadow: 0 0 20px rgba(67, 233, 123, 0.8);
    }
    100% {
      box-shadow: 0 0 5px rgba(67, 233, 123, 0.5);
    }
  }
`;

// Вставляем стили в head один раз
if (typeof document !== 'undefined') {
  if (!document.head.querySelector('style[data-notification-styles]')) {
    const styleSheet = document.createElement('style');
    styleSheet.setAttribute('data-notification-styles', 'true');
    styleSheet.textContent = notificationStyles;
    document.head.appendChild(styleSheet);
  }
}

// Указываем root для react-modal (лучше оставить для доступности)
if (typeof Modal !== 'undefined' && typeof document !== 'undefined') {
  try {
    Modal.setAppElement('#root');
  } catch (e) {
    // ignore in environments where #root doesn't exist
  }
}

// Глобальный сервис для управления фоновыми таймерами отработки
class BackgroundTimerService {
  constructor() {
    this.timers = new Map(); // Map<timerId, { type, startTime, savedSeconds, requiredSeconds, leaveId, userId, username, date, selectedLeaves }>
    this.intervalId = null;
    this.onCompleteCallback = null;
    this.socket = null; // WebSocket соединение для отправки событий HR/Admin
    this.selectedDate = null; // Текущая выбранная дата
  }

  // Установка WebSocket соединения
  setSocket(socket) {
    this.socket = socket;
  }

  // Установка выбранной даты
  setSelectedDate(date) {
    this.selectedDate = date;
  }

  // Запуск глобального интервала
  start() {
    if (this.intervalId) return;
    
    this.intervalId = setInterval(() => {
      this.updateTimers();
    }, 1000);
  }

  // Остановка глобального интервала
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  // Добавление таймера
  addTimer(timerId, config) {
    this.timers.set(timerId, {
      ...config,
      startTime: Date.now(),
      lastUpdate: Date.now()
    });
    this.saveToLocalStorage();
    this.start();
  }

  // Удаление таймера
  removeTimer(timerId) {
    this.timers.delete(timerId);
    this.saveToLocalStorage();
    if (this.timers.size === 0) {
      this.stop();
    }
  }

  // Обновление таймеров
  updateTimers() {
    const now = Date.now();
    const timersToRemove = [];
    
    for (const [timerId, timer] of this.timers.entries()) {
      const elapsed = Math.floor((now - timer.startTime) / 1000);
      const totalSeconds = timer.savedSeconds + elapsed;
      
      // Проверяем, достигнуто ли требуемое время
      // Используем строгое сравнение >= и останавливаем сразу при достижении
      if (timer.requiredSeconds > 0 && totalSeconds >= timer.requiredSeconds) {
        // Ограничиваем время до требуемого (на случай небольшой переработки)
        const finalSeconds = Math.min(totalSeconds, timer.requiredSeconds);
        
        console.log(`✅ [BackgroundTimer] Таймер ${timerId} завершен: ${Math.floor(finalSeconds / 60)} минут из ${Math.floor(timer.requiredSeconds / 60)} требуемых`);
        
        // Вызываем callback при завершении с ограниченным временем
        if (this.onCompleteCallback) {
          this.onCompleteCallback(timerId, timer, finalSeconds);
        }
        
        // Показываем браузерное уведомление
        this.showBrowserNotification(timer);
        
        // Помечаем таймер для удаления (не удаляем сразу, чтобы избежать изменения Map во время итерации)
        timersToRemove.push(timerId);
      } else {
        // Обновляем время последнего обновления
        timer.lastUpdate = now;
        
        // Отправляем WebSocket событие для HR/Admin каждую секунду
        // Это важно, чтобы HR/Admin видели лайв таймер даже когда модалка закрыта
        if (this.socket && timer.userId && timer.username) {
          const updateData = {
            userId: timer.userId,
            username: timer.username,
            date: timer.date || this.selectedDate || new Date().toISOString().slice(0, 10),
            workedMinutes: Math.floor(totalSeconds / 60),
            requiredMinutes: 0,
            status: 'running',
            isRunning: true,
            selectedLeaves: timer.selectedLeaves || [],
            workType: timer.type,
            currentSeconds: totalSeconds
          };
          
          this.socket.emit('worktime_update', updateData);
          
          // Логирование для отладки (только раз в 10 секунд)
          if (Math.floor(totalSeconds) % 10 === 0) {
            console.log('📤 [BackgroundTimer] Sending worktime_update:', {
              username: timer.username,
              userId: timer.userId,
              currentSeconds: totalSeconds,
              selectedLeaves: timer.selectedLeaves,
              workType: timer.type,
              socketConnected: this.socket.connected
            });
          }
        }
      }
    }
    
    // Удаляем завершенные таймеры
    for (const timerId of timersToRemove) {
      this.removeTimer(timerId);
    }
    
    this.saveToLocalStorage();
  }

  // Получение текущего времени таймера
  getCurrentSeconds(timerId) {
    const timer = this.timers.get(timerId);
    if (!timer) return 0;
    
    const now = Date.now();
    const elapsed = Math.floor((now - timer.startTime) / 1000);
    return timer.savedSeconds + elapsed;
  }

  // Сохранение в localStorage
  saveToLocalStorage() {
    try {
      const data = Array.from(this.timers.entries()).map(([id, timer]) => ({
        id,
        ...timer,
        startTime: timer.startTime,
        lastUpdate: timer.lastUpdate
      }));
      localStorage.setItem('backgroundWorktimeTimers', JSON.stringify(data));
    } catch (e) {
      console.error('❌ Failed to save timers to localStorage:', e);
    }
  }

  // Загрузка из localStorage (сохраняем startTime — время не обнуляется при обновлении/перезапуске сервера)
  loadFromLocalStorage() {
    try {
      const data = localStorage.getItem('backgroundWorktimeTimers');
      if (!data) return;

      const timers = JSON.parse(data);
      const now = Date.now();

      for (const timer of timers) {
        // Сохраняем оригинальный startTime, чтобы getCurrentSeconds считал savedSeconds + (now - startTime)
        this.timers.set(timer.id, {
          ...timer,
          startTime: timer.startTime != null ? timer.startTime : now,
          lastUpdate: timer.lastUpdate != null ? timer.lastUpdate : now
        });
      }

      if (this.timers.size > 0) {
        this.start();
      }
    } catch (e) {
      console.error('❌ Failed to load timers from localStorage:', e);
    }
  }

  // Показ браузерного уведомления
  showBrowserNotification(timer) {
    if (!('Notification' in window)) {
      console.log('⚠️ Браузер не поддерживает уведомления');
      return;
    }

    // Запрашиваем разрешение, если еще не запрашивали
    if (Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          this.createNotification(timer);
        }
      });
    } else if (Notification.permission === 'granted') {
      this.createNotification(timer);
    }
  }

  // Создание уведомления
  createNotification(timer) {
    const workTypeNames = {
      'offhours': 'Отработка в нерабочее время',
      'weekdays': 'Отработка по будням',
      'weekends': 'Отработка по выходным'
    };
    
    const workTypeName = workTypeNames[timer.type] || 'Отработка';
    const minutes = Math.floor(timer.requiredSeconds / 60);
    
    // Воспроизводим звук
    try {
      const audio = new Audio('/news.mp3'); // Используем существующий звук уведомлений
      audio.volume = 0.7;
      audio.play().catch(e => console.log('Ошибка воспроизведения звука:', e));
    } catch (e) {
      console.log('Ошибка создания аудио:', e);
    }
    
    const notification = new Notification('✅ Отработка завершена!', {
      body: `${workTypeName}: отработано ${minutes} минут из ${minutes} требуемых.`,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `worktime-${timer.id}`,
      requireInteraction: true, // Требует взаимодействия пользователя - уведомление не закроется автоматически
      silent: false // Включаем звук
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // НЕ закрываем уведомление автоматически - пользователь должен закрыть его вручную
  }

  // Установка callback для завершения
  setOnCompleteCallback(callback) {
    this.onCompleteCallback = callback;
  }
}

// Создаем глобальный экземпляр сервиса
const backgroundTimerService = new BackgroundTimerService();

// Ключ localStorage для состояния "на паузе" (чтобы после обновления страницы время не обнулялось)
const PAUSED_WORKTIME_KEY = 'worktimePausedState';

function getPausedWorktimeState() {
  try {
    const raw = localStorage.getItem(PAUSED_WORKTIME_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function savePausedWorktimeState(userId, date, type, savedSeconds) {
  try {
    const state = getPausedWorktimeState();
    const uid = String(userId);
    const d = String(date).slice(0, 10);
    if (!state[uid]) state[uid] = {};
    if (!state[uid][d]) state[uid][d] = {};
    state[uid][d][type] = savedSeconds;
    localStorage.setItem(PAUSED_WORKTIME_KEY, JSON.stringify(state));
  } catch (_) {}
}

function loadPausedWorktimeState(userId, date) {
  try {
    const state = getPausedWorktimeState();
    const uid = String(userId);
    const d = String(date).slice(0, 10);
    return state[uid]?.[d] || {};
  } catch {
    return {};
  }
}

function clearPausedWorktimeState(userId, date, type) {
  try {
    const state = getPausedWorktimeState();
    const uid = String(userId);
    const d = String(date).slice(0, 10);
    if (state[uid]?.[d]) {
      delete state[uid][d][type];
      if (Object.keys(state[uid][d]).length === 0) delete state[uid][d];
      if (Object.keys(state[uid]).length === 0) delete state[uid];
      localStorage.setItem(PAUSED_WORKTIME_KEY, JSON.stringify(state));
    }
  } catch (_) {}
}

// Загружаем таймеры при загрузке модуля
if (typeof window !== 'undefined') {
  backgroundTimerService.loadFromLocalStorage();
}

function LeavesWorktimeModal({ isOpen, onRequestClose, token: propToken, initialFilter, onInitialFilterConsumed }) {
  console.log('🔍 [LeavesWorktimeModal] Component rendered, isOpen:', isOpen);
  
  // Флаг для предотвращения повторного показа уведомления после закрытия
  const worktimeCompletedDismissedRef = useRef(false);
  // UI state
  const [data, setData] = useState([]); // base leave obligations array
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [worktimeMap, setWorktimeMap] = useState({}); // minutes keyed by username (legacy)
  const [worktimeByUserId, setWorktimeByUserId] = useState({}); // minutes keyed by userId (robust)
  const [weekendRunning, setWeekendRunning] = useState(false);
  const [weekendStartTs, setWeekendStartTs] = useState(null); // timestamp in ms
  const [weekendTodayMinutes, setWeekendTodayMinutes] = useState(0); // minutes accumulated on server today
  const [weekdayRunning, setWeekdayRunning] = useState(false);
  const [weekdayStartTs, setWeekdayStartTs] = useState(null);
  const [weekdayTodayMinutes, setWeekdayTodayMinutes] = useState(0);
  const [notification, setNotification] = useState(null);
  const [filter, setFilter] = useState('pending'); // 'pending' | 'completed'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [nameFilter, setNameFilter] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [userRole, setUserRole] = useState('employee');
  const [currentUser, setCurrentUser] = useState(null);
  const [verifiedMap, setVerifiedMap] = useState({});
  // Данные таймеров всех пользователей для HR/Admin (в реальном времени)
  const [realtimeUpdates, setRealtimeUpdates] = useState({}); // { username: { workedMinutes, status, isRunning } }
  const [allUsersTimers, setAllUsersTimers] = useState({}); // Данные таймеров всех пользователей
  
  // Новые состояния для выбора отгулов и таймера
  const [selectedLeaves, setSelectedLeaves] = useState(new Set()); // Set с ID выбранных отгулов
  const [timerSeconds, setTimerSeconds] = useState(0); // Секунды текущего таймера
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState(null); // Время начала таймера
  const [goalAchieved, setGoalAchieved] = useState(false);
  const [dataSaved, setDataSaved] = useState(false); // Флаг для предотвращения двойного сохранения
  const savingRef = useRef(false); // Ref для более надежного отслеживания процесса сохранения
  const lastSavedSecondsRef = useRef(0); // Отслеживание уже сохраненных секунд для предотвращения двойного подсчета
  const activitySessionIdRef = useRef(null); // ID сессии мониторинга активности (при отработке)
  const [isTimerMinimized, setIsTimerMinimized] = useState(() => {
    const saved = localStorage.getItem('timerMinimized');
    return saved === 'true';
  }); // Минимизирован ли таймер
  
  // Состояние для отключения проверки рабочего времени (доступно всем с паролем)
  const [workTimeCheckDisabled, setWorkTimeCheckDisabled] = useState(() => {
    const saved = localStorage.getItem('workTimeCheckDisabled');
    return saved === 'true';
  });
  
  // Состояние для модального окна ввода пароля
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  
  // Режим работы отработки: 'offhours' (нерабочее время), 'weekdays' (будни), 'weekends' (выходные), null (не выбран)
  const [workMode, setWorkMode] = useState(null);
  
  // Состояния для открытия модалок каждого типа отработки
  const [offhoursModalOpen, setOffhoursModalOpen] = useState(false);
  const [weekdaysModalOpen, setWeekdaysModalOpen] = useState(false);
  const [weekendsModalOpen, setWeekendsModalOpen] = useState(false);
  
  // Отдельные состояния таймеров для каждого режима работы
  const [offhoursTimerSeconds, setOffhoursTimerSeconds] = useState(0);
  const [offhoursTimerRunning, setOffhoursTimerRunning] = useState(false);
  const [offhoursStartTime, setOffhoursStartTime] = useState(null);
  const [offhoursGoalAchieved, setOffhoursGoalAchieved] = useState(false);
  const offhoursSavedSecondsRef = useRef(0); // Уже сохраненное время для нерабочего времени
  const [offhoursIsMinimized, setOffhoursIsMinimized] = useState(false);
  
  const [weekdaysTimerSeconds, setWeekdaysTimerSeconds] = useState(0);
  const [weekdaysTimerRunning, setWeekdaysTimerRunning] = useState(false);
  const [weekdaysStartTime, setWeekdaysStartTime] = useState(null);
  const [weekdaysGoalAchieved, setWeekdaysGoalAchieved] = useState(false);
  const weekdaysSavedSecondsRef = useRef(0); // Уже сохраненное время для будней
  const [weekdaysIsMinimized, setWeekdaysIsMinimized] = useState(false);
  
  const [weekendsTimerSeconds, setWeekendsTimerSeconds] = useState(0);
  const [weekendsTimerRunning, setWeekendsTimerRunning] = useState(false);
  const [weekendsStartTime, setWeekendsStartTime] = useState(null);
  const [weekendsGoalAchieved, setWeekendsGoalAchieved] = useState(false);
  const weekendsSavedSecondsRef = useRef(0); // Уже сохраненное время для выходных
  const [weekendsIsMinimized, setWeekendsIsMinimized] = useState(false);
  
  // Фильтры для каждой модалки
  const [offhoursFilter, setOffhoursFilter] = useState('pending');
  const [weekdaysFilter, setWeekdaysFilter] = useState('pending');
  const [weekendsFilter, setWeekendsFilter] = useState('pending');

  // Модалка активности сотрудника (только для admin/HR)
  const [activityModalLeaveId, setActivityModalLeaveId] = useState(null);
  const [activityModalData, setActivityModalData] = useState(null);
  const [activityModalLoading, setActivityModalLoading] = useState(false);
  const [activityModalTab, setActivityModalTab] = useState('sites'); // 'sites' | 'screenshots' | 'applications'
  const [activityModalPreviewUrl, setActivityModalPreviewUrl] = useState(null); // полный просмотр скриншота
  const [workoffCleanupLoading, setWorkoffCleanupLoading] = useState(false);

  // Пока модалка «глаза» открыта — подгружаем данные каждые 4 сек (скрины/приложения приходят не сразу после Старт)
  useEffect(() => {
    if (activityModalLeaveId == null) return;
    const lid = activityModalLeaveId;
    const fetchActivity = () => {
      fetch(`/api/activity-session/by-leave/${lid}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      })
        .then(res => res.json().catch(() => ({})))
        .then(json => {
          if (json.success) {
            setActivityModalData(json);
          }
        })
        .catch(() => {});
    };
    const interval = setInterval(fetchActivity, 4000);
    return () => clearInterval(interval);
  }, [activityModalLeaveId]);

  // Единая проверка «HR или Админ»: userRole, currentUser.role и localStorage (на случай задержки loadData)
  const isHROrAdmin = useMemo(() => {
    let r = (userRole || currentUser?.role || '').toString().toLowerCase().trim();
    if (!r && typeof window !== 'undefined') {
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        r = (stored?.role || '').toString().toLowerCase().trim();
      } catch (_) {}
    }
    return r === 'hr' || r === 'admin' || r.includes('admin');
  }, [userRole, currentUser?.role]);

  // Ref для звука
  const audioRef = useRef(null);

  // safe mounted ref
  const mountedRef = useRef(false);

  // Флаг, чтобы не слать повторные запросы activity-session/start при восстановлении сессии
  const activitySessionEnsureInProgressRef = useRef(false);

  // refs to avoid stale closures in intervals
  const refs = useRef({
    currentUser: null,
    data: [],
    worktimeMap: {},
    weekendRunning: false,
    weekdayRunning: false,
    weekendStartTs: null,
    weekdayStartTs: null,
    weekendTodayMinutes: 0,
    weekdayTodayMinutes: 0,
    filter: 'all',
    nameFilter: ''
  });

  // flag to avoid re-triggering completion logic multiple times
  const completionHandledRef = useRef(false);
  

  // unify token usage
  const getToken = () => propToken || localStorage.getItem('token') || '';

  // Локальный триггер на ПК пользователя: запуск/остановка скрипта сбора скриншотов при Старт/Стоп таймера
  const ACTIVITY_TRIGGER_URL = 'http://127.0.0.1:8765';
  const notifyLocalTriggerStart = (sessionId, leaveId) => {
    if (!sessionId || !leaveId) return;
    const url = `${ACTIVITY_TRIGGER_URL}/start?sessionId=${encodeURIComponent(sessionId)}&leaveId=${encodeURIComponent(leaveId)}`;
    fetch(url)
      .then((r) => {
        if (!r.ok) console.warn('📸 [trigger] /start returned', r.status);
        return r.json().catch(() => ({}));
      })
      .then((data) => {
        if (data && data.ok) console.log('📸 [trigger] Script started on this PC');
        else if (data && data.error) console.warn('📸 [trigger]', data.error);
      })
      .catch((e) => console.warn('📸 [trigger] Failed to reach 127.0.0.1:8765 — is the trigger running on this PC?', e.message));
  };
  const notifyLocalTriggerStop = (sessionId) => {
    const q = sessionId != null && sessionId !== '' ? `?sessionId=${encodeURIComponent(sessionId)}` : '';
    fetch(`${ACTIVITY_TRIGGER_URL}/stop${q}`).catch(() => {});
  };

  // Normalize usernames to avoid mismatches like "Крис Прад (1)" vs "Крис Прад"
  const normalizeUsername = (name) => {
    if (!name) return '';
    return String(name).replace(/\s*\(\d+\)\s*$/, '').trim();
  };

  // Robust "is this row for current user?" check (by userId first, then normalized name)
  const isRowForCurrentUser = (row) => {
    const rowUserId = row?.userId;
    const currentId = currentUser?.id ?? currentUser?.userId;
    if (rowUserId != null && currentId != null && Number(rowUserId) === Number(currentId)) return true;

    const rowName = normalizeUsername(row?.username || row?.fio || '');
    const curName = normalizeUsername(currentUser?.username || currentUser?.fio || '');
    return Boolean(rowName && curName && rowName === curName);
  };

  // WebSocket connection for real-time updates
  const [socket, setSocket] = useState(null);

  // helpers to sync refs
  useEffect(() => { refs.current.currentUser = currentUser; }, [currentUser]);
  useEffect(() => { refs.current.data = data; }, [data]);
  useEffect(() => { refs.current.worktimeMap = worktimeMap; }, [worktimeMap]);
  useEffect(() => { refs.current.worktimeByUserId = worktimeByUserId; }, [worktimeByUserId]);
  useEffect(() => { refs.current.weekendRunning = weekendRunning; }, [weekendRunning]);
  useEffect(() => { refs.current.weekdayRunning = weekdayRunning; }, [weekdayRunning]);
  useEffect(() => { refs.current.weekendStartTs = weekendStartTs; }, [weekendStartTs]);
  useEffect(() => { refs.current.weekdayStartTs = weekdayStartTs; }, [weekdayStartTs]);
  useEffect(() => { refs.current.weekendTodayMinutes = weekendTodayMinutes; }, [weekendTodayMinutes]);
  useEffect(() => { refs.current.weekdayTodayMinutes = weekdayTodayMinutes; }, [weekdayTodayMinutes]);
  useEffect(() => { refs.current.filter = filter; }, [filter]);
  useEffect(() => { refs.current.nameFilter = nameFilter; }, [nameFilter]);
  
  // Refs для хранения ID таймеров в глобальном сервисе
  const backgroundTimerIdsRef = useRef({
    offhours: null,
    weekdays: null,
    weekends: null
  });

  // Инициализация callback для завершения отработки и восстановление состояния
  useEffect(() => {
    backgroundTimerService.setOnCompleteCallback((timerId, timer, totalSeconds) => {
      console.log('✅ [BackgroundTimer] Отработка завершена:', { timerId, timer, totalSeconds });
      
      // Сохраняем отработку
      if (totalSeconds >= 60 && timer.leaveId) {
        const requiredMinutes = timer.requiredMinutes ?? Math.ceil((timer.requiredSeconds || 0) / 60);
        const alreadyWorkedMinutes = timer.alreadyWorkedMinutes ?? 0;
        const atGoal = (timer.requiredSeconds || 0) > 0 && totalSeconds >= timer.requiredSeconds;
        const minutesToSave = atGoal
          ? Math.max(0, requiredMinutes - alreadyWorkedMinutes)
          : Math.floor((totalSeconds - (timer.savedSeconds || 0)) / 60);
        if (minutesToSave > 0) {
          const saveDate = timer.date || new Date().toISOString().slice(0, 10);
          const token = getToken();
          fetch('/api/leaves/add-worktime-isolated', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              leaveId: timer.leaveId,
              minutes: minutesToSave,
              date: saveDate,
              type: timer.type
            })
          }).then(res => {
            if (!res.ok) throw new Error('Failed to save worktime');
            return res.json();
          }).then(() => {
            console.log('✅ [BackgroundTimer] Отработка сохранена:', minutesToSave, 'мин');
          }).catch(err => {
            console.error('❌ [BackgroundTimer] Ошибка сохранения отработки:', err);
          });
        }
      }
      
      // Остановка сессии мониторинга активности при завершении таймера
      if (timer.leaveId) {
        try {
          notifyLocalTriggerStop();
          fetch('/api/activity-session/stop', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({ leaveId: timer.leaveId })
          }).catch(e => console.warn('Activity session stop (on complete) failed:', e));
        } catch (e) {
          console.warn('Activity session stop (on complete) failed:', e);
        }
        activitySessionIdRef.current = null;
      }

      // Обновляем состояние компонента, если он открыт
      if (timer.type === 'offhours') {
        setOffhoursTimerRunning(false);
        setOffhoursTimerSeconds(totalSeconds);
        offhoursSavedSecondsRef.current = totalSeconds;
      } else if (timer.type === 'weekdays') {
        setWeekdaysTimerRunning(false);
        setWeekdaysTimerSeconds(totalSeconds);
        weekdaysSavedSecondsRef.current = totalSeconds;
      } else if (timer.type === 'weekends') {
        setWeekendsTimerRunning(false);
        setWeekendsTimerSeconds(totalSeconds);
        weekendsSavedSecondsRef.current = totalSeconds;
      }

      // Уведомление сотруднику поверх всех модалок (справа вверху), если приложение свёрнуто или модалка закрыта
      const requiredMinutes = timer.requiredMinutes ?? Math.ceil((timer.requiredSeconds || 0) / 60);
      const totalWorkedMinutes = Math.floor(totalSeconds / 60);
      try {
        window.dispatchEvent(new CustomEvent('worktime-completed-employee', {
          detail: {
            totalWorkedMinutes,
            requiredMinutes,
            username: timer.username || '',
            leaveId: timer.leaveId,
            workType: timer.type,
            avatar: timer.avatar
          }
        }));
      } catch (e) {
        console.warn('worktime-completed-employee dispatch failed', e);
      }
    });

    // При открытии модалки подтягиваем таймеры из localStorage (актуально после обновления страницы или перезапуска сервера)
    if (isOpen) {
      backgroundTimerService.loadFromLocalStorage();
    }

    // Восстанавливаем состояние таймеров при открытии модалки
    if (isOpen && currentUser) {
      const restoreTimer = (type, timerId) => {
        const currentSeconds = backgroundTimerService.getCurrentSeconds(timerId);
        if (currentSeconds > 0) {
          const now = Date.now();
          if (type === 'offhours') {
            setOffhoursTimerSeconds(currentSeconds);
            offhoursSavedSecondsRef.current = currentSeconds;
            setOffhoursTimerRunning(true);
            setOffhoursStartTime(now);
            backgroundTimerIdsRef.current.offhours = timerId;
          } else if (type === 'weekdays') {
            setWeekdaysTimerSeconds(currentSeconds);
            weekdaysSavedSecondsRef.current = currentSeconds;
            setWeekdaysTimerRunning(true);
            setWeekdaysStartTime(now);
            backgroundTimerIdsRef.current.weekdays = timerId;
          } else if (type === 'weekends') {
            setWeekendsTimerSeconds(currentSeconds);
            weekendsSavedSecondsRef.current = currentSeconds;
            setWeekendsTimerRunning(true);
            setWeekendsStartTime(now);
            backgroundTimerIdsRef.current.weekends = timerId;
          }
        }
      };

      // Проверяем активные таймеры напрямую из глобального сервиса (для текущего пользователя)
      const userId = currentUser.id || currentUser.userId;
      if (userId) {
        // Ищем таймеры для текущего пользователя
        for (const [timerId, timer] of backgroundTimerService.timers.entries()) {
          if (Number(timer.userId) === Number(userId)) {
            if (timer.type === 'offhours' && !backgroundTimerIdsRef.current.offhours) {
              restoreTimer('offhours', timerId);
            } else if (timer.type === 'weekdays' && !backgroundTimerIdsRef.current.weekdays) {
              restoreTimer('weekdays', timerId);
            } else if (timer.type === 'weekends' && !backgroundTimerIdsRef.current.weekends) {
              restoreTimer('weekends', timerId);
            }
          }
        }
      }
      
      // Также проверяем сохраненные ID (на случай, если они были установлены ранее)
      const timerId = backgroundTimerIdsRef.current.offhours;
      if (timerId && backgroundTimerService.timers.has(timerId)) {
        restoreTimer('offhours', timerId);
      }
      
      const weekdaysTimerId = backgroundTimerIdsRef.current.weekdays;
      if (weekdaysTimerId && backgroundTimerService.timers.has(weekdaysTimerId)) {
        restoreTimer('weekdays', weekdaysTimerId);
      }
      
      const weekendsTimerId = backgroundTimerIdsRef.current.weekends;
      if (weekendsTimerId && backgroundTimerService.timers.has(weekendsTimerId)) {
        restoreTimer('weekends', weekendsTimerId);
      }

      // Восстанавливаем состояние "на паузе" (после обновления страницы или перезахода в модалку)
      if (userId && selectedDate) {
        const paused = loadPausedWorktimeState(userId, selectedDate);
        if (!backgroundTimerIdsRef.current.offhours && paused.offhours != null && paused.offhours > 0) {
          const sec = Math.floor(paused.offhours);
          setOffhoursTimerSeconds(sec);
          offhoursSavedSecondsRef.current = sec;
        }
        if (!backgroundTimerIdsRef.current.weekdays && paused.weekdays != null && paused.weekdays > 0) {
          const sec = Math.floor(paused.weekdays);
          setWeekdaysTimerSeconds(sec);
          weekdaysSavedSecondsRef.current = sec;
        }
        if (!backgroundTimerIdsRef.current.weekends && paused.weekends != null && paused.weekends > 0) {
          const sec = Math.floor(paused.weekends);
          setWeekendsTimerSeconds(sec);
          weekendsSavedSecondsRef.current = sec;
        }
      }
    }
  }, [isOpen, currentUser, selectedDate]);

  // Логирование изменений realtimeUpdates для HR/Admin
  useEffect(() => {
    if ((userRole === 'hr' || userRole === 'admin') && Object.keys(realtimeUpdates).length > 0) {
      console.log('📊 [CLIENT] realtimeUpdates changed:', {
        keys: Object.keys(realtimeUpdates),
        data: realtimeUpdates,
        userRole
      });
    }
  }, [realtimeUpdates, userRole]);

  // Таймер для нового интерфейса отработки
  useEffect(() => {
    let interval = null;
    if (isTimerRunning && !goalAchieved) {
      interval = setInterval(() => {
        setTimerSeconds(prevSeconds => {
          const newSeconds = prevSeconds + 1;
          const workedMinutes = Math.floor(newSeconds / 60);
          const requiredMinutes = getSelectedLeavesRequiredMinutes();
          
          // Сохраняем состояние каждые 5 секунд для экономии ресурсов
          if (newSeconds % 5 === 0) {
            saveTimerState(true, timerStartTime, Array.from(selectedLeaves), newSeconds);
            
            // Отправляем обновление прогресса каждые 5 секунд
            if (socket && currentUser) {
              socket.emit('new_timer_progress', {
                userId: currentUser.id,
                username: currentUser.username,
                selectedLeaves: Array.from(selectedLeaves),
                currentSeconds: newSeconds,
                workedMinutes: Math.floor(newSeconds / 60),
                requiredMinutes: requiredMinutes,
                progress: requiredMinutes > 0 ? Math.min(100, Math.round((Math.floor(newSeconds / 60) / requiredMinutes) * 100)) : 0,
                date: selectedDate
              });
            }
          }
          
          // КРИТИЧЕСКАЯ ПРОВЕРКА: достигли ли цели для КОНКРЕТНОГО отгула - МОМЕНТАЛЬНО ОСТАНАВЛИВАЕМ ТАЙМЕР!
          // Вычисляем общее отработанное время с учетом уже сохраненного
          const totalWorkedMinutesFromSeconds = Math.floor(newSeconds / 60);
          if (requiredMinutes > 0 && totalWorkedMinutesFromSeconds >= requiredMinutes && !goalAchieved) {
            console.log(`🎯 ЦЕЛЬ ДОСТИГНУТА! Отработано: ${totalWorkedMinutesFromSeconds} мин (общее), требовалось: ${requiredMinutes} мин`);
            
            // Сразу останавливаем мониторинг активности (триггер + сессия на сервере)
            const sessionIdForStop = activitySessionIdRef.current;
            notifyLocalTriggerStop(sessionIdForStop);
            const leaveIdForStop = Array.from(selectedLeaves)[0];
            if (sessionIdForStop != null || leaveIdForStop != null) {
              fetch('/api/activity-session/stop', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                body: JSON.stringify(sessionIdForStop != null ? { sessionId: sessionIdForStop } : { leaveId: leaveIdForStop })
              }).catch(e => console.warn('Activity session stop (goal achieved) failed:', e));
            }
            activitySessionIdRef.current = null;
            
            // МОМЕНТАЛЬНО останавливаем таймер - устанавливаем точное требуемое время
            const exactRequiredSeconds = requiredMinutes * 60;
            
            // Цель достигнута для выбранного отгула! ПОЛНОСТЬЮ останавливаем таймер
            setIsTimerRunning(false);
            setGoalAchieved(true);
            
            // Играем звук и показываем уведомление
            playNotificationSound();
            setNotification({
              type: 'success',
              message: `🎉 Отгул полностью отработан! Всего отработано: ${totalWorkedMinutesFromSeconds} минут. Статус изменен на "Отработано".`,
              show: true,
              persistent: false
            });
            
            // Автоматически сохраняем результат и обновляем статус отгула
            setTimeout(async () => {
              try {
                const leaveId = Array.from(selectedLeaves)[0];
                
                // Получаем уже отработанные минуты из базы данных
                const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
                const alreadyWorkedHours = selectedLeave ? (Number(selectedLeave.workedHours) || 0) : 0;
                const alreadyWorkedMinutes = Math.round(alreadyWorkedHours * 60);
                const alreadyWorkedSeconds = alreadyWorkedMinutes * 60;
                
                // Инициализируем lastSavedSecondsRef если еще не установлен
                if (lastSavedSecondsRef.current === 0 && alreadyWorkedSeconds > 0) {
                  lastSavedSecondsRef.current = alreadyWorkedSeconds;
                }
                
                // При достижении цели сохраняем ровно требуемую дельту (requiredMinutes - alreadyWorkedMinutes),
                // чтобы в БД и в таблице было ровно 100% и "Отработано" = требуемое время (избегаем 9 мин/90% при таймере 10:00)
                const minutesToSave = Math.max(0, requiredMinutes - alreadyWorkedMinutes);
                const totalWorkedMinutes = alreadyWorkedMinutes + minutesToSave;
                
                console.log(`🎯 Начинаем сохранение отработанного отгула:`, { 
                  leaveId, 
                  newSeconds: newSeconds,
                  lastSavedSeconds: lastSavedSecondsRef.current,
                  minutesToSave,
                  alreadyWorkedMinutes,
                  totalWorkedMinutes,
                  requiredMinutes 
                });
                
                // Проверяем, не идет ли уже процесс сохранения
                if (savingRef.current || dataSaved) {
                  console.log(`⚠️ Сохранение уже выполняется или завершено, пропускаем автосохранение`);
                  return;
                }
                
                // Устанавливаем флаги, что данные сохраняются автоматически
                savingRef.current = true;
                setDataSaved(true);
                
                // Шаг 1: Сохраняем ровно требуемую дельту минут (endpoint добавляет к существующим)
                console.log(`💾 Шаг 1: Сохраняем ${minutesToSave} минут для отгула ${leaveId}. Общее будет: ${totalWorkedMinutes} минут`);
                await saveWorktimeDataForSpecificLeave(leaveId, minutesToSave);
                
                // Обновляем lastSavedSecondsRef после успешного сохранения
                lastSavedSecondsRef.current = newSeconds;
                console.log(`✅ Обновлен lastSavedSecondsRef: ${lastSavedSecondsRef.current} секунд`);
                console.log(`✅ Шаг 1 завершен: время отработки сохранено`);
                
                // Шаг 2: Обновляем статус отгула на "отработано"
                console.log(`🔄 Шаг 2: Обновляем статус отгула ${leaveId} на 'completed'`);
                await updateLeaveStatus(leaveId, 'completed');
                console.log(`✅ Шаг 2 завершен: статус обновлен на 'completed'`);
                
                // Шаг 3: Перезагружаем данные БЕЗ перезагрузки страницы
                console.log(`🔄 Шаг 3: Перезагружаем данные отгулов`);
                await reloadLeavesData();
                console.log(`✅ Шаг 3 завершен: данные перезагружены`);
                
                // Шаг 4: Автоматически переключаем на фильтр "Отработанные" чтобы показать результат
                console.log(`🔄 Шаг 4: Переключаем на фильтр 'completed'`);
                setFilter('completed');
                
                // Шаг 5: Очищаем состояние таймера
                console.log(`🧹 Шаг 5: Очищаем состояние таймера`);
                setTimerSeconds(exactRequiredSeconds);
                setSelectedLeaves(new Set());
                setTimerStartTime(null);
                lastSavedSecondsRef.current = 0; // Сбрасываем счетчик сохраненного времени
                clearTimerState();
                
                // Показываем успешное уведомление с переходом к отработанным
                setNotification({
                  type: 'success',
                  message: `🎉 Отгул ${leaveId} успешно отработан (${requiredMinutes} мин) и сохранен в "Отработанные"!`,
                  show: true,
                  persistent: false
                });
                
                console.log(`🎉 ВСЕ ШАГИ ЗАВЕРШЕНЫ! Отгул ${leaveId} отработан и сохранен.`);
                
                // Закрываем уведомление через 5 секунд
                setTimeout(() => {
                  setNotification(null);
                }, 5000);
              } catch (e) {
                console.error('❌ КРИТИЧЕСКАЯ ОШИБКА при сохранении отработанного отгула:', e);
                setNotification({
                  type: 'error',
                  message: `❌ Ошибка сохранения: ${e.message}. Проверьте консоль для деталей.`,
                  show: true,
                  persistent: true
                });
              } finally {
                // Всегда сбрасываем флаги сохранения в автоматическом сохранении
                savingRef.current = false;
                console.log(`✅ Флаги сохранения сброшены после автосохранения`);
              }
            }, 2000);
            
            // ВАЖНО: возвращаем ТОЧНОЕ требуемое время в секундах, останавливаем счетчик
            return exactRequiredSeconds;
          }
          
          return newSeconds;
        });
      }, 1000);
    } else if (!isTimerRunning && timerSeconds !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timerSeconds, selectedLeaves, timerStartTime, goalAchieved]);

  // Таймер для режима "нерабочее время"
  useEffect(() => {
    let interval = null;
    if (offhoursTimerRunning && offhoursStartTime) {
      interval = setInterval(() => {
        setOffhoursTimerSeconds(prev => {
          const elapsed = Math.floor((Date.now() - offhoursStartTime) / 1000);
          // Общее время = уже сохраненное + новое время с момента старта
          const totalSeconds = offhoursSavedSecondsRef.current + elapsed;
          
          // Проверяем, достигнуто ли требуемое время отработки
          if (selectedLeaves.size > 0) {
            const leaveId = Array.from(selectedLeaves)[0];
            const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
            if (selectedLeave) {
              const requiredMinutes = Math.round((Number(selectedLeave.requiredHours) || Number(selectedLeave.leaveHours) || 0) * 60);
              const requiredSeconds = requiredMinutes * 60;
              
              // Если достигнуто требуемое время, автоматически останавливаем таймер
              // Используем строгое сравнение >= и останавливаем сразу при достижении
              if (requiredSeconds > 0 && totalSeconds >= requiredSeconds) {
                console.log(`✅ [Offhours] Достигнуто требуемое время: ${requiredMinutes} минут (${requiredSeconds} сек), текущее: ${totalSeconds} сек, останавливаем таймер`);
                
                // Сразу останавливаем мониторинг активности (триггер + сессия на сервере)
                const sessionIdForStop = activitySessionIdRef.current;
                notifyLocalTriggerStop(sessionIdForStop);
                if (sessionIdForStop != null || leaveId != null) {
                  fetch('/api/activity-session/stop', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                    body: JSON.stringify(sessionIdForStop != null ? { sessionId: sessionIdForStop } : { leaveId })
                  }).catch(e => console.warn('Activity session stop (offhours goal) failed:', e));
                }
                activitySessionIdRef.current = null;
                
                // Останавливаем таймер СРАЗУ, не давая ему продолжить
                setOffhoursTimerRunning(false);
                
                // Удаляем таймер из глобального сервиса
                removeTimerFromBackgroundService('offhours');
                
                // Ограничиваем время до требуемого (на случай небольшой переработки)
                const finalSeconds = Math.min(totalSeconds, requiredSeconds);
                
                // Сразу показываем уведомление поверх всех модалок (до сохранения), чтобы было видно при свёрнутом приложении
                try {
                  window.dispatchEvent(new CustomEvent('worktime-completed-employee', {
                    detail: {
                      totalWorkedMinutes: requiredMinutes,
                      requiredMinutes,
                      username: currentUser?.username || currentUser?.fio || '',
                      leaveId,
                      workType: 'offhours',
                      avatar: currentUser?.avatar || selectedLeave?.avatar
                    }
                  }));
                } catch (e) { console.warn('worktime-completed-employee dispatch failed', e); }
                
                // Сохраняем отработку: при достижении цели сохраняем ровно требуемую дельту (requiredMinutes - alreadyWorkedMinutes),
                // чтобы в таблице и в БД было ровно 100% и "Отработано" = требуемое время (избегаем 9 мин/90% при таймере 10:00)
                if (finalSeconds >= 60) {
                  const alreadyWorkedMinutes = Math.round((Number(selectedLeave.workedHours) || 0) * 60);
                  const minutesToSave = Math.max(0, requiredMinutes - alreadyWorkedMinutes);
                  if (minutesToSave > 0) {
                    saveWorktimeDataForSpecificLeave(leaveId, minutesToSave, 'offhours').then(async () => {
                      offhoursSavedSecondsRef.current = finalSeconds;
                      setOffhoursTimerSeconds(finalSeconds);
                      setData(prev => prev.map(r => ((r.id || r.leaveId) === leaveId && isRowForCurrentUser(r)) ? { ...r, workedHours: requiredMinutes / 60 } : r));
                      setNotification({
                        type: 'success',
                        message: `✅ Отработка завершена! Отработано ${Math.floor(finalSeconds / 60)} минут из ${requiredMinutes} требуемых. Статус: На проверке у HR.`,
                        show: true,
                        persistent: false
                      });
                      setTimeout(() => setNotification(null), 5000);
                      await reloadLeavesData();
                      setFilter('completed');
                      const sock = socket || (typeof window !== 'undefined' && window.socket);
                      if (sock && currentUser) {
                        sock.emit('worktime_completed', {
                          leaveId,
                          userId: currentUser.id || currentUser.userId,
                          username: currentUser.username || currentUser.fio,
                          avatar: currentUser?.avatar,
                          date: selectedDate,
                          totalWorkedMinutes: requiredMinutes,
                          requiredMinutes,
                          workType: 'offhours'
                        });
                      }
                    });
                  }
                }
                
                return finalSeconds;
              }
            }
          }
          
          // Отправляем обновление в реальном времени для HR/Admin
          if (socket && currentUser && selectedLeaves.size > 0) {
            const leaveId = Array.from(selectedLeaves)[0];
            const username = currentUser.username || currentUser.fio;
            const updateData = {
              userId: currentUser.id,
              username: username,
              date: selectedDate,
              workedMinutes: Math.floor(totalSeconds / 60),
              requiredMinutes: 0,
              status: 'running',
              isRunning: true,
              selectedLeaves: Array.from(selectedLeaves),
              workType: 'offhours',
              currentSeconds: totalSeconds
            };
            
            socket.emit('worktime_update', updateData);
            
            // Логирование для отладки (только раз в 10 секунд, чтобы не засорять консоль)
            if (Math.floor(totalSeconds) % 10 === 0) {
              console.log('📤 [CLIENT Offhours] Sending worktime_update:', {
                username,
                userId: currentUser.id,
                currentSeconds: totalSeconds,
                selectedLeaves: Array.from(selectedLeaves),
                workType: 'offhours',
                socketConnected: socket.connected,
                fullData: updateData
              });
            }
          }
          
          return totalSeconds;
        });
      }, 1000);
    } else if (!offhoursTimerRunning && offhoursTimerSeconds > 0) {
      // При остановке таймера сохраняем текущее значение
      // Это нужно для правильного продолжения
      // Отправляем событие об остановке
      if (socket && currentUser && selectedLeaves.size > 0) {
        socket.emit('worktime_update', {
          userId: currentUser.id,
          username: currentUser.username || currentUser.fio,
          date: selectedDate,
          workedMinutes: Math.floor(offhoursTimerSeconds / 60),
          requiredMinutes: 0,
          status: 'paused',
          isRunning: false,
          selectedLeaves: Array.from(selectedLeaves),
          workType: 'offhours',
          currentSeconds: offhoursTimerSeconds
        });
      }
      
      // Удаляем таймер из глобального сервиса при остановке и сохраняем время "на паузе" для восстановления после обновления страницы
      removeTimerFromBackgroundService('offhours');
      if (currentUser && selectedDate && offhoursTimerSeconds > 0) {
        savePausedWorktimeState(currentUser.id || currentUser.userId, selectedDate, 'offhours', offhoursTimerSeconds);
      }
    }

    // Добавляем таймер в глобальный сервис при запуске
    if (offhoursTimerRunning && !backgroundTimerIdsRef.current.offhours) {
      const leaveId = Array.from(selectedLeaves)[0];
      if (leaveId) {
        addTimerToBackgroundService('offhours', offhoursSavedSecondsRef.current, null, leaveId);
      }
    }
    
    // Синхронизируем состояние с глобальным сервисом каждую секунду
    // Это важно для правильного отображения времени после закрытия/открытия модалки
    if (offhoursTimerRunning && backgroundTimerIdsRef.current.offhours) {
      const currentSeconds = backgroundTimerService.getCurrentSeconds(backgroundTimerIdsRef.current.offhours);
      if (currentSeconds > 0) {
        // Обновляем состояние, если разница больше 0.5 секунды (для плавности)
        if (Math.abs(currentSeconds - offhoursTimerSeconds) > 0.5) {
          setOffhoursTimerSeconds(currentSeconds);
          // Также обновляем savedSecondsRef для правильного продолжения
          const elapsed = Math.floor((Date.now() - offhoursStartTime) / 1000);
          offhoursSavedSecondsRef.current = currentSeconds - elapsed;
        }
      }
    } else if (!offhoursTimerRunning && backgroundTimerIdsRef.current.offhours) {
      // Если таймер не запущен в компоненте, но есть в глобальном сервисе - восстанавливаем
      const currentSeconds = backgroundTimerService.getCurrentSeconds(backgroundTimerIdsRef.current.offhours);
      if (currentSeconds > 0) {
        setOffhoursTimerSeconds(currentSeconds);
        offhoursSavedSecondsRef.current = currentSeconds;
        setOffhoursTimerRunning(true);
        setOffhoursStartTime(Date.now());
      }
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [offhoursTimerRunning, offhoursStartTime, offhoursTimerSeconds, socket, currentUser, selectedLeaves, selectedDate, data]);

  // Таймер для режима "будни"
  useEffect(() => {
    let interval = null;
    if (weekdaysTimerRunning && weekdaysStartTime) {
      interval = setInterval(() => {
        setWeekdaysTimerSeconds(prev => {
          const elapsed = Math.floor((Date.now() - weekdaysStartTime) / 1000);
          // Общее время = уже сохраненное + новое время с момента старта
          const totalSeconds = weekdaysSavedSecondsRef.current + elapsed;
          
          // Проверяем, достигнуто ли требуемое время отработки
          if (selectedLeaves.size > 0) {
            const leaveId = Array.from(selectedLeaves)[0];
            const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
            if (selectedLeave) {
              const requiredMinutes = Math.round((Number(selectedLeave.requiredHours) || Number(selectedLeave.leaveHours) || 0) * 60);
              const requiredSeconds = requiredMinutes * 60;
              
              // Если достигнуто требуемое время, автоматически останавливаем таймер
              // Используем строгое сравнение >= и останавливаем сразу при достижении
              if (requiredSeconds > 0 && totalSeconds >= requiredSeconds) {
                console.log(`✅ [Weekdays] Достигнуто требуемое время: ${requiredMinutes} минут (${requiredSeconds} сек), текущее: ${totalSeconds} сек, останавливаем таймер`);
                
                // Сразу останавливаем мониторинг активности (триггер + сессия на сервере)
                const sessionIdForStop = activitySessionIdRef.current;
                notifyLocalTriggerStop(sessionIdForStop);
                if (sessionIdForStop != null || leaveId != null) {
                  fetch('/api/activity-session/stop', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                    body: JSON.stringify(sessionIdForStop != null ? { sessionId: sessionIdForStop } : { leaveId })
                  }).catch(e => console.warn('Activity session stop (weekdays goal) failed:', e));
                }
                activitySessionIdRef.current = null;
                
                // Останавливаем таймер СРАЗУ, не давая ему продолжить
                setWeekdaysTimerRunning(false);
                
                // Удаляем таймер из глобального сервиса
                removeTimerFromBackgroundService('weekdays');
                
                // Ограничиваем время до требуемого (на случай небольшой переработки)
                const finalSeconds = Math.min(totalSeconds, requiredSeconds);
                
                // Сразу показываем уведомление поверх всех модалок (до сохранения)
                try {
                  window.dispatchEvent(new CustomEvent('worktime-completed-employee', {
                    detail: {
                      totalWorkedMinutes: requiredMinutes,
                      requiredMinutes,
                      username: currentUser?.username || currentUser?.fio || '',
                      leaveId,
                      workType: 'weekdays',
                      avatar: currentUser?.avatar || selectedLeave?.avatar
                    }
                  }));
                } catch (e) { console.warn('worktime-completed-employee dispatch failed', e); }
                
                // Сохраняем отработку: при достижении цели сохраняем ровно требуемую дельту (requiredMinutes - alreadyWorkedMinutes),
                // чтобы в таблице и в БД было ровно 100% и "Отработано" = требуемое время (избегаем 9 мин/90% при таймере 10:00)
                if (finalSeconds >= 60) {
                  const alreadyWorkedMinutes = Math.round((Number(selectedLeave.workedHours) || 0) * 60);
                  const minutesToSave = Math.max(0, requiredMinutes - alreadyWorkedMinutes);
                  if (minutesToSave > 0) {
                    saveWorktimeDataForSpecificLeave(leaveId, minutesToSave, 'weekdays').then(async () => {
                      weekdaysSavedSecondsRef.current = finalSeconds;
                      setWeekdaysTimerSeconds(finalSeconds);
                      setData(prev => prev.map(r => ((r.id || r.leaveId) === leaveId && isRowForCurrentUser(r)) ? { ...r, workedHours: requiredMinutes / 60 } : r));
                      setNotification({
                        type: 'success',
                        message: `✅ Отработка завершена! Отработано ${Math.floor(finalSeconds / 60)} минут из ${requiredMinutes} требуемых. Статус: На проверке у HR.`,
                        show: true,
                        persistent: false
                      });
                      setTimeout(() => setNotification(null), 5000);
                      await reloadLeavesData();
                      setFilter('completed');
                      const sockW = socket || (typeof window !== 'undefined' && window.socket);
                      if (sockW && currentUser) {
                        sockW.emit('worktime_completed', {
                          leaveId,
                          userId: currentUser.id || currentUser.userId,
                          username: currentUser.username || currentUser.fio,
                          avatar: currentUser?.avatar,
                          date: selectedDate,
                          totalWorkedMinutes: requiredMinutes,
                          requiredMinutes,
                          workType: 'weekdays'
                        });
                      }
                    });
                  }
                }
                
                return finalSeconds;
              }
            }
          }
          
          // Отправляем обновление в реальном времени для HR/Admin
          if (socket && currentUser && selectedLeaves.size > 0) {
            const leaveId = Array.from(selectedLeaves)[0];
            socket.emit('worktime_update', {
              userId: currentUser.id,
              username: currentUser.username || currentUser.fio,
              date: selectedDate,
              workedMinutes: Math.floor(totalSeconds / 60),
              requiredMinutes: 0,
              status: 'running',
              isRunning: true,
              selectedLeaves: Array.from(selectedLeaves),
              workType: 'weekdays',
              currentSeconds: totalSeconds
            });
          }
          
          return totalSeconds;
        });
      }, 1000);
    } else if (!weekdaysTimerRunning && weekdaysTimerSeconds > 0) {
      // При остановке таймера сохраняем текущее значение
      // Это нужно для правильного продолжения
      // Отправляем событие об остановке
      if (socket && currentUser && selectedLeaves.size > 0) {
        socket.emit('worktime_update', {
          userId: currentUser.id,
          username: currentUser.username || currentUser.fio,
          date: selectedDate,
          workedMinutes: Math.floor(weekdaysTimerSeconds / 60),
          requiredMinutes: 0,
          status: 'paused',
          isRunning: false,
          selectedLeaves: Array.from(selectedLeaves),
          workType: 'weekdays',
          currentSeconds: weekdaysTimerSeconds
        });
      }
      
      // Удаляем таймер из глобального сервиса при остановке и сохраняем время "на паузе"
      removeTimerFromBackgroundService('weekdays');
      if (currentUser && selectedDate && weekdaysTimerSeconds > 0) {
        savePausedWorktimeState(currentUser.id || currentUser.userId, selectedDate, 'weekdays', weekdaysTimerSeconds);
      }
    }

    // Добавляем таймер в глобальный сервис при запуске
    if (weekdaysTimerRunning && !backgroundTimerIdsRef.current.weekdays) {
      const leaveId = Array.from(selectedLeaves)[0];
      if (leaveId) {
        addTimerToBackgroundService('weekdays', weekdaysSavedSecondsRef.current, null, leaveId);
      }
    }
    
    // Синхронизируем состояние с глобальным сервисом каждую секунду
    // Это важно для правильного отображения времени после закрытия/открытия модалки
    if (weekdaysTimerRunning && backgroundTimerIdsRef.current.weekdays) {
      const currentSeconds = backgroundTimerService.getCurrentSeconds(backgroundTimerIdsRef.current.weekdays);
      if (currentSeconds > 0) {
        // Обновляем состояние, если разница больше 0.5 секунды (для плавности)
        if (Math.abs(currentSeconds - weekdaysTimerSeconds) > 0.5) {
          setWeekdaysTimerSeconds(currentSeconds);
          // Также обновляем savedSecondsRef для правильного продолжения
          const elapsed = Math.floor((Date.now() - weekdaysStartTime) / 1000);
          weekdaysSavedSecondsRef.current = currentSeconds - elapsed;
        }
      }
    } else if (!weekdaysTimerRunning && backgroundTimerIdsRef.current.weekdays) {
      // Если таймер не запущен в компоненте, но есть в глобальном сервисе - восстанавливаем
      const currentSeconds = backgroundTimerService.getCurrentSeconds(backgroundTimerIdsRef.current.weekdays);
      if (currentSeconds > 0) {
        setWeekdaysTimerSeconds(currentSeconds);
        weekdaysSavedSecondsRef.current = currentSeconds;
        setWeekdaysTimerRunning(true);
        setWeekdaysStartTime(Date.now());
      }
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [weekdaysTimerRunning, weekdaysStartTime, weekdaysTimerSeconds, socket, currentUser, selectedLeaves, selectedDate, data]);

  // Таймер для режима "выходные"
  useEffect(() => {
    let interval = null;
    if (weekendsTimerRunning && weekendsStartTime) {
      interval = setInterval(() => {
        setWeekendsTimerSeconds(prev => {
          const elapsed = Math.floor((Date.now() - weekendsStartTime) / 1000);
          // Общее время = уже сохраненное + новое время с момента старта
          const totalSeconds = weekendsSavedSecondsRef.current + elapsed;
          
          // Проверяем, достигнуто ли требуемое время отработки
          if (selectedLeaves.size > 0) {
            const leaveId = Array.from(selectedLeaves)[0];
            const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
            if (selectedLeave) {
              const requiredMinutes = Math.round((Number(selectedLeave.requiredHours) || Number(selectedLeave.leaveHours) || 0) * 60);
              const requiredSeconds = requiredMinutes * 60;
              
              // Если достигнуто требуемое время, автоматически останавливаем таймер
              // Используем строгое сравнение >= и останавливаем сразу при достижении
              if (requiredSeconds > 0 && totalSeconds >= requiredSeconds) {
                console.log(`✅ [Weekends] Достигнуто требуемое время: ${requiredMinutes} минут (${requiredSeconds} сек), текущее: ${totalSeconds} сек, останавливаем таймер`);
                
                // Сразу останавливаем мониторинг активности (триггер + сессия на сервере)
                const sessionIdForStop = activitySessionIdRef.current;
                notifyLocalTriggerStop(sessionIdForStop);
                if (sessionIdForStop != null || leaveId != null) {
                  fetch('/api/activity-session/stop', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                    body: JSON.stringify(sessionIdForStop != null ? { sessionId: sessionIdForStop } : { leaveId })
                  }).catch(e => console.warn('Activity session stop (weekends goal) failed:', e));
                }
                activitySessionIdRef.current = null;
                
                // Останавливаем таймер СРАЗУ, не давая ему продолжить
                setWeekendsTimerRunning(false);
                
                // Удаляем таймер из глобального сервиса
                removeTimerFromBackgroundService('weekends');
                
                // Ограничиваем время до требуемого (на случай небольшой переработки)
                const finalSeconds = Math.min(totalSeconds, requiredSeconds);
                
                // Сразу показываем уведомление поверх всех модалок (до сохранения)
                try {
                  window.dispatchEvent(new CustomEvent('worktime-completed-employee', {
                    detail: {
                      totalWorkedMinutes: requiredMinutes,
                      requiredMinutes,
                      username: currentUser?.username || currentUser?.fio || '',
                      leaveId,
                      workType: 'weekends',
                      avatar: currentUser?.avatar || selectedLeave?.avatar
                    }
                  }));
                } catch (e) { console.warn('worktime-completed-employee dispatch failed', e); }
                
                // Сохраняем отработку: при достижении цели сохраняем ровно требуемую дельту (requiredMinutes - alreadyWorkedMinutes),
                // чтобы в таблице и в БД было ровно 100% и "Отработано" = требуемое время (избегаем 9 мин/90% при таймере 10:00)
                if (finalSeconds >= 60) {
                  const alreadyWorkedMinutes = Math.round((Number(selectedLeave.workedHours) || 0) * 60);
                  const minutesToSave = Math.max(0, requiredMinutes - alreadyWorkedMinutes);
                  if (minutesToSave > 0) {
                    saveWorktimeDataForSpecificLeave(leaveId, minutesToSave, 'weekends').then(async () => {
                      weekendsSavedSecondsRef.current = finalSeconds;
                      setWeekendsTimerSeconds(finalSeconds);
                      setData(prev => prev.map(r => ((r.id || r.leaveId) === leaveId && isRowForCurrentUser(r)) ? { ...r, workedHours: requiredMinutes / 60 } : r));
                      setNotification({
                        type: 'success',
                        message: `✅ Отработка завершена! Отработано ${Math.floor(finalSeconds / 60)} минут из ${requiredMinutes} требуемых. Статус: На проверке у HR.`,
                        show: true,
                        persistent: false
                      });
                      setTimeout(() => setNotification(null), 5000);
                      await reloadLeavesData();
                      setFilter('completed');
                      const sockWe = socket || (typeof window !== 'undefined' && window.socket);
                      if (sockWe && currentUser) {
                        sockWe.emit('worktime_completed', {
                          leaveId,
                          userId: currentUser.id || currentUser.userId,
                          username: currentUser.username || currentUser.fio,
                          avatar: currentUser?.avatar,
                          date: selectedDate,
                          totalWorkedMinutes: requiredMinutes,
                          requiredMinutes,
                          workType: 'weekends'
                        });
                      }
                    });
                  }
                }
                
                return finalSeconds;
              }
            }
          }
          
          // Отправляем обновление в реальном времени для HR/Admin
          if (socket && currentUser && selectedLeaves.size > 0) {
            const leaveId = Array.from(selectedLeaves)[0];
            socket.emit('worktime_update', {
              userId: currentUser.id,
              username: currentUser.username || currentUser.fio,
              date: selectedDate,
              workedMinutes: Math.floor(totalSeconds / 60),
              requiredMinutes: 0,
              status: 'running',
              isRunning: true,
              selectedLeaves: Array.from(selectedLeaves),
              workType: 'weekends',
              currentSeconds: totalSeconds
            });
          }
          
          return totalSeconds;
        });
      }, 1000);
    } else if (!weekendsTimerRunning && weekendsTimerSeconds > 0) {
      // При остановке таймера сохраняем текущее значение
      // Это нужно для правильного продолжения
      // Отправляем событие об остановке
      if (socket && currentUser && selectedLeaves.size > 0) {
        socket.emit('worktime_update', {
          userId: currentUser.id,
          username: currentUser.username || currentUser.fio,
          date: selectedDate,
          workedMinutes: Math.floor(weekendsTimerSeconds / 60),
          requiredMinutes: 0,
          status: 'paused',
          isRunning: false,
          selectedLeaves: Array.from(selectedLeaves),
          workType: 'weekends',
          currentSeconds: weekendsTimerSeconds
        });
      }
      
      // Удаляем таймер из глобального сервиса при остановке и сохраняем время "на паузе"
      removeTimerFromBackgroundService('weekends');
      if (currentUser && selectedDate && weekendsTimerSeconds > 0) {
        savePausedWorktimeState(currentUser.id || currentUser.userId, selectedDate, 'weekends', weekendsTimerSeconds);
      }
    }

    // Добавляем таймер в глобальный сервис при запуске
    if (weekendsTimerRunning && !backgroundTimerIdsRef.current.weekends) {
      const leaveId = Array.from(selectedLeaves)[0];
      if (leaveId) {
        addTimerToBackgroundService('weekends', weekendsSavedSecondsRef.current, null, leaveId);
      }
    }
    
    // Синхронизируем состояние с глобальным сервисом каждую секунду
    // Это важно для правильного отображения времени после закрытия/открытия модалки
    if (weekendsTimerRunning && backgroundTimerIdsRef.current.weekends) {
      const currentSeconds = backgroundTimerService.getCurrentSeconds(backgroundTimerIdsRef.current.weekends);
      if (currentSeconds > 0) {
        // Обновляем состояние, если разница больше 0.5 секунды (для плавности)
        if (Math.abs(currentSeconds - weekendsTimerSeconds) > 0.5) {
          setWeekendsTimerSeconds(currentSeconds);
          // Также обновляем savedSecondsRef для правильного продолжения
          const elapsed = Math.floor((Date.now() - weekendsStartTime) / 1000);
          weekendsSavedSecondsRef.current = currentSeconds - elapsed;
        }
      }
    } else if (!weekendsTimerRunning && backgroundTimerIdsRef.current.weekends) {
      // Если таймер не запущен в компоненте, но есть в глобальном сервисе - восстанавливаем
      const currentSeconds = backgroundTimerService.getCurrentSeconds(backgroundTimerIdsRef.current.weekends);
      if (currentSeconds > 0) {
        setWeekendsTimerSeconds(currentSeconds);
        weekendsSavedSecondsRef.current = currentSeconds;
        setWeekendsTimerRunning(true);
        setWeekendsStartTime(Date.now());
      }
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [weekendsTimerRunning, weekendsStartTime, weekendsTimerSeconds, socket, currentUser, selectedLeaves, selectedDate, data]);

  // Восстановление состояния таймера при загрузке компонента
  // ОТКЛЮЧЕНО: не восстанавливаем из localStorage, чтобы избежать проблем с устаревшими значениями
  // Вместо этого всегда синхронизируем с актуальными данными из БД при запуске таймера
  // useEffect(() => {
  //   if (isOpen && currentUser) {
  //     restoreTimerState().then(restored => {
  //       if (restored) {
  //         setNotification({
  //           type: 'info',
  //           message: '🔄 Состояние таймера восстановлено (сервер + localStorage)',
  //           show: true,
  //           persistent: false
  //         });
  //         setTimeout(() => setNotification(null), 3000);
  //       }
  //     }).catch(e => {
  //       console.error('Ошибка восстановления таймера:', e);
  //     });
  //   }
  // }, [isOpen, currentUser]);

  // small helpers
  const safeParseTs = (ts) => {
    if (!ts) return null;
    const n = Number(ts);
    if (!Number.isNaN(n)) return n;
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getSessionMinutes = (startTs) => {
    const s = safeParseTs(startTs);
    if (!s) return 0;
    const diffMs = Date.now() - s;
    return Math.max(0, Math.floor(diffMs / 60000));
  };

  const getTotalWeekendMinutes = () => {
    const base = Number(refs.current.weekendTodayMinutes || 0);
    const running = refs.current.weekendRunning && refs.current.weekendStartTs ? getSessionMinutes(refs.current.weekendStartTs) : 0;
    return base + running;
  };

  const getTotalWeekdayMinutes = () => {
    const base = Number(refs.current.weekdayTodayMinutes || 0);
    const running = refs.current.weekdayRunning && refs.current.weekdayStartTs ? getSessionMinutes(refs.current.weekdayStartTs) : 0;
    return base + running;
  };

  // Только текущие «бегущие» минуты (без накопленных за сегодня) — чтобы не удваивать
  const getRunningWeekendMinutes = () => {
    return refs.current.weekendRunning && refs.current.weekendStartTs ? getSessionMinutes(refs.current.weekendStartTs) : 0;
  };

  const getRunningWeekdayMinutes = () => {
    return refs.current.weekdayRunning && refs.current.weekdayStartTs ? getSessionMinutes(refs.current.weekdayStartTs) : 0;
  };

  // Получение динамических минут для любого пользователя (для HR/Admin)
  const getTotalWeekendMinutesForUser = (username) => {
    if (!username) return 0;
    const isCurrentUser = username === currentUser?.username;
    
    if (isCurrentUser) {
      // В таблице учитываем только «бегущие» минуты у текущего пользователя
      return getRunningWeekendMinutes();
    }
    
    // Для других пользователей берем данные из allUsersTimers
    const userTimer = allUsersTimers[username];
    if (!userTimer) return 0;
    
    const running = userTimer.weekendRunning && userTimer.weekendStartTs ? getSessionMinutes(userTimer.weekendStartTs) : 0;
    return running;
  };

  const getTotalWeekdayMinutesForUser = (username) => {
    if (!username) return 0;
    const isCurrentUser = username === currentUser?.username;
    
    if (isCurrentUser) {
      // В таблице учитываем только «бегущие» минуты у текущего пользователя
      return getRunningWeekdayMinutes();
    }
    
    // Для других пользователей берем данные из allUsersTimers
    const userTimer = allUsersTimers[username];
    if (!userTimer) return 0;
    
    const running = userTimer.weekdayRunning && userTimer.weekdayStartTs ? getSessionMinutes(userTimer.weekdayStartTs) : 0;
    return running;
  };

  const isTodayWeekend = () => {
    const d = new Date();
    const day = d.getDay();
    return day === 0 || day === 6;
  };

  const isTodayWeekday = () => {
    const d = new Date();
    const day = d.getDay();
    return day >= 1 && day <= 5;
  };

  const isWorktimeAllowed = () => {
    // Если проверка времени отключена паролем - разрешаем всегда
    if (workTimeCheckDisabled) {
      return true;
    }
    
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    const workStart = 9 * 60;
    const workEnd = 18 * 60;
    return minutes < workStart || minutes > workEnd;
  };

  // Проверка времени для отработки в нерабочее время: только по будням, 6:00-8:59 и 18:01-23:00
  const isOffhoursWorktimeAllowed = () => {
    if (workTimeCheckDisabled) {
      return true;
    }
    
    // Проверяем, что сегодня будний день (понедельник-пятница)
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = воскресенье, 6 = суббота
    const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5; // Понедельник-пятница
    
    if (!isWeekday) {
      return false; // Не будний день - отработка в нерабочее время недоступна
    }
    
    const totalMinutes = now.getHours() * 60 + now.getMinutes();
    
    // Первый период: 6:00 - 8:59 (360 - 539 минут)
    const period1Start = 6 * 60;  // 6:00
    const period1End = 8 * 60 + 59; // 8:59
    
    // Второй период: 18:01 - 23:00 (1081 - 1380 минут)
    const period2Start = 18 * 60 + 1; // 18:01
    const period2End = 23 * 60; // 23:00
    
    const inPeriod1 = totalMinutes >= period1Start && totalMinutes <= period1End;
    const inPeriod2 = totalMinutes >= period2Start && totalMinutes <= period2End;
    
    return inPeriod1 || inPeriod2;
  };

  // Проверка времени для отработки по будням: 6:00-23:00
  const isWeekdaysWorktimeAllowed = () => {
    if (workTimeCheckDisabled) {
      return true;
    }
    
    const now = new Date();
    const totalMinutes = now.getHours() * 60 + now.getMinutes();
    
    const start = 6 * 60;  // 6:00
    const end = 23 * 60;   // 23:00
    
    return totalMinutes >= start && totalMinutes <= end;
  };

  // Проверка времени для отработки по выходным: 6:00-23:00, только в субботу и воскресенье
  const isWeekendsWorktimeAllowed = () => {
    if (workTimeCheckDisabled) {
      return true;
    }
    
    // Проверяем, что сегодня выходной день (суббота или воскресенье)
    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 = воскресенье, 6 = суббота
    const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
    
    if (!isWeekendDay) {
      return false; // Не выходной день - отработка по выходным недоступна
    }
    
    const totalMinutes = now.getHours() * 60 + now.getMinutes();
    
    const start = 6 * 60;  // 6:00
    const end = 23 * 60;   // 23:00
    
    return totalMinutes >= start && totalMinutes <= end;
  };

  // Проверка времени для отработки отгулов (с 7:00 до 23:00) + наличие неотработанных отгулов
  const isLeaveWorktimeAllowed = () => {
    // Если проверка времени отключена паролем - разрешаем всегда
    if (workTimeCheckDisabled) {
      return true;
    }
    
    // Проверяем время (с 7:00 до 23:00)
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    const workStart = 7 * 60;  // 7:00
    const workEnd = 23 * 60;   // 23:00
    const timeAllowed = minutes >= workStart && minutes <= workEnd;
    
    if (!timeAllowed) {
      return false;
    }
    
    // Проверяем наличие неотработанных отгулов у текущего пользователя
    if (!currentUser || !Array.isArray(data)) {
      return false;
    }
    
    // Нормализация имени - убираем суффиксы типа "(1)"
    const normalizeUsername = (name) => {
      if (!name) return '';
      return name.replace(/\s*\(\d+\)\s*$/, '').trim();
    };
    
    const currentUsername = normalizeUsername(currentUser.username || currentUser.fio || '');
    
    const userLeaves = data.filter(row => {
      const rowUsername = normalizeUsername(row.username || row.fio || '');
      const matchesByName = rowUsername === currentUsername;
      const matchesById = row.userId === currentUser.id || row.userId === currentUser.userId;
      
      // Исключаем тип remote из отработки (как отпуск и больничный)
      const isRemoteType = row.type === 'remote';
      
      return (matchesByName || matchesById) && row.status === 'approved' && !isRemoteType;
    });
    
    // Проверяем есть ли отгулы с нулевым прогрессом (неотработанные)
    const hasUnworkedLeaves = userLeaves.some(row => {
      const requiredHours = Number(row.requiredHours) || Number(row.leaveHours) || 0;
      const workedHours = Number(row.workedHours) || 0;
      const requiredMinutes = Math.round(requiredHours * 60);
      const workedMinutes = Math.round(workedHours * 60);
      const progress = requiredMinutes > 0 ? Math.min(100, Math.round((workedMinutes / requiredMinutes) * 100)) : 0;
      return progress < 100;
    });
    
    console.log('🔍 [isLeaveWorktimeAllowed] Проверка:', {
      currentUsername,
      userLeavesCount: userLeaves.length,
      hasUnworkedLeaves,
      timeAllowed,
      workTimeCheckDisabled,
      firstLeave: userLeaves[0]
    });
    
    return hasUnworkedLeaves;
  };

  const isSameDay = (isoDate) => {
    if (!isoDate) return false;
    const d = new Date(isoDate);
    const today = new Date();
    return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  };

  const isPastDay = (isoDate) => {
    if (!isoDate) return false;
    const d = new Date(isoDate);
    const today = new Date();
    d.setHours(0,0,0,0);
    today.setHours(0,0,0,0);
    return d.getTime() < today.getTime();
  };

  // compute if a user has obligation (based on loaded data + worktimeMap)
  const hasWorktimeObligation = (user) => {
    if (!user) return false;
    const row = data.find(r => (r.username || r.fio || '') === user.username);
    if (!row) return false;
    const leaveHours = Number(row.leaveHours) || 0;
    return leaveHours > 0;
  };

  // Check completion for current user using minutes (prevent rounding bugs)
  const isWorktimeCompletedForUser = (user) => {
    if (!user) return false;
    const row = refs.current.data.find(r => (r.username || r.fio || '') === user.username);
    if (!row) return false;
    const requiredMinutes = Math.round((Number(row.leaveHours) || 0) * 60);
    const mapMinutes = Number(refs.current.worktimeMap[user.username] || 0);
    const totalExtra = getTotalWeekendMinutes() + getTotalWeekdayMinutes();
    return (mapMinutes + totalExtra) >= requiredMinutes && requiredMinutes > 0;
  };

  // Проигрывание звука
  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(e => console.log("Ошибка воспроизведения звука:", e));
    }
  };

  // Открытие модального окна для ввода пароля
  const openPasswordModal = () => {
    setPasswordInput('');
    setPasswordModalOpen(true);
  };

  // Проверка пароля и переключение режима
  const handlePasswordSubmit = () => {
    const correctPassword = '1711';
    
    if (passwordInput === correctPassword) {
      // Пароль верный - переключаем режим
      const newState = !workTimeCheckDisabled;
      setWorkTimeCheckDisabled(newState);
      localStorage.setItem('workTimeCheckDisabled', newState.toString());
      
      setNotification({
        type: 'success',
        message: newState 
          ? '🔓 Проверка рабочего времени ОТКЛЮЧЕНА (режим тестирования)' 
          : '🔒 Проверка рабочего времени ВКЛЮЧЕНА (обычный режим)',
        show: true,
        persistent: false
      });
      setTimeout(() => setNotification(null), 3000);
      
      // Закрываем модальное окно
      setPasswordModalOpen(false);
      setPasswordInput('');
    } else {
      // Неверный пароль
      setNotification({
        type: 'error',
        message: '❌ Неверный пароль!',
        show: true,
        persistent: false
      });
      setTimeout(() => setNotification(null), 2000);
      setPasswordInput('');
    }
  };

  // Закрытие модального окна пароля
  const closePasswordModal = () => {
    setPasswordModalOpen(false);
    setPasswordInput('');
  };

  // Вспомогательные функции для работы с глобальным сервисом таймеров
  const addTimerToBackgroundService = (type, savedSeconds, requiredSeconds, leaveId) => {
    if (!currentUser || selectedLeaves.size === 0) return null;
    
    const timerId = `${type}-${currentUser.id}-${leaveId}-${Date.now()}`;
    const leaveIdValue = Array.from(selectedLeaves)[0];
    const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveIdValue);
    const requiredMinutes = selectedLeave ? (selectedLeave.requiredMinutes ?? Math.round((Number(selectedLeave.requiredHours) || Number(selectedLeave.leaveHours) || 0) * 60)) : 0;
    const requiredSecondsValue = requiredSeconds || (requiredMinutes * 60);
    const alreadyWorkedMinutes = selectedLeave ? (selectedLeave.workedMinutes ?? Math.round((Number(selectedLeave.workedHours) || 0) * 60)) : 0;
    
    backgroundTimerService.addTimer(timerId, {
      type,
      savedSeconds: savedSeconds || 0,
      requiredSeconds: requiredSecondsValue,
      requiredMinutes,
      alreadyWorkedMinutes,
      leaveId: leaveIdValue,
      userId: currentUser.id,
      username: currentUser.username || currentUser.fio,
      avatar: currentUser.avatar || selectedLeave?.avatar,
      date: selectedDate,
      selectedLeaves: Array.from(selectedLeaves)
    });
    
    // Сохраняем ID таймера в ref и сбрасываем сохранённое состояние "на паузе" для этого типа
    if (type === 'offhours') {
      backgroundTimerIdsRef.current.offhours = timerId;
    } else if (type === 'weekdays') {
      backgroundTimerIdsRef.current.weekdays = timerId;
    } else if (type === 'weekends') {
      backgroundTimerIdsRef.current.weekends = timerId;
    }
    clearPausedWorktimeState(currentUser.id || currentUser.userId, selectedDate, type);

    console.log(`✅ [BackgroundTimer] Добавлен таймер ${type}:`, timerId);
    return timerId;
  };

  const removeTimerFromBackgroundService = (type) => {
    const timerId = backgroundTimerIdsRef.current[type];
    if (timerId) {
      backgroundTimerService.removeTimer(timerId);
      backgroundTimerIdsRef.current[type] = null;
      console.log(`✅ [BackgroundTimer] Удален таймер ${type}:`, timerId);
    }
  };

  const updateTimerInBackgroundService = (type, savedSeconds) => {
    const timerId = backgroundTimerIdsRef.current[type];
    if (timerId) {
      // Обновляем savedSeconds в таймере
      const timer = backgroundTimerService.timers.get(timerId);
      if (timer) {
        timer.savedSeconds = savedSeconds;
        timer.startTime = Date.now(); // Сбрасываем startTime для корректного подсчета
        backgroundTimerService.saveToLocalStorage();
      }
    }
  };

  // Форматирование времени из часов в читаемый формат
  const formatTimeFromHours = (hours) => {
    if (!hours || hours <= 0) return '0 минут';
    
    const totalMinutes = Math.round(hours * 60);
    
    if (totalMinutes < 60) {
      return `${totalMinutes} минут`;
    }
    
    const fullHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    
    if (remainingMinutes === 0) {
      return fullHours === 1 ? '1 час' : `${fullHours} час`;
    }
    
    const hourText = fullHours === 1 ? '1 час' : `${fullHours} час`;
    return `${hourText} ${remainingMinutes} минут`;
  };

  // Main check that triggers stop/save + notification + reset sequence
  const handleCompletionIfNeeded = async () => {
    const user = refs.current.currentUser;
    if (!user) return;
    if (completionHandledRef.current) return; // already handled
    if (worktimeCompletedDismissedRef.current) return; // уведомление уже было закрыто
    if (!isWorktimeCompletedForUser(user)) return;

    completionHandledRef.current = true; // mark so we don't re-run

    try {
      // stop running timers on server (if any)
      if (refs.current.weekendRunning) {
        try { await stopWeekendInternal(); } catch (e) { /* ignore */ }
      }
      if (refs.current.weekdayRunning) {
        try { await stopWeekdayInternal(); } catch (e) { /* ignore */ }
      }

      // Получаем данные об отработанном времени для установки статуса
      const row = refs.current.data.find(r => (r.username || r.fio || '') === user.username);
      if (row) {
        const requiredMinutes = Math.round((Number(row.leaveHours) || 0) * 60);
        const mapMinutes = Number(refs.current.worktimeMap[user.username] || 0);
        const totalExtra = getTotalWeekendMinutes() + getTotalWeekdayMinutes();
        const totalWorkedMinutes = mapMinutes + totalExtra;

        // Устанавливаем статус "на проверке" через API
        try {
          await fetch('/api/worktime-status', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getToken()}`
            },
            body: JSON.stringify({
              userId: user.id,
              date: selectedDate,
              status: 'checking',
              workedMinutes: totalWorkedMinutes
            })
          });

          // Отправляем WebSocket уведомление о завершении отработки
          if (socket) {
            socket.emit('worktime_completed', {
              userId: user.id,
              username: user.username,
              date: selectedDate,
              totalWorkedMinutes,
              requiredMinutes
            });
          }
        } catch (e) {
          console.warn('Не удалось обновить статус:', e);
        }
      }
    } catch (e) {
      // continue anyway
    }

    // show success notification (эффектное появление)
    setNotification({
      type: 'success',
      message: ' Отработка завершена и находится на проверке у HR!\n\nВаш статус изменен на "На проверке". HR-менеджер получит уведомление и сможет подтвердить вашу отработку.',
      show: true,
      persistent: true
    });

    // Проигрываем звук
    playNotificationSound();
    // after short delay reset worktime on server & local state
    setTimeout(async () => {
      try {
        await resetWorktime();
      } catch (e) {
        // ignore
      }
    }, 1800);
  };

  // Закрытие уведомления
  const closeNotification = () => {
    setNotification(null);
    // Если уведомление было успешным, сбрасываем только флаги
    if (notification && notification.type === 'success') {
      completionHandledRef.current = false;
      worktimeCompletedDismissedRef.current = true;
    }
  };

  // ------------------ Server interactions (helpers) ------------------

  const fetchJson = async (url, opts = {}) => {
    const controller = new AbortController();
    const signal = controller.signal;
    const token = getToken();
    const headers = { ...(opts.headers || {}), Authorization: `Bearer ${token}` };
    const finalOpts = { ...opts, headers, signal };
    try {
      const res = await fetch(url, finalOpts);
      if (!res.ok) {
        // try to parse error body
        let text;
        try { text = await res.text(); } catch (_) { text = ''; }
        throw new Error(`HTTP ${res.status} ${res.statusText} ${text}`);
      }
      const json = await res.json().catch(() => ({}));
      return json;
    } finally {
      // no-op; caller may not need abort
    }
  };

  // Функция для перезагрузки данных отгулов
  const reloadLeavesData = async () => {
    try {
      console.log('🔄 Перезагружаем данные отгулов...');
      
      const rep = await fetch('/api/leaves-worktime-report', { headers: { Authorization: `Bearer ${getToken()}` } });
      if (rep.ok) {
        const json = await rep.json().catch(() => ([]));
        const newData = Array.isArray(json) ? json : [];
        setData(newData);
        console.log(`✅ Данные отгулов обновлены: ${newData.length} записей`);
        
        // Также обновляем верификации для текущей даты
        await loadVerifications(selectedDate);
      } else {
        console.error('❌ Ошибка загрузки данных отгулов:', rep.status);
      }
    } catch (e) {
      console.error('❌ Ошибка при перезагрузке данных отгулов:', e);
    }
  };

  // Загрузка подтверждений HR/Admin на выбранную дату
  const loadVerifications = async (dateIso) => {
    try {
      const day = (dateIso || new Date().toISOString().slice(0, 10)).slice(0, 10);
      const res = await fetch(`/api/leaves/verified?date=${encodeURIComponent(day)}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) { setVerifiedMap({}); return; }
      const list = await res.json().catch(() => []);
      const map = {};
      (Array.isArray(list) ? list : []).forEach(it => { if (it && it.user_id != null) map[it.user_id] = true; });
      setVerifiedMap(map);
    } catch { setVerifiedMap({}); }
  };

  // Загрузка данных таймеров всех пользователей для HR/Admin
  const loadAllUsersTimers = async () => {
    // Только для HR и админов
    if (userRole !== 'hr' && userRole !== 'admin') return;
    
    try {
      const res = await fetch('/api/all-users-timers', { 
        headers: { Authorization: `Bearer ${getToken()}` } 
      });
      if (!res.ok) { 
        setAllUsersTimers({}); 
        return; 
      }
      const data = await res.json().catch(() => ({}));
      setAllUsersTimers(data || {});
    } catch (e) {
      console.warn('Failed to load all users timers:', e);
      setAllUsersTimers({});
    }
  };

  // load status endpoints
  const loadWeekendTimer = async () => {
    try {
      const status = await fetchJson('/api/weekend-work/status');
      setWeekendRunning(!!status.running);
      setWeekendStartTs(safeParseTs(status.startTs) || null);
      setWeekendTodayMinutes(Number(status.todayMinutes) || 0);
    } catch (e) {
      setWeekendRunning(false);
      setWeekendStartTs(null);
    }
  };

  const loadWeekdayTimer = async () => {
    try {
      const status = await fetchJson('/api/weekday-work/status');
      setWeekdayRunning(!!status.running);
      setWeekdayStartTs(safeParseTs(status.startTs) || null);
      setWeekdayTodayMinutes(Number(status.todayMinutes) || 0);
    } catch (e) {
      setWeekdayRunning(false);
      setWeekdayStartTs(null);
    }
  };

  // start/stop helpers that return parsed json
  const startWeekend = async () => {
    try {
      const res = await fetch('/api/weekend-work/start', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({}) });
      if (!res.ok) throw new Error('Failed to start weekend work');
      const j = await res.json().catch(() => ({}));
      const ts = safeParseTs(j.startTs) || Date.now();
      setWeekendRunning(true);
      setWeekendStartTs(ts);
      
      // Отправляем WebSocket событие об изменении таймера
      if (socket && currentUser) {
        socket.emit('worktime_timer_changed', {
          userId: currentUser.id,
          username: currentUser.username,
          timerType: 'weekend',
          isRunning: true,
          startTime: ts
        });
      }
      
      return j;
    } catch (e) {
      console.error('startWeekend error', e);
      throw e;
    }
  };

  const stopWeekendInternal = async () => {
    // internal version used by completion logic that returns minutes
    try {
      const res = await fetch('/api/weekend-work/stop', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ date: selectedDate }) });
      if (!res.ok) throw new Error('Failed to stop weekend work');
      const j = await res.json().catch(() => ({}));
      const minutes = Number(j.minutes) || 0;
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setWeekendRunning(false);
      setWeekendStartTs(null);
      setWeekendTodayMinutes(v => Number(v || 0) + minutes);
      if (user.username) {
        setWorktimeMap(m => ({ ...(m || {}), [user.username]: (Number(m?.[user.username] || 0) + minutes) }));
      }

      // Отправляем WebSocket событие об изменении таймера
      if (socket && currentUser) {
        socket.emit('worktime_timer_changed', {
          userId: currentUser.id,
          username: currentUser.username,
          timerType: 'weekend',
          isRunning: false,
          startTime: null
        });
      }

      // check completion after update
      setTimeout(() => {
        handleCompletionIfNeeded();
      }, 100);
      return j;
    } catch (e) {
      console.error('stopWeekendInternal error', e);
      throw e;
    }
  };

  const stopWeekend = async () => {
    try {
      await stopWeekendInternal();
    } catch (e) {
      // show error if needed
      setNotification({ type: 'error', message: 'Ошибка при остановке отработки по выходным', show: true, persistent: false });
      setTimeout(() => setNotification(null), 2500);
    }
  };

  const startWeekday = async () => {
    try {
      const res = await fetch('/api/weekday-work/start', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({}) });
      if (!res.ok) throw new Error('Failed to start weekday work');
      const j = await res.json().catch(() => ({}));
      const ts = safeParseTs(j.startTs) || Date.now();
      setWeekdayRunning(true);
      setWeekdayStartTs(ts);

      // Отправляем WebSocket событие об изменении таймера
      if (socket && currentUser) {
        socket.emit('worktime_timer_changed', {
          userId: currentUser.id,
          username: currentUser.username,
          timerType: 'weekday',
          isRunning: true,
          startTime: ts
        });
      }

      return j;
    } catch (e) {
      console.error('startWeekday error', e);
      throw e;
    }
  };

  const stopWeekdayInternal = async () => {
    try {
      const res = await fetch('/api/weekday-work/stop', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ date: selectedDate }) });
      if (!res.ok) throw new Error('Failed to stop weekday work');
      const j = await res.json().catch(() => ({}));
      const minutes = Number(j.minutes) || 0;
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setWeekdayRunning(false);
      setWeekdayStartTs(null);
      setWeekdayTodayMinutes(v => Number(v || 0) + minutes);
      if (user.username) {
        setWorktimeMap(m => ({ ...(m || {}), [user.username]: (Number(m?.[user.username] || 0) + minutes) }));
      }

      // Отправляем WebSocket событие об изменении таймера
      if (socket && currentUser) {
        socket.emit('worktime_timer_changed', {
          userId: currentUser.id,
          username: currentUser.username,
          timerType: 'weekday',
          isRunning: false,
          startTime: null
        });
      }

      // check completion
      setTimeout(() => {
        handleCompletionIfNeeded();
      }, 100);
      return j;
    } catch (e) {
      console.error('stopWeekdayInternal error', e);
      throw e;
    }
  };

  const stopWeekday = async () => {
    try {
      await stopWeekdayInternal();
    } catch (e) {
      setNotification({ type: 'error', message: 'Ошибка при остановке отработки по будням', show: true, persistent: false });
      setTimeout(() => setNotification(null), 2500);
    }
  };

  const addManualMinutes = async ({ kind, minutes }) => {
    // kind: 'weekend' | 'weekday'
    if (!minutes || minutes <= 0) return;
    try {
      const url = kind === 'weekend' ? '/api/weekend-work/add-minutes' : '/api/weekday-work/add-minutes';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ minutes })
      });
      if (!res.ok) throw new Error('Failed to add minutes');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (kind === 'weekend') {
        setWeekendTodayMinutes(v => Number(v || 0) + minutes);
      } else {
        setWeekdayTodayMinutes(v => Number(v || 0) + minutes);
      }
      if (user.username) {
        setWorktimeMap(m => ({ ...(m || {}), [user.username]: (Number(m?.[user.username] || 0) + minutes) }));
      }
      handleCompletionIfNeeded();
    } catch (e) {
      console.error('addManualMinutes error', e);
      setNotification({ type: 'error', message: 'Ошибка при добавлении минут', show: true, persistent: false });
      setTimeout(() => setNotification(null), 2500);
    }
  };
  // ==================== ФУНКЦИИ ДЛЯ РАБОТЫ С ВЫБРАННЫМИ ОТГУЛАМИ ====================
  
  const toggleLeaveSelection = (leaveId) => {
    // Проверяем, что таймер не запущен
    if (isTimerRunning) {
      setNotification({
        type: 'warning',
        message: 'Нельзя изменять выбор отгулов во время работы таймера',
        show: true,
        persistent: false
      });
      setTimeout(() => setNotification(null), 2500);
      return;
    }

    // Проверяем, что отгул не отработан полностью
    const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
    if (selectedLeave && selectedLeave.status === 'completed') {
      setNotification({
        type: 'warning',
        message: 'Этот отгул уже полностью отработан и находится в архиве',
        show: true,
        persistent: false
      });
      setTimeout(() => setNotification(null), 2500);
      return;
    }

    setSelectedLeaves(prev => {
      // ВАЖНО: Разрешаем выбирать только ОДИН отгул за раз
      const newSet = new Set();
      if (!prev.has(leaveId)) {
        newSet.add(leaveId); // Выбираем только этот отгул, очищая все остальные
        
        // При смене отгула полностью сбрасываем все таймеры и сохранённое состояние,
        // чтобы не показывать отработанное время предыдущего отгула и дать отработать новый
        localStorage.removeItem('worktimeTimer');
        setTimerSeconds(0);
        setTimerStartTime(null);
        setGoalAchieved(false);
        lastSavedSecondsRef.current = 0;
        setDataSaved(false);
        // Сбрасываем таймеры вкладок (нерабочее время / будние / выходные)
        removeTimerFromBackgroundService('offhours');
        removeTimerFromBackgroundService('weekdays');
        removeTimerFromBackgroundService('weekends');
        setOffhoursTimerSeconds(0);
        offhoursSavedSecondsRef.current = 0;
        setOffhoursTimerRunning(false);
        setOffhoursStartTime(null);
        setWeekdaysTimerSeconds(0);
        weekdaysSavedSecondsRef.current = 0;
        setWeekdaysTimerRunning(false);
        setWeekdaysStartTime(null);
        setWeekendsTimerSeconds(0);
        weekendsSavedSecondsRef.current = 0;
        setWeekendsTimerRunning(false);
        setWeekendsStartTime(null);
        backgroundTimerIdsRef.current.offhours = null;
        backgroundTimerIdsRef.current.weekdays = null;
        backgroundTimerIdsRef.current.weekends = null;
        
        // Показываем информацию о выбранном отгуле
        // ВАЖНО: НЕ устанавливаем timerSeconds здесь, так как workedHours может быть неправильным
        // Вместо этого всегда синхронизируемся с актуальными данными из БД при запуске таймера
        if (selectedLeave) {
          const requiredHours = Number(selectedLeave.requiredHours) || Number(selectedLeave.leaveHours) || 0;
          const workedHours = Number(selectedLeave.workedHours) || 0;
          const requiredMinutes = Math.round(requiredHours * 60);
          const workedMinutes = Math.round(workedHours * 60);
          
          console.log(`📋 Выбран отгул:`, {
            id: selectedLeave.id || selectedLeave.leaveId,
            requiredHours: selectedLeave.requiredHours,
            leaveHours: selectedLeave.leaveHours,
            workedHours: selectedLeave.workedHours,
            calculatedMinutes: requiredMinutes,
            workedMinutes: workedMinutes,
            note: 'timerSeconds будет установлен из БД при запуске таймера',
            rawData: selectedLeave
          });
          
          let message = `Выбран отгул для изолированной отработки. Требуется: ${requiredMinutes} минут (${Math.floor(requiredMinutes/60)}ч ${requiredMinutes%60}м)`;
          if (workedMinutes > 0) {
            const remainingMinutes = requiredMinutes - workedMinutes;
            message += `. Уже отработано: ${workedMinutes} минут (из БД). Осталось: ${remainingMinutes} минут`;
          }
          
          setNotification({
            type: 'info',
            message: message,
            show: true,
            persistent: false
          });
          setTimeout(() => setNotification(null), 3000);
        }
      }
      // Если отгул уже выбран, просто очищаем выбор (newSet остается пустым)
      
      // НЕ сохраняем состояние в localStorage при выборе отгула, если таймер не запущен
      // Вместо этого всегда синхронизируемся с актуальными данными из БД при запуске таймера
      // saveTimerState(false, null, Array.from(newSet), 0);
      
      return newSet;
    });
  };

  const selectAllLeaves = () => {
    // Отключаем функцию "Выбрать все" для изолированной отработки
    setNotification({
      type: 'info',
      message: 'Выберите только один отгул для изолированной отработки',
      show: true,
      persistent: false
    });
    setTimeout(() => setNotification(null), 2500);
  };

  const clearLeaveSelection = () => {
    setSelectedLeaves(new Set());
    lastSavedSecondsRef.current = 0; // Сбрасываем счетчик сохраненного времени
    setTimerSeconds(0); // Сбрасываем таймер
    
    // Сохраняем пустой выбор в localStorage
    if (!isTimerRunning) {
      saveTimerState(false, null, [], 0);
    }
  };

  // Расчет требуемого времени на основе выбранных отгулов (общее требуемое время)
  const getSelectedLeavesRequiredMinutes = () => {
    if (selectedLeaves.size === 0) return 0;
    
    const userLeaves = data.filter(row => 
      isRowForCurrentUser(row) && 
      selectedLeaves.has(row.id || row.leaveId)
    );
    
    return userLeaves.reduce((total, leave) => {
      // Конвертируем часы в минуты - используем правильное поле
      const requiredHours = Number(leave.requiredHours) || Number(leave.leaveHours) || 0;
      console.log(`📊 Отгул ${leave.id || leave.leaveId}: требуется ${requiredHours} часов (${requiredHours * 60} минут)`);
      return total + Math.round(requiredHours * 60);
    }, 0);
  };
  
  // Расчет уже отработанного времени для выбранных отгулов
  const getSelectedLeavesWorkedMinutes = () => {
    if (selectedLeaves.size === 0) return 0;
    
    const userLeaves = data.filter(row => 
      isRowForCurrentUser(row) && 
      selectedLeaves.has(row.id || row.leaveId)
    );
    
    return userLeaves.reduce((total, leave) => {
      const workedHours = Number(leave.workedHours) || 0;
      const workedMinutes = Math.round(workedHours * 60);
      return total + workedMinutes;
    }, 0);
  };
  
  // Расчет оставшегося времени для отработки
  const getSelectedLeavesRemainingMinutes = () => {
    const required = getSelectedLeavesRequiredMinutes();
    const worked = getSelectedLeavesWorkedMinutes();
    return Math.max(0, required - worked);
  };

  // ==================== ФУНКЦИИ ДЛЯ НОВОГО ТАЙМЕРА ====================
  
  // Сохранение состояния таймера в localStorage и на сервере
  const saveTimerState = async (running, startTime, selectedLeavesArray, seconds = 0) => {
    const timerState = {
      isRunning: running,
      startTime: startTime,
      selectedLeaves: selectedLeavesArray,
      savedSeconds: seconds,
      timestamp: Date.now()
    };
    
    // Сохраняем локально
    localStorage.setItem('worktimeTimer', JSON.stringify(timerState));
    
    // Состояние сохраняется только в localStorage
    console.log('💾 Состояние таймера сохранено в localStorage');
  };

  // Восстановление состояния таймера с сервера и localStorage
  const restoreTimerState = async () => {
    try {
      let timerState = null;
      
      // Восстанавливаем состояние из localStorage
      const savedState = localStorage.getItem('worktimeTimer');
      if (savedState) {
        try {
          timerState = JSON.parse(savedState);
          console.log('🔄 Состояние таймера восстановлено из localStorage');
        } catch (e) {
          console.warn('Ошибка парсинга сохранённого состояния таймера:', e);
        }
      }
      
      if (!timerState) return false;

      const now = Date.now();
      
      // Проверяем, что сохраненное состояние не старше 24 часов
      if (now - timerState.timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('worktimeTimer');
        console.log('🗑️ Устаревшее состояние таймера очищено из localStorage');
        return false;
      }

      if (timerState.isRunning && timerState.startTime) {
        // Восстанавливаем состояние таймера из сохраненных данных
        // НЕ вычисляем время на основе старого startTime, так как таймер мог быть на паузе
        // Используем savedSeconds напрямую - это уже накопленное время
        const savedSeconds = timerState.savedSeconds || 0;
        
        // Восстанавливаем состояние
        setSelectedLeaves(new Set(timerState.selectedLeaves || []));
        setTimerSeconds(savedSeconds);
        setIsTimerRunning(true);
        // Устанавливаем новый startTime для текущей сессии (не используем старый startTime)
        // Это важно, чтобы не учитывать время паузы
        setTimerStartTime(now);
        
        return true;
      } else if (timerState.selectedLeaves && timerState.selectedLeaves.length > 0) {
        // Восстанавливаем только выбранные отгулы если таймер не запущен
        setSelectedLeaves(new Set(timerState.selectedLeaves));
        setTimerSeconds(timerState.savedSeconds || 0);
        return true;
      }
    } catch (e) {
      console.error('Ошибка восстановления состояния таймера:', e);
      localStorage.removeItem('worktimeTimer');
    }
    return false;
  };

  // Очистка сохраненного состояния таймера
  const clearTimerState = async () => {
    localStorage.removeItem('worktimeTimer');
    lastSavedSecondsRef.current = 0; // Сбрасываем счетчик сохраненного времени
    setTimerSeconds(0); // Сбрасываем таймер, чтобы не использовать старое значение
    console.log('🗑️ Состояние таймера очищено из localStorage, timerSeconds сброшен');
  };
  
  const formatTimerDisplay = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  };

  // Функция для сохранения данных отработки для конкретного отгула
  const saveWorktimeDataForSpecificLeave = async (leaveId, workedMinutes, workType = null) => {
    const today = new Date().toISOString().slice(0, 10);
    
    // Если тип не передан, определяем его по текущему режиму работы
    let finalWorkType = workType;
    if (!finalWorkType && workMode) {
      finalWorkType = workMode; // offhours, weekdays, weekends
    }
    
    console.log('💾 Auto-saving worktime for specific leave:', { 
      leaveId,
      workedMinutes, 
      date: today,
      workType: finalWorkType,
      currentUser: currentUser ? { id: currentUser.id, username: currentUser.username } : null
    });
    
    const res = await fetch('/api/leaves/add-worktime-isolated', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        Authorization: `Bearer ${getToken()}` 
      },
      body: JSON.stringify({ 
        leaveId: leaveId,
        minutes: workedMinutes,
        date: today,
        type: finalWorkType
      })
    });
    
    if (!res.ok) {
      const errorText = await res.text();
      console.error('❌ Auto-save error for specific leave:', res.status, errorText);
      if (res.status === 401) {
        try {
          const errJson = JSON.parse(errorText || '{}');
          if (errJson.error === 'TokenExpired') {
            throw new Error('Сессия истекла. Войдите снова и повторите сохранение отработки.');
          }
        } catch (parseErr) {
          if (parseErr.message && parseErr.message.includes('Сессия истекла')) throw parseErr;
        }
      }
      if (res.status === 403) {
        throw new Error('Нет прав на отработку этого отгула. Проверьте, что отгул принадлежит вам.');
      }
      throw new Error(`Не удалось сохранить отработку: ${res.status} ${errorText}`);
    }
    
    const saveResult = await res.json();
    console.log('✅ Auto-save result for specific leave:', saveResult);
    
    // Отправляем WebSocket событие об завершении отработки конкретного отгула
    if (socket && currentUser) {
      socket.emit('leave_worktime_completed', {
        userId: currentUser.id,
        username: currentUser.username,
        leaveId: leaveId,
        workedMinutes: workedMinutes,
        date: selectedDate
      });
    }
  };

  // Функция для обновления статуса отгула
  const updateLeaveStatus = async (leaveId, status) => {
    console.log('🔄 Updating leave status:', { leaveId, status });
    
    const res = await fetch('/api/leaves/update-status', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        Authorization: `Bearer ${getToken()}` 
      },
      body: JSON.stringify({ 
        leaveId: leaveId,
        status: status
      })
    });
    
    if (!res.ok) {
      const errorText = await res.text();
      console.error('❌ Error updating leave status:', res.status, errorText);
      throw new Error(`Failed to update leave status: ${res.status} ${errorText}`);
    }
    
    const updateResult = await res.json();
    console.log('✅ Leave status updated:', updateResult);
    
    return updateResult;
  };

  // Функция для сохранения данных отработки без сброса интерфейса (legacy)
  // eslint-disable-next-line no-unused-vars
  const saveWorktimeData = async (leavesArray, workedMinutes) => {
    const today = new Date().toISOString().slice(0, 10);
    
    console.log('💾 Auto-saving worktime:', { 
      workedMinutes, 
      selectedLeaves: leavesArray, 
      date: today,
      currentUser: currentUser ? { id: currentUser.id, username: currentUser.username } : null
    });
    
    const res = await fetch('/api/leaves/add-worktime', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        Authorization: `Bearer ${getToken()}` 
      },
      body: JSON.stringify({ 
        minutes: workedMinutes,
        date: today,
        selectedLeaves: leavesArray
      })
    });
    
    if (!res.ok) {
      const errorText = await res.text();
      console.error('❌ Auto-save error:', res.status, errorText);
      throw new Error(`Failed to auto-save worked time: ${res.status} ${errorText}`);
    }
    
    const saveResult = await res.json();
    console.log('✅ Auto-save result:', saveResult);
    
    // Отправляем WebSocket событие об остановке таймера
    if (socket && currentUser) {
      socket.emit('new_timer_stopped', {
        userId: currentUser.id,
        username: currentUser.username,
        selectedLeaves: leavesArray,
        workedMinutes: workedMinutes,
        date: selectedDate
      });
    }
    
    // Перезагружаем данные после успешного сохранения
    console.log('🔄 Reloading data after auto-save...');
    await loadData();
    console.log('✅ Data reloaded successfully after auto-save');
  };

  const startNewTimer = async () => {
    if (selectedLeaves.size === 0) {
      setNotification({
        type: 'error',
        message: 'Выберите отгулы для отработки',
        show: true,
        persistent: false
      });
      setTimeout(() => setNotification(null), 2500);
      return;
    }
    
    // Проверяем и логируем требуемое время перед запуском
    const requiredMinutes = getSelectedLeavesRequiredMinutes();
    console.log(`🚀 Запуск таймера: требуется ${requiredMinutes} минут для отгулов:`, Array.from(selectedLeaves));
    
    if (requiredMinutes <= 0) {
      setNotification({
        type: 'error',
        message: 'Не удалось определить требуемое время для выбранных отгулов',
        show: true,
        persistent: false
      });
      setTimeout(() => setNotification(null), 2500);
      return;
    }
    
    // КРИТИЧЕСКИ ВАЖНО: Сначала сбрасываем timerSeconds в 0, чтобы не использовать старое значение
    // Затем синхронизируемся с актуальными данными из БД
    setTimerSeconds(0);
    
    const startTime = Date.now();
    setIsTimerRunning(true);
    setGoalAchieved(false);
    setDataSaved(false); // Сбрасываем флаг сохранения для нового таймера
    savingRef.current = false; // Сбрасываем ref
    setTimerStartTime(startTime);
    
    // Инициализируем lastSavedSecondsRef из базы данных при запуске таймера
    // ВАЖНО: всегда синхронизируем timerSeconds с данными из БД при запуске
    // Это гарантирует, что после паузы и сохранения все будет синхронизировано
    let currentWorkedSeconds = 0; // Переменная для сохранения в localStorage
    
    console.log(`🔍 [startNewTimer] DEBUG: timerSeconds сброшен на 0 перед синхронизацией, lastSavedSecondsRef: ${lastSavedSecondsRef.current}`);
    
    if (selectedLeaves.size === 1) {
      const leaveId = Array.from(selectedLeaves)[0];
      const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
      if (selectedLeave) {
        // Получаем актуальные данные из БД напрямую
        // НЕ используем workedHours, так как он может быть неправильно вычислен на сервере
        // Вместо этого получаем данные из leave_worktime через прямой запрос
        try {
          // Используем workedHours из данных отгула (основной источник)
          const alreadyWorkedHours = Number(selectedLeave.workedHours) || 0;
          let actualWorkedMinutes = Math.round(alreadyWorkedHours * 60);
          
          // Пробуем получить дополнительные данные через API (если endpoint существует)
          // Это необязательно, так как workedHours уже содержит актуальные данные
          try {
            const today = new Date().toISOString().slice(0, 10);
            const worktimeRes = await fetch(`/api/leaves/${leaveId}/worktime?date=${today}`, {
              headers: { Authorization: `Bearer ${getToken()}` }
            });
            
            if (worktimeRes.ok) {
              const worktimeData = await worktimeRes.json().catch(() => ({}));
              // Если есть данные, суммируем минуты
              if (Array.isArray(worktimeData) && worktimeData.length > 0) {
                const apiMinutes = worktimeData.reduce((sum, w) => sum + (Number(w.minutes) || 0), 0);
                // Используем данные из API, если они есть и больше нуля
                if (apiMinutes > 0) {
                  actualWorkedMinutes = apiMinutes;
                  console.log(`📊 [startNewTimer] Получены актуальные данные из API для отгула ${leaveId}: ${actualWorkedMinutes} минут`);
                }
              }
            }
          } catch (apiError) {
            // Игнорируем ошибку API (404 или другие), используем workedHours
            // Это нормально, если endpoint не существует
          }
          
          console.log(`📊 [startNewTimer] Используем workedHours из данных отгула: ${actualWorkedMinutes} минут`);
          
          const alreadyWorkedSeconds = actualWorkedMinutes * 60;
          
          console.log(`🔍 [startNewTimer] DEBUG: отгул ${leaveId}:
            - workedMinutes из БД: ${actualWorkedMinutes}
            - workedSeconds: ${alreadyWorkedSeconds}
            - timerSeconds до: ${timerSeconds} (должен быть 0)
            - lastSavedSecondsRef до: ${lastSavedSecondsRef.current}`);
          
          // ВСЕГДА синхронизируем с данными из БД
          // Устанавливаем lastSavedSecondsRef на уже сохраненное время из БД
          lastSavedSecondsRef.current = alreadyWorkedSeconds;
          // Устанавливаем timerSeconds на уже сохраненное время из БД
          // Это гарантирует правильную работу после паузы и продолжения
          setTimerSeconds(alreadyWorkedSeconds);
          // Используем актуальное значение, которое мы только что вычислили
          currentWorkedSeconds = alreadyWorkedSeconds;
          console.log(`🚀 Запуск таймера: синхронизация с БД - ${alreadyWorkedSeconds} секунд (${actualWorkedMinutes} минут)
            - timerSeconds установлен на: ${alreadyWorkedSeconds}
            - lastSavedSecondsRef установлен на: ${lastSavedSecondsRef.current}`);
        } catch (e) {
          console.error(`❌ [startNewTimer] Ошибка получения данных из БД:`, e);
          // Fallback: используем workedHours из данных
          const alreadyWorkedHours = Number(selectedLeave.workedHours) || 0;
          const alreadyWorkedMinutes = Math.round(alreadyWorkedHours * 60);
          const alreadyWorkedSeconds = alreadyWorkedMinutes * 60;
          lastSavedSecondsRef.current = alreadyWorkedSeconds;
          setTimerSeconds(alreadyWorkedSeconds);
          currentWorkedSeconds = alreadyWorkedSeconds;
          console.log(`⚠️ Запуск таймера (fallback): использованы workedHours из данных - ${alreadyWorkedSeconds} секунд (${alreadyWorkedMinutes} минут)`);
        }
      } else {
        // Если отгул не найден, сбрасываем счетчики
        lastSavedSecondsRef.current = 0;
        setTimerSeconds(0);
        currentWorkedSeconds = 0;
        console.log(`🚀 Запуск таймера: отгул не найден, сброс счетчиков`);
      }
    } else {
      // Для legacy режима используем текущее значение timerSeconds (но оно должно быть 0 после сброса выше)
      currentWorkedSeconds = 0;
      console.log(`🚀 Запуск таймера (legacy): timerSeconds сброшен на 0`);
    }
    
    // Сохраняем состояние в localStorage с актуальным прогрессом (для продолжения отработки)
    // Используем currentWorkedSeconds, который содержит актуальное значение из БД
    saveTimerState(true, startTime, Array.from(selectedLeaves), currentWorkedSeconds);
    
    // Отправляем WebSocket событие о начале нового таймера
    if (socket && currentUser) {
      socket.emit('new_timer_started', {
        userId: currentUser.id,
        username: currentUser.username,
        selectedLeaves: Array.from(selectedLeaves),
        startTime: startTime,
        date: selectedDate,
        requiredMinutes: requiredMinutes
      });
    }

    // Старт сессии мониторинга активности (агент на ПК / серверный захват скриншотов)
    if (selectedLeaves.size === 1) {
      const leaveId = Array.from(selectedLeaves)[0];
      try {
        const res = await fetch('/api/activity-session/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ leaveId })
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.sessionId) {
          activitySessionIdRef.current = json.sessionId;
          notifyLocalTriggerStart(json.sessionId, leaveId);
          console.log('📸 [activity-session] Started session for leave', leaveId, 'sessionId=', json.sessionId);
        } else {
          console.warn('📸 [activity-session] Start failed for leave', leaveId, 'status=', res.status, 'body=', json);
        }
      } catch (e) {
        console.warn('📸 [activity-session] Start request failed:', e);
      }
    }
  };

  const stopNewTimer = async (autoSave = false) => {
    // Защита от повторного вызова
    if (savingRef.current) {
      console.log(`⚠️ Сохранение уже в процессе, пропускаем повторный вызов`);
      return;
    }
    
    setIsTimerRunning(false);

    // Остановка сессии мониторинга активности
    const sessionId = activitySessionIdRef.current;
    const leaveId = selectedLeaves.size === 1 ? Array.from(selectedLeaves)[0] : null;
    if (sessionId || leaveId) {
      try {
        notifyLocalTriggerStop(sessionId);
        await fetch('/api/activity-session/stop', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify(sessionId ? { sessionId } : { leaveId })
        });
      } catch (e) {
        console.warn('Activity session stop failed:', e);
      }
      activitySessionIdRef.current = null;
    }
    
    // Очищаем уведомление при ручной остановке
    if (!autoSave) {
      setNotification(null);
    }
    
    if (timerSeconds < 60 && !autoSave) {
      setNotification({
        type: 'warning',
        message: 'Слишком мало времени для сохранения (минимум 1 минута)',
        show: true,
        persistent: false
      });
      setTimeout(() => setNotification(null), 2500);
      return;
    }
    
    // Переменная для WebSocket события
    let finalWorkedMinutes = 0;
    
    try {
      if (selectedLeaves.size === 1) {
        // Изолированная отработка одного отгула
        const leaveId = Array.from(selectedLeaves)[0];
        
        // Проверяем, не были ли данные уже сохранены автоматически
        if (dataSaved) {
          console.log(`⚠️ Данные уже сохранены автоматически, пропускаем повторное сохранение`);
          // Сбрасываем флаг для следующего цикла
          setDataSaved(false);
          return;
        }
        
        // КРИТИЧЕСКИ ВАЖНО: Не используем workedHours из data, так как он может быть неправильным
        // Вместо этого используем lastSavedSecondsRef, который был установлен при запуске таймера из актуальных данных БД
        
        // Если lastSavedSecondsRef не установлен, пытаемся получить актуальные данные из БД
        if (lastSavedSecondsRef.current === 0) {
          try {
            const today = new Date().toISOString().slice(0, 10);
            const worktimeRes = await fetch(`/api/leaves/${leaveId}/worktime?date=${today}`, {
              headers: { Authorization: `Bearer ${getToken()}` }
            });
            
            if (worktimeRes.ok) {
              const worktimeData = await worktimeRes.json().catch(() => ([]));
              if (Array.isArray(worktimeData)) {
                const actualWorkedMinutes = worktimeData.reduce((sum, w) => sum + (Number(w.minutes) || 0), 0);
                lastSavedSecondsRef.current = actualWorkedMinutes * 60;
                console.log(`📊 [stopNewTimer] Инициализация lastSavedSecondsRef из БД: ${lastSavedSecondsRef.current} секунд (${actualWorkedMinutes} минут)`);
              }
            }
          } catch (e) {
            console.error(`❌ [stopNewTimer] Ошибка получения данных из БД:`, e);
            // Fallback: используем workedHours из data (может быть неправильным, но лучше чем 0)
            const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
            if (selectedLeave) {
              const alreadyWorkedHours = Number(selectedLeave.workedHours) || 0;
              const alreadyWorkedMinutes = Math.round(alreadyWorkedHours * 60);
              lastSavedSecondsRef.current = alreadyWorkedMinutes * 60;
              console.log(`⚠️ [stopNewTimer] Fallback: использованы workedHours из data: ${lastSavedSecondsRef.current} секунд (${alreadyWorkedMinutes} минут)`);
            }
          }
        }
        
        // Вычисляем НОВОЕ время: текущее время минус уже сохраненное
        const newSeconds = timerSeconds - lastSavedSecondsRef.current;
        const newWorkedMinutes = Math.floor(newSeconds / 60);
        
        // Общее количество отработанных минут = timerSeconds (который содержит все время)
        // timerSeconds был установлен при запуске на уже сохраненное время из БД,
        // а затем увеличивался во время работы таймера
        const totalMinutes = Math.floor(timerSeconds / 60);
        finalWorkedMinutes = totalMinutes; // Сохраняем для WebSocket события
        
        console.log(`💾 Расчет времени для сохранения:
          - timerSeconds (всего накоплено): ${timerSeconds} сек (${Math.floor(timerSeconds / 60)} мин)
          - lastSavedSecondsRef (уже сохранено в БД): ${lastSavedSecondsRef.current} сек (${Math.floor(lastSavedSecondsRef.current / 60)} мин)
          - newSeconds (новое время для сохранения): ${newSeconds} сек (${newWorkedMinutes} мин)
          - totalMinutes (всего для сохранения в БД): ${totalMinutes} мин`);
        
        // Если новое время меньше 1 минуты, не сохраняем
        if (newWorkedMinutes < 1 && !autoSave) {
          setNotification({
            type: 'warning',
            message: 'Слишком мало нового времени для сохранения (минимум 1 минута)',
            show: true,
            persistent: false
          });
          setTimeout(() => setNotification(null), 2500);
          return;
        }
        
        // Устанавливаем флаги процесса сохранения
        savingRef.current = true;
        console.log(`💾 Начинаем ручное сохранение для отгула ${leaveId}: ${newWorkedMinutes} минут (новое время) будет добавлено к существующим в БД`);
        
        // КРИТИЧЕСКИ ВАЖНО: Передаем только НОВЫЕ минуты, так как endpoint добавляет записи к существующим
        // Не передаем totalWorkedMinutes, так как это приведет к удвоению времени
        // Передаем тип отработки из текущего режима (workMode)
        await saveWorktimeDataForSpecificLeave(leaveId, newWorkedMinutes, workMode);
        
        // После сохранения обновляем lastSavedSecondsRef на новое общее время (timerSeconds)
        // timerSeconds содержит уже общее время (было в БД + новое), которое мы только что сохранили
        lastSavedSecondsRef.current = timerSeconds;
        // timerSeconds уже содержит правильное значение, не нужно его менять
        console.log(`✅ Обновлен lastSavedSecondsRef на общее время: ${timerSeconds} секунд (${Math.floor(timerSeconds / 60)} минут)`);
        
        // КРИТИЧЕСКИ ВАЖНО: Обновляем данные в состоянии напрямую, чтобы при продолжении они были актуальными
        // Это предотвращает проблему, когда loadData() еще не завершился, а пользователь уже нажимает "продолжить"
        // totalMinutes уже вычислено выше
        setData(prevData => prevData.map(row => {
          if ((row.id || row.leaveId) === leaveId && isRowForCurrentUser(row)) {
            return {
              ...row,
              workedHours: totalMinutes / 60, // Обновляем отработанные часы
              workedMinutes: totalMinutes // Обновляем отработанные минуты
            };
          }
          return row;
        }));
        
        // КРИТИЧЕСКИ ВАЖНО: Очищаем localStorage после сохранения, чтобы при следующем открытии модалки
        // не восстанавливалось старое значение, которое может быть неправильным
        // Вместо этого всегда будем синхронизировать с актуальными данными из БД
        clearTimerState();
        console.log(`🗑️ localStorage очищен после сохранения, чтобы избежать восстановления устаревших значений`);
        
        // Проверяем, достигнута ли цель для этого отгула
        const requiredMinutes = getSelectedLeavesRequiredMinutes();
        // totalMinutes уже вычислено выше
        if (totalMinutes >= requiredMinutes) {
          // Обновляем статус отгула на "отработано"
          await updateLeaveStatus(leaveId, 'completed');
          
          // Очищаем состояние таймера, так как отгул полностью отработан
          setTimerSeconds(0);
          lastSavedSecondsRef.current = 0; // Сбрасываем счетчик сохраненного времени
          clearTimerState();
          
          setNotification({
            type: 'success',
            message: `🎉 Отгул полностью отработан! Всего отработано: ${totalMinutes} минут. Статус изменен на "Отработано".`,
            show: true,
            persistent: true
          });
          
          // Перезагружаем данные чтобы обновить прогресс
          await loadData();
        } else {
          const remainingMinutes = requiredMinutes - totalMinutes;
          setNotification({
            type: 'success',
            message: `✅ Сохранено ${newWorkedMinutes} минут отработки сегодня. Всего отработано: ${totalMinutes}/${requiredMinutes} минут. Осталось: ${remainingMinutes} минут. Вы можете продолжить завтра.`,
            show: true,
            persistent: true
          });
          
          // Перезагружаем данные чтобы обновить прогресс
          await loadData();
        }
      } else {
        // Legacy режим для множественного выбора (не должен использоваться)
        const today = new Date().toISOString().slice(0, 10);
        const legacyWorkedMinutes = Math.floor(timerSeconds / 60);
        finalWorkedMinutes = legacyWorkedMinutes; // Сохраняем для WebSocket события
        
        console.log('💾 Saving worktime (legacy):', { 
          workedMinutes: legacyWorkedMinutes, 
          selectedLeaves: Array.from(selectedLeaves), 
          date: today,
          currentUser: currentUser ? { id: currentUser.id, username: currentUser.username } : null
        });
        
        const res = await fetch('/api/leaves/add-worktime', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            Authorization: `Bearer ${getToken()}` 
          },
          body: JSON.stringify({ 
            minutes: legacyWorkedMinutes,
            date: today,
            selectedLeaves: Array.from(selectedLeaves)
          })
        });
        
        if (!res.ok) {
          const errorText = await res.text();
          console.error('❌ Server error:', res.status, errorText);
          throw new Error(`Failed to save worked time: ${res.status} ${errorText}`);
        }
        
        const saveResult = await res.json();
        console.log('✅ Save result:', saveResult);
        
        setNotification({
          type: 'success',
          message: `✅ Сохранено ${legacyWorkedMinutes} минут отработки!`,
          show: true,
          persistent: true
        });
      }
      
      // Сохраняем selectedLeaves до очистки для WebSocket события
      const selectedLeavesArray = Array.from(selectedLeaves);
      
      // Отправляем WebSocket событие об остановке таймера ПЕРЕД очисткой состояния
      if (socket && currentUser) {
        socket.emit('new_timer_stopped', {
          userId: currentUser.id,
          username: currentUser.username,
          selectedLeaves: selectedLeavesArray,
          workedMinutes: finalWorkedMinutes,
          date: selectedDate
        });
      }
      
      // Обновляем данные
      handleCompletionIfNeeded();
      
      // Перезагружаем данные после успешного сохранения
      console.log('🔄 Reloading data after timer stop...');
      await loadData();
      console.log('✅ Data reloaded successfully');
      
      // Данные отработки теперь загружаются через новую систему в loadData()
      console.log('📊 Worktime data will be updated via loadData() call above');
      
      // ТОЛЬКО ПОСЛЕ перезагрузки данных очищаем состояние таймера с небольшой задержкой
      setTimeout(() => {
        setTimerSeconds(0);
        setSelectedLeaves(new Set());
        setTimerStartTime(null);
        setIsTimerRunning(false);
        
        // Очищаем сохраненное состояние
        clearTimerState();
        
        // Очищаем данные реального времени для текущего пользователя
        if (currentUser) {
          setRealtimeUpdates(prev => {
            const updated = { ...prev };
            delete updated[currentUser.username];
            return updated;
          });
        }
      }, 100); // Небольшая задержка для обновления UI
      
    } catch (e) {
      console.error('Ошибка сохранения времени:', e);
      setNotification({
        type: 'error',
        message: 'Ошибка при сохранении отработанного времени',
        show: true,
        persistent: false
      });
      setTimeout(() => setNotification(null), 2500);
    } finally {
      // Всегда сбрасываем флаги сохранения в конце
      savingRef.current = false;
      console.log(`✅ Флаги сохранения сброшены`);
    }
  };

  const resetNewTimer = () => {
    setIsTimerRunning(false);
    setTimerSeconds(0);
    setTimerStartTime(null);
    setGoalAchieved(false); // Сбрасываем флаг при сбросе
    clearTimerState();
  };

const resetWorktime = async () => {
  try {
    const res = await fetch('/api/worktime-reset', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error('Failed reset');

    // Сбрасываем только локальные счётчики и таймеры, НЕ трогаем worktimeMap
    setWeekendTodayMinutes(0);
    setWeekdayTodayMinutes(0);
    setWeekendRunning(false);
    setWeekdayRunning(false);
    setWeekendStartTs(null);
    setWeekdayStartTs(null);

    // ВАЖНО: НЕ сбрасываем worktimeMap, так как он содержит данные завершенных сессий отработки!
    // worktimeMap обновляется только при смене даты или загрузке модалки

    // Обновляем статусы таймеров с сервера (без перезагрузки data)
    await loadWeekendTimer();
    await loadWeekdayTimer();
    
    // Перезагружаем worktimeMap для текущей даты, чтобы получить актуальные данные
    try {
      const day = new Date(selectedDate).toISOString().slice(0, 10);
      const worktimeUrl = `/api/worktime-stats/${day}`;
      const worktimeRes = await fetch(worktimeUrl, { 
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      if (worktimeRes.ok) {
        const worktimeData = await worktimeRes.json().catch(() => ({}));
        const map = {};
        
        if (Array.isArray(worktimeData)) {
          worktimeData.forEach(userStat => {
            const username = userStat.username || '';
            if (username && userStat.totalMinutes !== undefined) {
              map[username] = Number(userStat.totalMinutes) || 0;
            }
          });
        }
        
        setWorktimeMap(map);
      }
    } catch (e) {
      console.warn('Failed to reload worktime stats after reset:', e);
    }

    setNotification({ type: 'success', message: '✨ Время учтено и находится на проверке у HR', show: true, persistent: true });

    completionHandledRef.current = false; // allow next completion cycle
  } catch (e) {
    console.error('resetWorktime error', e);
    throw e;
  }
};

  // ------------------ Data loading function ------------------
  const loadData = async (signal = null) => {
    try {
      // current user from localStorage (keep for backward compat)
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setCurrentUser(user);
      setUserRole((user.role || 'employee').toString().toLowerCase());

      // load users list for HR/Admin
      try {
        if ((user.role === 'hr' || user.role === 'admin')) {
          const usersRes = await fetch('/api/users', { headers: { Authorization: `Bearer ${getToken()}` }, signal });
          if (usersRes.ok) {
            const users = await usersRes.json().catch(() => ([]));
            setAllUsers(Array.isArray(users) ? users : []);
          } else {
            setAllUsers([]);
          }
        } else {
          setAllUsers([user]);
        }
      } catch (e) {
        setAllUsers(user ? [user] : []);
      }

      // 1) load leaves-worktime-report (obligations)
      try {
        const rep = await fetch('/api/leaves-worktime-report', { headers: { Authorization: `Bearer ${getToken()}` }, signal });
        if (rep.ok) {
          const json = await rep.json().catch(() => ([]));
          setData(Array.isArray(json) ? json : []);
        } else {
          setData([]);
        }
      } catch (e) {
        setData([]);
      }
    } catch (e) {
      console.error('Error loading data:', e);
    }
  };

  // ------------------ Global styles for desktop modal overlay ------------------
  useEffect(() => {
    if (!isOpen) return;
    
    // Добавляем глобальные стили для overlay react-modal, чтобы он не закрывал сайдбар
    const styleId = 'leaves-worktime-modal-overlay-styles';
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .ReactModal__Overlay--leaves-worktime {
        left: 320px !important;
        width: calc(100vw - 320px) !important;
        right: 0 !important;
        top: 0 !important;
        bottom: 0 !important;
        height: 100vh !important;
        position: fixed !important;
        pointer-events: auto !important;
        z-index: 250000 !important;
        background-color: rgba(0,0,0,0.45) !important;
        display: flex !important;
        align-items: stretch !important;
        justify-content: flex-start !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .ReactModal__Overlay--leaves-worktime .ReactModal__Content {
        left: 320px !important;
        width: calc(100vw - 320px) !important;
        max-width: calc(100vw - 320px) !important;
        min-width: 0 !important;
        height: 100vh !important;
        top: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        transform: none !important;
        pointer-events: auto !important;
        z-index: 250001 !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
      }
      .ReactModal__Overlay--leaves-worktime * {
        pointer-events: auto !important;
      }
    `;
    // Вставляем в начало head для максимального приоритета
    if (document.head.firstChild) {
      document.head.insertBefore(style, document.head.firstChild);
    } else {
      document.head.appendChild(style);
    }

    // Прямая DOM-манипуляция для принудительного применения стилей
    const applyStyles = () => {
      const overlays = document.querySelectorAll('.ReactModal__Overlay--leaves-worktime');
      overlays.forEach(overlay => {
        const s = overlay.style;
        s.setProperty('left', '320px', 'important');
        s.setProperty('width', 'calc(100vw - 320px)', 'important');
        s.setProperty('right', '0', 'important');
        s.setProperty('top', '0', 'important');
        s.setProperty('bottom', '0', 'important');
        s.setProperty('height', '100vh', 'important');
        s.setProperty('position', 'fixed', 'important');
        s.setProperty('background-color', 'rgba(0,0,0,0.45)', 'important');
        s.setProperty('display', 'flex', 'important');
        s.setProperty('align-items', 'stretch', 'important');
        s.setProperty('justify-content', 'flex-start', 'important');
        s.setProperty('margin', '0', 'important');
        s.setProperty('padding', '0', 'important');
        s.setProperty('z-index', '250000', 'important');
        
        const content = overlay.querySelector('.ReactModal__Content');
        if (content) {
          const cs = content.style;
          cs.setProperty('left', '320px', 'important');
          cs.setProperty('width', 'calc(100vw - 320px)', 'important');
          cs.setProperty('max-width', 'calc(100vw - 320px)', 'important');
          cs.setProperty('min-width', '0', 'important');
          cs.setProperty('height', '100vh', 'important');
          cs.setProperty('top', '0', 'important');
          cs.setProperty('right', '0', 'important');
          cs.setProperty('bottom', '0', 'important');
          cs.setProperty('margin', '0', 'important');
          cs.setProperty('padding', '0', 'important');
          cs.setProperty('transform', 'none', 'important');
          cs.setProperty('box-sizing', 'border-box', 'important');
          cs.setProperty('overflow', 'hidden', 'important');
          cs.setProperty('z-index', '250001', 'important');
          
          // Применяем стили к внутренним div
          const firstDiv = content.querySelector('div');
          if (firstDiv) {
            firstDiv.style.setProperty('width', '100%', 'important');
            firstDiv.style.setProperty('max-width', '100%', 'important');
            firstDiv.style.setProperty('min-width', '0', 'important');
            firstDiv.style.setProperty('box-sizing', 'border-box', 'important');
            firstDiv.style.setProperty('overflow', 'hidden', 'important');
          }
        }
      });
    };

    // Применяем стили с задержкой, чтобы модалка успела отрендериться
    setTimeout(applyStyles, 0);
    setTimeout(applyStyles, 10);
    setTimeout(applyStyles, 50);
    setTimeout(applyStyles, 100);
    setTimeout(applyStyles, 200);

    // Наблюдаем за изменениями DOM и применяем стили
    const observer = new MutationObserver(applyStyles);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
      observer.disconnect();
    };
  }, [isOpen]);

  // ------------------ WebSocket connection setup ------------------
  useEffect(() => {
    if (!isOpen) return;

    // Используем общий socket из SocketProvider вместо создания нового
    const socketConnection = window.socket;
    if (!socketConnection || !socketConnection.connected) {
      console.warn('⚠️ Socket not available or not connected');
      return;
    }

    // Устанавливаем socket и selectedDate в глобальный сервис для отправки WebSocket событий
    backgroundTimerService.setSocket(socketConnection);
    backgroundTimerService.setSelectedDate(selectedDate);

    // Обработчики WebSocket событий для всех ролей
    // Обновление данных отработки в реальном времени
    socketConnection.on('worktime_data_updated', (updateData) => {
      console.log('🔄 [CLIENT] Received worktime_data_updated:', {
        username: updateData.username,
        userId: updateData.userId,
        isRunning: updateData.isRunning,
        workType: updateData.workType,
        currentSeconds: updateData.currentSeconds,
        selectedLeaves: updateData.selectedLeaves,
        date: updateData.date
      });
      console.log('👤 [CLIENT] Current user role:', userRole, 'Current user:', currentUser?.username || currentUser?.fio);
      console.log('📅 [CLIENT] Selected date:', selectedDate, 'Update date:', updateData.date);
      
      // Обновляем карты минут по username и по userId
      if (updateData.date === selectedDate) {
        if (updateData.username) {
          setWorktimeMap(prev => ({
            ...prev,
            [updateData.username]: updateData.workedMinutes
          }));
        }
        if (updateData.userId != null) {
          setWorktimeByUserId(prev => ({
            ...prev,
            [String(updateData.userId)]: updateData.workedMinutes
          }));
        }
      }
      
      // Сохраняем real-time обновления для HR/Admin (независимо от даты, для отображения лайв таймера)
      if ((userRole === 'hr' || userRole === 'admin') && updateData.username) {
        // Нормализуем username (убираем лишние пробелы) для консистентности
        const normalizedUsername = updateData.username.replace(/\s+/g, ' ').trim();
        
        console.log('💾 [CLIENT] Saving real-time update for HR/Admin:', {
          originalUsername: updateData.username,
          normalizedUsername: normalizedUsername,
          isRunning: updateData.isRunning,
          workType: updateData.workType,
          currentSeconds: updateData.currentSeconds,
          selectedLeaves: updateData.selectedLeaves,
          userId: updateData.userId
        });
        
        setRealtimeUpdates(prev => {
          const updated = {
            ...prev,
            // Сохраняем и по оригинальному, и по нормализованному ключу для совместимости
            [updateData.username]: {
              workedMinutes: updateData.workedMinutes,
              status: updateData.status,
              isRunning: updateData.isRunning,
              workType: updateData.workType || null,
              currentSeconds: updateData.currentSeconds || 0,
              selectedLeaves: updateData.selectedLeaves || []
            }
          };
          
          // Также сохраняем по нормализованному ключу, если он отличается
          if (normalizedUsername !== updateData.username) {
            updated[normalizedUsername] = updated[updateData.username];
          }
          
          console.log('📊 [CLIENT] Updated realtimeUpdates. Keys:', Object.keys(updated));
          console.log('📊 [CLIENT] Data for', updateData.username, ':', updated[updateData.username]);
          if (normalizedUsername !== updateData.username) {
            console.log('📊 [CLIENT] Also saved under normalized key:', normalizedUsername, ':', updated[normalizedUsername]);
          }
          return updated;
        });
      } else {
        console.log('⚠️ [CLIENT] Not saving realtime update - userRole:', userRole, 'username:', updateData.username);
      }
    });

    // Обновление статуса таймера
    socketConnection.on('worktime_timer_updated', (timerData) => {
      console.log('⏱️ Received timer update:', timerData);
      
      // Обновляем данные таймеров для всех пользователей
      if (timerData.username) {
        setAllUsersTimers(prev => {
          const updated = { ...prev };
          if (!updated[timerData.username]) {
            updated[timerData.username] = {
              weekendRunning: false,
              weekdayRunning: false,
              weekendStartTs: null,
              weekdayStartTs: null,
              weekendTodayMinutes: 0,
              weekdayTodayMinutes: 0
            };
          }
          
          if (timerData.timerType === 'weekend') {
            updated[timerData.username].weekendRunning = timerData.isRunning;
            updated[timerData.username].weekendStartTs = timerData.isRunning ? timerData.startTime : null;
          } else if (timerData.timerType === 'weekday') {
            updated[timerData.username].weekdayRunning = timerData.isRunning;
            updated[timerData.username].weekdayStartTs = timerData.isRunning ? timerData.startTime : null;
          }
          
          return updated;
        });
      }
    });

    // Уведомления о завершении отработки
    socketConnection.on('worktime_completion_notification', (completionData) => {
      console.log('🎉 Received completion notification:', completionData);
      
      // Показать уведомление HR/Admin
      if (userRole === 'hr' || userRole === 'admin') {
        setNotification({
          type: 'info',
          message: `📋 Сотрудник ${completionData.username} завершил отработку отгула!\n\nТребовалось: ${Math.round(completionData.requiredMinutes/60*100)/100} ч\nОтработано: ${Math.round(completionData.totalWorkedMinutes/60*100)/100} ч\n\nСтатус: На проверке`,
          show: true,
          persistent: true
        });
        
        // Проигрываем звук для HR
        playNotificationSound();
        
        // Синхронизация таблицы HR: сохраняем финальные минуты в realtimeUpdates, чтобы столбцы «Отработано» и «Прогресс» сразу показывали 10 мин / 100%, а не 9 / 90%
        const totalMinutes = completionData.totalWorkedMinutes != null ? Number(completionData.totalWorkedMinutes) : 0;
        const finalSeconds = totalMinutes * 60;
        if (completionData.username && totalMinutes >= 0) {
          setRealtimeUpdates(prev => {
            const normalizedUsername = (completionData.username || '').replace(/\s+/g, ' ').trim();
            const finalState = {
              workedMinutes: totalMinutes,
              status: 'paused',
              isRunning: false,
              workType: completionData.workType || 'weekdays',
              currentSeconds: finalSeconds,
              selectedLeaves: completionData.selectedLeaves || []
            };
            return {
              ...prev,
              [completionData.username]: finalState,
              ...(normalizedUsername !== completionData.username ? { [normalizedUsername]: finalState } : {})
            };
          });
        }
        
        // Перезагружаем данные таблицы с сервера, чтобы «Отработано» и «Прогресс» совпадали с данными сотрудника
        reloadLeavesData();
      }
      
      // Обновить верификации
      loadVerifications(selectedDate);
    });

    // Уведомления о подтверждении отработки HR
    socketConnection.on('worktime_verified', (verificationData) => {
      console.log('✅ Received verification notification:', verificationData);
      
      // Показать уведомление сотруднику о подтверждении
      if (currentUser && verificationData.userId === currentUser.id) {
        setNotification({
          type: 'success',
          message: `🎉 Ваша отработка отгула подтверждена HR!\n\nСтатус изменен на "Отработано". Отработка засчитана и добавлена в историю.`,
          show: true,
          persistent: true
        });
        
        // Проигрываем звук
        playNotificationSound();
      }
      
      // Обновить верификации
      loadVerifications(selectedDate);
    });

    // Обработчики для нового таймера отработки отгулов
    socketConnection.on('new_timer_started', (timerData) => {
      console.log('🚀 Received new timer started:', timerData);
      
      // Показать уведомление HR о начале отработки
      if ((userRole === 'hr' || userRole === 'admin') && timerData.userId !== currentUser?.id) {
        setNotification({
          type: 'info',
          message: `⏱️ Сотрудник ${timerData.username} начал отработку отгулов`,
          show: true,
          persistent: true
        });
      }
      
      // Обновляем данные для перерисовки таблицы
      reloadLeavesData();
    });

    socketConnection.on('new_timer_stopped', async (timerData) => {
      console.log('⏹️ Received new timer stopped:', timerData);
      
      // Показать уведомление HR об остановке отработки (только для других пользователей)
      if ((userRole === 'hr' || userRole === 'admin') && timerData.userId !== currentUser?.id) {
        setNotification({
          type: 'info',
          message: `⏸️ Сотрудник ${timerData.username} завершил отработку (${timerData.workedMinutes} мин)`,
          show: true,
          persistent: true
        });
      }
      
      // Обновляем данные для перерисовки таблицы
      await reloadLeavesData();
      
      // Данные отработки обновляются через loadData() выше
      console.log('📊 Worktime data updated via loadData() call');
      
      // Очищаем данные реального времени для остановленного пользователя (для всех)
      setRealtimeUpdates(prev => {
        const updated = { ...prev };
        delete updated[timerData.username];
        return updated;
      });
    });

    // Обработчик обновлений прогресса в реальном времени
    socketConnection.on('new_timer_progress', (progressData) => {
      console.log('📊 Received timer progress update:', progressData);
      
      // Обновляем состояние для отображения в таблице
      // Для HR/Admin - показываем всех пользователей
      // Для обычных пользователей - показываем только свои данные
      const shouldUpdate = (userRole === 'hr' || userRole === 'admin') || 
                          (progressData.userId === currentUser?.id);
      
      if (shouldUpdate) {
        // Сохраняем данные прогресса для отображения в таблице
        setRealtimeUpdates(prev => ({
          ...prev,
          [progressData.username]: {
            workedMinutes: progressData.workedMinutes,
            progress: progressData.progress,
            isRunning: true,
            selectedLeaves: progressData.selectedLeaves,
            currentSeconds: progressData.currentSeconds,
            lastUpdate: Date.now()
          }
        }));
        
        // Также обновляем worktimeMap для синхронизации
        if (progressData.date === selectedDate) {
          setWorktimeMap(prev => ({
            ...prev,
            [progressData.username]: progressData.workedMinutes
          }));
        }
        
        console.log(`✅ Updated realtime data for ${progressData.username}: ${progressData.workedMinutes} min`);
      }
    });

    setSocket(socketConnection);

    // Устанавливаем socket и selectedDate в глобальный сервис для отправки WebSocket событий
    // Это важно, чтобы HR/Admin видели лайв таймер даже когда модалка закрыта
    backgroundTimerService.setSocket(socketConnection);
    backgroundTimerService.setSelectedDate(selectedDate);

    return () => {
      // Не отключаем общий socket, только снимаем обработчики
      if (socketConnection) {
        socketConnection.off('worktime_data_updated');
        socketConnection.off('worktime_timer_updated');
        socketConnection.off('worktime_completion_notification');
        socketConnection.off('worktime_verified');
        socketConnection.off('new_timer_started');
        socketConnection.off('new_timer_stopped');
        socketConnection.off('new_timer_progress');
      }
    };
  }, [isOpen, userRole, selectedDate]);

  // При открытии модалки с initialFilter (например, по кнопке «Перейти» у HR или из уведомления сотрудника) переключаем фильтр и вкладку
  useEffect(() => {
    if (!isOpen || !initialFilter) return;
    const workModes = ['offhours', 'weekdays', 'weekends'];
    if (workModes.includes(initialFilter)) {
      setWorkMode(initialFilter);
      setFilter('pending');
    } else {
      setFilter(initialFilter);
      setWorkMode(null);
    }
    if (typeof onInitialFilterConsumed === 'function') onInitialFilterConsumed();
  }, [isOpen, initialFilter]);

  // Обновляем selectedDate в глобальном сервисе при изменении
  useEffect(() => {
    if (selectedDate) {
      backgroundTimerService.setSelectedDate(selectedDate);
    }
  }, [selectedDate]);

  // ------------------ Initial data load when modal opens ------------------
  useEffect(() => {
    if (!isOpen) return;

  mountedRef.current = true;
  setLoading(true);
  setError('');
  completionHandledRef.current = false;
  worktimeCompletedDismissedRef.current = false;

    const controller = new AbortController();

    (async () => {
      try {
        await loadData(controller.signal);

        // 2) load worktime sessions for selected date (worktimeMap)
        try {
          const day = new Date(selectedDate).toISOString().slice(0, 10);
          
          // Загружаем данные сессий отработки для выбранной даты
          const worktimeUrl = `/api/worktime-stats/${day}`;
          const worktimeRes = await fetch(worktimeUrl, { 
            headers: { Authorization: `Bearer ${getToken()}` }, 
            signal: controller.signal 
          });
          
          if (worktimeRes.ok) {
            const worktimeData = await worktimeRes.json().catch(() => ({}));
            const map = {};
            
            // Если это API возвращает статистику по пользователям
            if (Array.isArray(worktimeData)) {
              worktimeData.forEach(userStat => {
                const username = userStat.username || '';
                if (username && userStat.totalMinutes !== undefined) {
                  map[username] = Number(userStat.totalMinutes) || 0;
                }
              });
            } else {
              // Если это объект с данными текущего пользователя
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              if (user.username && worktimeData.totalMinutes) {
                map[user.username] = Number(worktimeData.totalMinutes) || 0;
              }
            }
            
      setWorktimeMap(map);
      // Также построим карту по userId, если сможем сопоставить
      try {
        const byId = {};
        (Array.isArray(allUsers) ? allUsers : []).forEach(u => {
          if (u && u.username && u.id != null && map[u.username] != null) {
            byId[String(u.id)] = Number(map[u.username]) || 0;
          }
        });
        setWorktimeByUserId(byId);
      } catch {}
          } else {
            // Fallback: используем старый метод через quick-db-report
            const url = `/api/quick-db-report?start=${day}&end=${day}`;
            const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` }, signal: controller.signal });
            if (r.ok) {
              const json = await r.json().catch(() => ({}));
              const report = Array.isArray(json?.report) ? json.report : [];
              const map = {};
              report.forEach(row => {
                const fio = row.fio || row.username || row.user || '';
                let overtimeMinutes = 0;
                try {
                  const first = row.firstLogin ? new Date(row.firstLogin) : null;
                  const last = row.lastLogout ? new Date(row.lastLogout) : null;
                  if (first) {
                    const workStart = new Date(first);
                    workStart.setHours(9, 0, 0, 0);
                    if (first < workStart) {
                      overtimeMinutes += Math.max(0, Math.round((workStart - first) / 60000));
                    }
                  }
                  if (last) {
                    const workEnd = new Date(last);
                    workEnd.setHours(18, 0, 0, 0);
                    if (last > workEnd) {
                      overtimeMinutes += Math.max(0, Math.round((last - workEnd) / 60000));
                    }
                  }
                } catch (ee) {}
                map[fio] = overtimeMinutes;
              });
      setWorktimeMap(map);
      try {
        const byId = {};
        (Array.isArray(allUsers) ? allUsers : []).forEach(u => {
          if (u && u.username && u.id != null && map[u.username] != null) {
            byId[String(u.id)] = Number(map[u.username]) || 0;
          }
        });
        setWorktimeByUserId(byId);
      } catch {}
            } else {
              setWorktimeMap({});
            }
          }
        } catch (e) {
          setWorktimeMap({});
        }

        // 3) load timers state
        try {
          await Promise.all([loadWeekendTimer(), loadWeekdayTimer()]);
        } catch (e) {
          // ignore
        }

        // 4) load verifications for selected date
        await loadVerifications(selectedDate);

        // 5) load all users timers for HR/Admin (real-time data)
        if (currentUser?.role === 'hr' || currentUser?.role === 'admin') {
          await loadAllUsersTimers();
        }

        // initial completion check
        setTimeout(() => {
          try { handleCompletionIfNeeded(); } catch (e) { /* ignore */ }
        }, 600);

      } catch (e) {
        console.error('Initial load error', e);
        setError('Ошибка загрузки данных');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => {
      mountedRef.current = false;
      controller.abort();
    };
  }, [isOpen, propToken, selectedDate]);

  // Восстановление сессии мониторинга: если таймер уже идёт, один отгул выбран, но сессия не создана (например после обновления страницы) — создаём сессию
  useEffect(() => {
    if (!isOpen || selectedLeaves.size !== 1) return;
    const timerActive = isTimerRunning || weekdaysTimerRunning || offhoursTimerRunning || weekendsTimerRunning;
    if (!timerActive || activitySessionIdRef.current != null || activitySessionEnsureInProgressRef.current) return;

    const leaveId = Array.from(selectedLeaves)[0];
    activitySessionEnsureInProgressRef.current = true;
    fetch('/api/activity-session/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ leaveId })
    })
      .then((res) => res.json().catch(() => ({})))
      .then((json) => {
        if (json && json.sessionId) {
          activitySessionIdRef.current = json.sessionId;
          notifyLocalTriggerStart(json.sessionId, leaveId);
          console.log('📸 [activity-session] Restored session for leave', leaveId, 'sessionId=', json.sessionId);
        }
      })
      .catch(() => {})
      .finally(() => {
        activitySessionEnsureInProgressRef.current = false;
      });
  }, [isOpen, isTimerRunning, weekdaysTimerRunning, offhoursTimerRunning, weekendsTimerRunning, selectedLeaves]);

  // Отдельный эффект для перезагрузки worktimeMap при смене даты
  useEffect(() => {
    if (!isOpen) return;
    
    const loadWorktimeForDate = async () => {
      try {
        const day = new Date(selectedDate).toISOString().slice(0, 10);
        const worktimeUrl = `/api/worktime-stats/${day}`;
        const worktimeRes = await fetch(worktimeUrl, { 
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        
        if (worktimeRes.ok) {
          const worktimeData = await worktimeRes.json().catch(() => ({}));
          const map = {};
          
          if (Array.isArray(worktimeData)) {
            worktimeData.forEach(userStat => {
              const username = userStat.username || '';
              if (username && userStat.totalMinutes !== undefined) {
                map[username] = Number(userStat.totalMinutes) || 0;
              }
            });
          }
          
      setWorktimeMap(map);
      try {
        const byId = {};
        (Array.isArray(allUsers) ? allUsers : []).forEach(u => {
          if (u && u.username && u.id != null && map[u.username] != null) {
            byId[String(u.id)] = Number(map[u.username]) || 0;
          }
        });
        setWorktimeByUserId(byId);
      } catch {}
        } else {
          // Fallback: используем старый метод
          const url = `/api/quick-db-report?start=${day}&end=${day}`;
          const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
          if (r.ok) {
            const json = await r.json().catch(() => ({}));
            const report = Array.isArray(json?.report) ? json.report : [];
            const map = {};
            report.forEach(row => {
              const fio = row.fio || row.username || row.user || '';
              let overtimeMinutes = 0;
              try {
                const first = row.firstLogin ? new Date(row.firstLogin) : null;
                const last = row.lastLogout ? new Date(row.lastLogout) : null;
                if (first) {
                  const workStart = new Date(first);
                  workStart.setHours(9, 0, 0, 0);
                  if (first < workStart) {
                    overtimeMinutes += Math.max(0, Math.round((workStart - first) / 60000));
                  }
                }
                if (last) {
                  const workEnd = new Date(last);
                  workEnd.setHours(18, 0, 0, 0);
                  if (last > workEnd) {
                    overtimeMinutes += Math.max(0, Math.round((last - workEnd) / 60000));
                  }
                }
              } catch (ee) {}
              map[fio] = overtimeMinutes;
            });
            setWorktimeMap(map);
          } else {
            setWorktimeMap({});
          }
        }
        
        // Также обновляем подтверждения для новой даты
        await loadVerifications(selectedDate);
      } catch (e) {
        console.warn('Failed to load worktime for date:', e);
        setWorktimeMap({});
      }
    };
    
    loadWorktimeForDate();
  }, [selectedDate, isOpen]);

  // ------------------ intervals: update time & poll server status ------------------
  useEffect(() => {
    if (!isOpen) return;

    mountedRef.current = true;
    
    // Интервал обновления данных таблицы каждые 5 секунд для синхронизации прогресса
    const dataUpdateInterval = setInterval(async () => {
      try {
        // Перезагружаем данные отгулов для обновления прогресса
        await reloadLeavesData();
        
        // Обновляем данные отработанного времени для выбранной даты
        const day = new Date(selectedDate).toISOString().slice(0, 10);
        const worktimeUrl = `/api/worktime-stats/${day}`;
        const worktimeRes = await fetch(worktimeUrl, { 
          headers: { Authorization: `Bearer ${getToken()}` }
        });
        
        if (worktimeRes.ok) {
          const worktimeData = await worktimeRes.json().catch(() => ({}));
          const map = {};
          
          if (Array.isArray(worktimeData)) {
            worktimeData.forEach(userStat => {
              const username = userStat.username || '';
              if (username && userStat.totalMinutes !== undefined) {
                map[username] = Number(userStat.totalMinutes) || 0;
              }
            });
          }
          
          setWorktimeMap(map);
          
          // Обновляем карту по userId
          try {
            const byId = {};
            (Array.isArray(allUsers) ? allUsers : []).forEach(u => {
              if (u && u.username && u.id != null && map[u.username] != null) {
                byId[String(u.id)] = Number(map[u.username]) || 0;
              }
            });
            setWorktimeByUserId(byId);
          } catch {}
        }
        
        // Загружаем данные таймеров всех пользователей для HR/Admin
        if (userRole === 'hr' || userRole === 'admin') {
          await loadAllUsersTimers();
        }
        
        // Очищаем устаревшие данные реального времени (старше 30 секунд)
        const now = Date.now();
        setRealtimeUpdates(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(username => {
            const data = updated[username];
            if (data.lastUpdate && (now - data.lastUpdate) > 30000) {
              delete updated[username];
              console.log(`🗑️ Очищены устаревшие данные для ${username}`);
            }
          });
          return updated;
        });
        
        console.log('🔄 Данные таблицы обновлены (каждые 5 сек)');
      } catch (e) {
        console.warn('Ошибка обновления данных таблицы:', e);
      }
    }, 5000);
    
    // Poll server timers every 15s to keep in sync
    const pollInterval = setInterval(() => {
      loadWeekendTimer();
      loadWeekdayTimer();
      // обновляем подтверждения HR/Admin для выбранной даты
      loadVerifications(selectedDate);
      // Also ensure we check completion occasionally
      handleCompletionIfNeeded();
    }, 15000);

    // Update UI time every second and check for completion and auto-stop
    const tickInterval = setInterval(() => {
      // If user has completed obligation => handleCompletionIfNeeded will stop timers + notify
      handleCompletionIfNeeded();

      // Отправляем обновления данных каждые 5 секунд (когда таймер работает)
      if (socket && currentUser && (weekendRunning || weekdayRunning)) {
        const now = Date.now();
        if (!window.lastWorktimeUpdate || now - window.lastWorktimeUpdate > 5000) {
          window.lastWorktimeUpdate = now;
          
          const row = refs.current.data.find(r => (r.username || r.fio || '') === currentUser.username);
          if (row) {
            const requiredMinutes = Math.round((Number(row.leaveHours) || 0) * 60);
            const mapMinutes = Number(refs.current.worktimeMap[currentUser.username] || 0);
            const totalExtra = getRunningWeekendMinutes() + getRunningWeekdayMinutes();
            const totalWorkedMinutes = mapMinutes + totalExtra;
            
            socket.emit('worktime_update', {
              userId: currentUser.id,
              username: currentUser.username,
              date: selectedDate,
              workedMinutes: totalWorkedMinutes,
              requiredMinutes,
              status: totalWorkedMinutes >= requiredMinutes ? 'checking' : 'in_progress',
              isRunning: weekendRunning || weekdayRunning
            });
          }
        }
      }
    }, 1000);

    return () => {
      clearInterval(dataUpdateInterval);
      clearInterval(pollInterval);
      clearInterval(tickInterval);
    };
  }, [isOpen, selectedDate, userRole]);

  // recompute filtered data with useMemo for perf
  const filteredData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return data.filter(row => {
      const fio = row.username || row.fio || '';
      const leaveHours = (Number(row.leaveHours) ?? Number(row.requiredHours)) || 0;

      // employee sees only self (by userId or normalized name)
      if (userRole === 'employee' && !isRowForCurrentUser(row)) return false;

      // name filter for HR/Admin
      if (nameFilter && !fio.toLowerCase().includes(nameFilter.toLowerCase())) return false;

      // compute progress for display
      const isCurrentUser = fio === currentUser?.username;
      // base minutes from worktimeMap
      const baseMapMinutes = Number(worktimeMap[fio] || 0);
      // add dynamic minutes - теперь для всех пользователей если сегодняшний день и HR/Admin
      const dayIsToday = isSameDay(selectedDate);
      let extraMinutes = 0;
      
      // Проверяем данные реального времени для более точного отображения
      const realtimeData = realtimeUpdates[fio];
      
      if (dayIsToday) {
        if (isCurrentUser) {
          // Для текущего пользователя: используем данные нового таймера если активен, иначе старые таймеры
          if (isTimerRunning && selectedLeaves.size > 0) {
            // Новый таймер отработки отгулов активен - используем точное значение для обновления в реальном времени
            extraMinutes = timerSeconds / 60; // Используем дробное значение для точности
          } else {
            // Старые таймеры выходных/будней
            extraMinutes = getRunningWeekendMinutes() + getRunningWeekdayMinutes();
          }
        } else if (userRole === 'hr' || userRole === 'admin') {
          // HR/Admin видят данные других пользователей
          if (realtimeData && realtimeData.isRunning) {
            // Используем данные реального времени если доступны - используем точное значение
            if (realtimeData.currentSeconds !== undefined) {
              extraMinutes = realtimeData.currentSeconds / 60; // Точное значение в минутах
            } else {
              extraMinutes = realtimeData.workedMinutes || 0;
            }
          } else {
            // Fallback на старые таймеры
            extraMinutes = getTotalWeekendMinutesForUser(fio) + getTotalWeekdayMinutesForUser(fio);
          }
        }
      }
      
      const workedMinutes = Math.max(0, baseMapMinutes + extraMinutes);
      const requiredMinutes = Math.round(leaveHours * 60);
      const progress = requiredMinutes > 0 ? Math.min(100, Math.round((workedMinutes / requiredMinutes) * 100)) : 0;

      // Для фильтрации различаем "На проверке" (сегодня достигнут 100%) и "Отработано" (вчера/прошлые дни по отчёту >= требуемых минут)
      const dayIsPast = isPastDay(selectedDate);
      const baseOnlyProgress = requiredMinutes > 0 ? Math.min(100, Math.round((Math.max(0, baseMapMinutes) / requiredMinutes) * 100)) : 0;
      // Если уже есть подтверждение на выбранную дату — исключаем из "На проверке"
      const rowUser = (allUsers || []).find(u => (u && (u.username === fio)));
      const isVerified = rowUser && verifiedMap[rowUser.id];

      switch (filter) {
        case 'completed': {
          // Отработанные: статус из БД, workStatus из отчёта или прогресс 100%
          const reqH = Number(row.requiredHours) || leaveHours;
          const workH = Number(row.workedHours) || 0;
          const progress100 = reqH > 0 && workH >= reqH;
          const isCompleted = isVerified
            || (row.status === 'completed')
            || (row.workStatus === 'Отработано')
            || (dayIsPast && baseOnlyProgress >= 100)
            || progress100;
          
          // Для HR/Admin показываем все отработанные отгулы
          if (userRole === 'hr' || userRole === 'admin') {
            return isCompleted;
          }
          // Для сотрудника — только свои
          return isCompleted && isRowForCurrentUser(row);
        }
        case 'pending': {
          // На очереди - отгулы которые еще не начинали отрабатываться
          // Показываем только отгулы со статусом 'approved' и с нулевым прогрессом
          const isPending = (row.status === 'approved') && (progress === 0) && !isVerified;
          
          // Для HR показываем все ожидающие отгулы всех сотрудников
          if (userRole === 'hr' || userRole === 'admin') {
            return isPending;
          }
          
          // Для обычных сотрудников показываем только свои ожидающие отгулы
          return isPending && isRowForCurrentUser(row);
        }
        default: return true;
      }
    });
  }, [data, userRole, currentUser, nameFilter, filter, worktimeMap, weekendTodayMinutes, weekdayTodayMinutes, weekendRunning, weekdayRunning, allUsers, verifiedMap, selectedDate, allUsersTimers, realtimeUpdates, timerSeconds, isTimerRunning, selectedLeaves]);

  // Для вкладок «Отработка по будням / выходным / нерабочее время» — только отгулы, которые ещё не отработаны.
  // Отработанные отображаются только во вкладке «Отработанные».
  // Обычный пользователь видит только свои отгулы; HR/Admin — все.
  const dataForWorkTabs = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    const isHROrAdmin = userRole === 'hr' || userRole === 'admin';
    return data.filter(row => {
      if (row.status === 'completed') return false;
      if (row.workStatus === 'Отработано') return false;
      if (!isHROrAdmin && !isRowForCurrentUser(row)) return false;
      if (nameFilter) {
        const fio = row.username || row.fio || '';
        if (!fio.toLowerCase().includes(nameFilter.toLowerCase())) return false;
      }
      return true;
    });
  }, [data, userRole, nameFilter, currentUser]);

  // UI event handlers for manual minutes (kept prompt for simplicity)
  const handleAddManualWeekend = async () => {
    const val = prompt('Сколько минут добавить к отработке за сегодня?');
    const minutes = Math.max(0, Number(val) || 0);
    if (!minutes) return;
    await addManualMinutes({ kind: 'weekend', minutes });
  };

  const handleAddManualWeekday = async () => {
    const val = prompt('Сколько минут добавить к отработке за сегодня?');
    const minutes = Math.max(0, Number(val) || 0);
    if (!minutes) return;
    await addManualMinutes({ kind: 'weekday', minutes });
  };

  // Render
  // Определяем, мобильное ли устройство
  const isMobileDevice = typeof window !== 'undefined' && (window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  
  console.log('🔍 [LeavesWorktimeModal] Рендер, isOpen:', isOpen, 'isMobileDevice:', isMobileDevice);
  
  const modalStyles = isMobileDevice ? {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      zIndex: 250000, // Выше #app-modal-root (200000)
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      position: 'fixed'
    },
    content: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: '100%',
      margin: 0,
      padding: 0,
      border: 'none',
      background: 'transparent',
      overflow: 'auto',
      inset: 0
    }
  } : {
    overlay: {
      backgroundColor: 'rgba(0,0,0,0.45)',
      zIndex: 250000,
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      left: '320px',
      width: 'calc(100vw - 320px)',
      height: '100vh',
      margin: 0,
      padding: 0,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      pointerEvents: 'auto'
    },
    content: {
      position: 'fixed',
      top: 0,
      left: '320px',
      right: 0,
      bottom: 0,
      width: 'calc(100vw - 320px)',
      maxWidth: 'calc(100vw - 320px)',
      height: '100vh',
      maxHeight: '100vh',
      margin: 0,
      padding: 0,
      inset: 'unset',
      border: 'none',
      background: 'transparent',
      overflow: 'hidden',
      zIndex: 250001,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      transform: 'none !important',
      pointerEvents: 'auto',
      boxSizing: 'border-box'
    }
  };

  return (
    <>
    <style>{`
      [data-leaves-worktime-modal] {
        color: var(--modal-worktime-text-color, #ffffff);
        font-size: var(--modal-worktime-text-size, 14px);
        font-family: var(--modal-worktime-font-family, Inter, system-ui, -apple-system, sans-serif);
      }
      [data-leaves-worktime-modal] input,
      [data-leaves-worktime-modal] select,
      [data-leaves-worktime-modal] textarea {
        background: var(--modal-worktime-input-background, #2d3748) !important;
        color: var(--modal-worktime-input-text-color, #ffffff) !important;
        border: 1px solid var(--modal-worktime-border-color, rgba(67, 233, 123, 0.3)) !important;
      }
      [data-leaves-worktime-modal] button:not([data-worktime-filter]) {
        background: var(--modal-worktime-button-background, #3b82f6) !important;
        color: var(--modal-worktime-button-text-color, #ffffff) !important;
        border: 1px solid var(--modal-worktime-border-color, rgba(67, 233, 123, 0.3)) !important;
      }
    `}</style>
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Отработанное время по отгулам"
      style={modalStyles}
      ariaHideApp={false}
      overlayClassName={isMobileDevice ? undefined : "ReactModal__Overlay--leaves-worktime"}
    >
      {/* Скрытое аудио для воспроизведения звука */}
      <audio ref={audioRef} src="/time_of.mp3" preload="auto" />

      <div
        onClick={onRequestClose}
        style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'stretch', 
          justifyContent: 'flex-start',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          data-leaves-worktime-modal
          style={{
            background: 'var(--modal-worktime-background, linear-gradient(135deg, #232931 0%, #181c22 100%))',
            backgroundImage: 'var(--modal-worktime-background-image, none)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            borderRadius: 0,
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            maxHeight: '100%',
            boxSizing: 'border-box',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            color: 'var(--modal-worktime-text-color, #fff)',
            padding: isMobileDevice ? '16px' : '40px 48px',
            paddingTop: isMobileDevice ? '60px' : '40px',
            overflowY: 'auto',
            overflowX: 'auto',
            margin: 0
          }}
        >
          {(() => {
            if (typeof document === 'undefined') return null;
            const bgImageVar = getComputedStyle(document.documentElement)
              .getPropertyValue('--modal-worktime-background-image')
              .trim();
            const hasImage = bgImageVar && bgImageVar !== 'none' && !bgImageVar.includes('gradient(');
            if (!hasImage) return null;
            const opacityVar = getComputedStyle(document.documentElement)
              .getPropertyValue('--modal-worktime-background-image-opacity')
              .trim() || '1';
            return (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'var(--modal-worktime-background, #111827)',
                  opacity: `calc(1 - ${opacityVar})`,
                  pointerEvents: 'none'
                }}
              />
            );
          })()}
          <button
            onClick={onRequestClose}
            style={{
              position: 'absolute',
              top: isMobileDevice ? 12 : 16,
              right: isMobileDevice ? 12 : 16,
              fontSize: 28,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#fff',
              fontWeight: 'bold',
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.3s, color 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.18)';
              e.currentTarget.style.color = '#43e97b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#fff';
            }}
            aria-label="Close modal"
          >
            ×
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobileDevice ? 8 : 16, marginBottom: isMobileDevice ? 12 : 18, flexWrap: isMobileDevice ? 'wrap' : 'nowrap' }}>
            <h2 style={{
              marginTop: 0,
              marginBottom: 0,
              color: 'var(--modal-worktime-title-color, #43e97b)',
              fontWeight: 900,
              fontSize: isMobileDevice ? '1.4em' : '2em',
              letterSpacing: '0.5px',
              textShadow: 'none'
            }}>
              Мониторинг отработанного времени
            </h2>


            <div style={{
              padding: '4px 12px',
              borderRadius: 20,
              background: userRole === 'admin'
                ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)'
                : userRole === 'hr'
                  ? 'linear-gradient(135deg, #4dabf7 0%, #339af0 100%)'
                  : 'linear-gradient(135deg, #51cf66 0%, #40c057 100%)',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}>
              {userRole === 'admin' ? '👑 Админ' : userRole === 'hr' ? '👥 HR' : '👤 Сотрудник'}
            </div>
          </div>

          {/* Кнопки выбора режима отработки */}
          <div style={{
            display: 'flex',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            <button
              data-worktime-filter="offhours"
              onClick={() => setWorkMode(workMode === 'offhours' ? null : 'offhours')}
              style={{
                padding: '12px 24px',
                borderRadius: 12,
                border: `2px solid ${workMode === 'offhours' ? '#6dd5ed' : 'var(--modal-worktime-filter-after-hours-border-color, rgba(109, 213, 237, 0.3))'}`,
                background: workMode === 'offhours' 
                  ? 'var(--modal-worktime-filter-after-hours-background, linear-gradient(135deg, rgba(109, 213, 237, 0.2) 0%, rgba(109, 213, 237, 0.1) 100%))'
                  : 'var(--modal-worktime-filter-after-hours-background, rgba(109, 213, 237, 0.05))',
                color: workMode === 'offhours' ? '#6dd5ed' : 'var(--modal-worktime-filter-after-hours-text-color, #a0aec0)',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: 700,
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: workMode === 'offhours' ? '0 4px 12px rgba(109, 213, 237, 0.3)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (workMode !== 'offhours') {
                  e.currentTarget.style.background = 'var(--modal-worktime-filter-after-hours-background, rgba(109, 213, 237, 0.1))';
                  e.currentTarget.style.color = '#6dd5ed';
                  e.currentTarget.style.borderColor = 'var(--modal-worktime-filter-after-hours-border-color, rgba(109, 213, 237, 0.5))';
                }
              }}
              onMouseLeave={(e) => {
                if (workMode !== 'offhours') {
                  e.currentTarget.style.background = 'var(--modal-worktime-filter-after-hours-background, rgba(109, 213, 237, 0.05))';
                  e.currentTarget.style.color = '#a0aec0';
                  e.currentTarget.style.borderColor = 'var(--modal-worktime-filter-after-hours-border-color, rgba(109, 213, 237, 0.3))';
                }
              }}
            >
              🌙 Отработка в нерабочее время
            </button>
            <button
              data-worktime-filter="weekdays"
              onClick={() => {
                if (!isWeekdaysWorktimeAllowed()) {
                  setNotification({
                    type: 'warning',
                    message: '⏰ Отработка по будням доступна только с 6:00 до 23:00',
                    show: true,
                    persistent: false
                  });
                  setTimeout(() => setNotification(null), 4000);
                  return;
                }
                setWorkMode(workMode === 'weekdays' ? null : 'weekdays');
              }}
              disabled={!isWeekdaysWorktimeAllowed() && !workTimeCheckDisabled}
              style={{
                padding: '12px 24px',
                borderRadius: 12,
                border: `2px solid ${workMode === 'weekdays' ? '#ffc107' : 'var(--modal-worktime-filter-weekdays-border-color, rgba(255, 193, 7, 0.3))'}`,
                background: workMode === 'weekdays' 
                  ? 'var(--modal-worktime-filter-weekdays-background, linear-gradient(135deg, rgba(255, 193, 7, 0.2) 0%, rgba(255, 193, 7, 0.1) 100%))'
                  : (!isWeekdaysWorktimeAllowed() && !workTimeCheckDisabled ? 'rgba(108, 117, 125, 0.1)' : 'var(--modal-worktime-filter-weekdays-background, rgba(255, 193, 7, 0.05))'),
                color: workMode === 'weekdays' ? '#ffc107' : ((!isWeekdaysWorktimeAllowed() && !workTimeCheckDisabled) ? '#6c757d' : 'var(--modal-worktime-filter-weekdays-text-color, #a0aec0)'),
                cursor: (!isWeekdaysWorktimeAllowed() && !workTimeCheckDisabled) ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
                fontWeight: 700,
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: workMode === 'weekdays' ? '0 4px 12px rgba(255, 193, 7, 0.3)' : 'none',
                opacity: (!isWeekdaysWorktimeAllowed() && !workTimeCheckDisabled) ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (workMode !== 'weekdays' && isWeekdaysWorktimeAllowed()) {
                  e.currentTarget.style.background = 'var(--modal-worktime-filter-weekdays-background, rgba(255, 193, 7, 0.1))';
                  e.currentTarget.style.color = '#ffc107';
                  e.currentTarget.style.borderColor = 'var(--modal-worktime-filter-weekdays-border-color, rgba(255, 193, 7, 0.5))';
                }
              }}
              onMouseLeave={(e) => {
                if (workMode !== 'weekdays') {
                  e.currentTarget.style.background = (!isWeekdaysWorktimeAllowed() && !workTimeCheckDisabled)
                    ? 'rgba(108, 117, 125, 0.1)'
                    : 'var(--modal-worktime-filter-weekdays-background, rgba(255, 193, 7, 0.05))';
                  e.currentTarget.style.color = (!isWeekdaysWorktimeAllowed() && !workTimeCheckDisabled) ? '#6c757d' : '#a0aec0';
                  e.currentTarget.style.borderColor = 'var(--modal-worktime-filter-weekdays-border-color, rgba(255, 193, 7, 0.3))';
                }
              }}
            >
              📅 Отработка по будням
            </button>
            <button
              data-worktime-filter="weekends"
              onClick={() => {
                if (!isWeekendsWorktimeAllowed()) {
                  setNotification({
                    type: 'warning',
                    message: '⏰ Отработка по выходным доступна только с 6:00 до 23:00',
                    show: true,
                    persistent: false
                  });
                  setTimeout(() => setNotification(null), 4000);
                  return;
                }
                setWorkMode(workMode === 'weekends' ? null : 'weekends');
              }}
              disabled={!isWeekendsWorktimeAllowed() && !workTimeCheckDisabled}
              style={{
                padding: '12px 24px',
                borderRadius: 12,
                border: `2px solid ${workMode === 'weekends' ? '#43e97b' : 'var(--modal-worktime-filter-weekends-border-color, rgba(67, 233, 123, 0.3))'}`,
                background: workMode === 'weekends' 
                  ? 'var(--modal-worktime-filter-weekends-background, linear-gradient(135deg, rgba(67, 233, 123, 0.2) 0%, rgba(67, 233, 123, 0.1) 100%))'
                  : (!isWeekendsWorktimeAllowed() && !workTimeCheckDisabled ? 'rgba(108, 117, 125, 0.1)' : 'var(--modal-worktime-filter-weekends-background, rgba(67, 233, 123, 0.05))'),
                color: workMode === 'weekends' ? '#43e97b' : ((!isWeekendsWorktimeAllowed() && !workTimeCheckDisabled) ? '#6c757d' : 'var(--modal-worktime-filter-weekends-text-color, #a0aec0)'),
                cursor: (!isWeekendsWorktimeAllowed() && !workTimeCheckDisabled) ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem',
                fontWeight: 700,
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                boxShadow: workMode === 'weekends' ? '0 4px 12px rgba(67, 233, 123, 0.3)' : 'none',
                opacity: (!isWeekendsWorktimeAllowed() && !workTimeCheckDisabled) ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (workMode !== 'weekends' && isWeekendsWorktimeAllowed()) {
                  e.currentTarget.style.background = 'var(--modal-worktime-filter-weekends-background, rgba(67, 233, 123, 0.1))';
                  e.currentTarget.style.color = '#43e97b';
                  e.currentTarget.style.borderColor = 'var(--modal-worktime-filter-weekends-border-color, rgba(67, 233, 123, 0.5))';
                }
              }}
              onMouseLeave={(e) => {
                if (workMode !== 'weekends') {
                  e.currentTarget.style.background = (!isWeekendsWorktimeAllowed() && !workTimeCheckDisabled)
                    ? 'rgba(108, 117, 125, 0.1)'
                    : 'var(--modal-worktime-filter-weekends-background, rgba(67, 233, 123, 0.05))';
                  e.currentTarget.style.color = (!isWeekendsWorktimeAllowed() && !workTimeCheckDisabled) ? '#6c757d' : '#a0aec0';
                  e.currentTarget.style.borderColor = 'var(--modal-worktime-filter-weekends-border-color, rgba(67, 233, 123, 0.3))';
                }
              }}
            >
              🎉 Отработка по выходным
            </button>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: isMobileDevice ? 8 : 16, marginBottom: isMobileDevice ? 12 : 20, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: isMobileDevice ? 4 : 8, alignItems: 'center', flexWrap: isMobileDevice ? 'wrap' : 'nowrap' }}>
              <span style={{ color: 'var(--modal-worktime-text-color, #ffe082)', fontWeight: 600, fontSize: isMobileDevice ? '0.8rem' : '0.9rem' }}>Фильтр:</span>
              <button data-worktime-filter="pending" onClick={() => setFilter('pending')} style={{
                padding: isMobileDevice ? '5px 10px' : '6px 12px',
                borderRadius: 8,
                border: filter === 'pending' ? `2px solid var(--modal-worktime-filter-pending-border-color, #43e97b)` : '1px solid var(--modal-worktime-filter-pending-border-color, #4a5568)',
                background: filter === 'pending' ? 'var(--modal-worktime-filter-pending-background, #43e97b22)' : 'var(--modal-worktime-filter-pending-background, transparent)',
                color: filter === 'pending' ? 'var(--modal-worktime-filter-pending-text-color, #43e97b)' : 'var(--modal-worktime-filter-pending-text-color, #a0aec0)',
                cursor: 'pointer',
                fontSize: isMobileDevice ? '0.8rem' : '0.85rem',
                fontWeight: 600
              }}>📋 На очереди</button>
              <button data-worktime-filter="completed" onClick={() => { setFilter('completed'); setWorkMode(null); }} style={{
                padding: isMobileDevice ? '5px 10px' : '6px 12px',
                borderRadius: 8,
                border: filter === 'completed' ? `2px solid var(--modal-worktime-filter-completed-border-color, #28a745)` : '1px solid var(--modal-worktime-filter-completed-border-color, #4a5568)',
                background: filter === 'completed' ? 'var(--modal-worktime-filter-completed-background, #28a74522)' : 'var(--modal-worktime-filter-completed-background, transparent)',
                color: filter === 'completed' ? 'var(--modal-worktime-filter-completed-text-color, #28a745)' : 'var(--modal-worktime-filter-completed-text-color, #a0aec0)',
                cursor: 'pointer',
                fontSize: isMobileDevice ? '0.8rem' : '0.85rem',
                fontWeight: 600
              }}>💼 Отработанные</button>
            </div>

            {filter === 'completed' && isHROrAdmin && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ color: '#a0aec0', fontSize: '0.85rem' }}>Удалить данные отработки:</span>
                {['day', 'week', 'month'].map((period) => (
                  <button
                    key={period}
                    type="button"
                    disabled={workoffCleanupLoading}
                    onClick={async () => {
                      const label = period === 'day' ? 'за день' : period === 'week' ? 'за неделю' : 'за месяц';
                      if (!window.confirm(`Удалить все данные отработки (отработки, сессии, логи, скриншоты) ${label}? Это действие нельзя отменить.`)) return;
                      setWorkoffCleanupLoading(true);
                      try {
                        const res = await fetch('/api/worktime/cleanup', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                          body: JSON.stringify({ period })
                        });
                        const json = await res.json().catch(() => ({}));
                        if (res.ok && json.success) {
                          setNotification({ type: 'success', message: json.message || 'Данные удалены', show: true, persistent: false });
                          if (typeof reloadLeavesData === 'function') reloadLeavesData();
                        } else {
                          setNotification({ type: 'error', message: json.error || 'Ошибка удаления', show: true, persistent: false });
                        }
                      } catch (e) {
                        setNotification({ type: 'error', message: e.message || 'Ошибка запроса', show: true, persistent: false });
                      } finally {
                        setWorkoffCleanupLoading(false);
                      }
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      border: '1px solid #dc3545',
                      background: 'rgba(220, 53, 69, 0.15)',
                      color: '#dc3545',
                      cursor: workoffCleanupLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    {period === 'day' ? 'За день' : period === 'week' ? 'За неделю' : 'За месяц'}
                  </button>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: isMobileDevice ? 4 : 8, alignItems: 'center', flexWrap: isMobileDevice ? 'wrap' : 'nowrap' }}>
              <span style={{ color: 'var(--modal-worktime-text-color, #ffe082)', fontWeight: 600, fontSize: isMobileDevice ? '0.8rem' : '0.9rem' }}>Дата:</span>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{
                padding: isMobileDevice ? '5px 8px' : '6px 10px',
                borderRadius: 8,
                border: '1px solid #4a5568',
                background: '#2d3748',
                color: '#fff',
                fontSize: isMobileDevice ? '0.8rem' : '0.85rem',
                outline: 'none'
              }} />
            </div>

            {(userRole === 'hr' || userRole === 'admin') && (
              <div style={{ display: 'flex', gap: isMobileDevice ? 4 : 8, alignItems: 'center', flexWrap: isMobileDevice ? 'wrap' : 'nowrap', width: isMobileDevice ? '100%' : 'auto' }}>
                <span style={{ color: 'var(--modal-worktime-text-color, #ffe082)', fontWeight: 600, fontSize: isMobileDevice ? '0.8rem' : '0.9rem' }}>Поиск:</span>
                <input type="text" placeholder={
                  filter === 'completed' 
                    ? "Поиск отработанных отгулов по имени..." 
                    : "Введите имя сотрудника..."
                } value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} style={{
                  padding: isMobileDevice ? '5px 8px' : '6px 10px',
                  borderRadius: 8,
                  border: '1px solid #4a5568',
                  background: '#2d3748',
                  color: '#fff',
                  fontSize: isMobileDevice ? '0.8rem' : '0.85rem',
                  outline: 'none',
                  minWidth: isMobileDevice ? '150px' : '200px',
                  flex: isMobileDevice ? '1 1 auto' : 'none',
                  maxWidth: isMobileDevice ? '100%' : 'none'
                }} />
                {nameFilter && <button onClick={() => setNameFilter('')} style={{
                  padding: '4px 8px',
                  borderRadius: 6,
                  border: '1px solid #ff6b6b',
                  background: 'rgba(255, 107, 107, 0.1)',
                  color: '#ff6b6b',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}>✕</button>}
              </div>
            )}


          </div>

          {/* Информационное сообщение когда таймер отработки отгулов недоступен - скрыто, используются только режимы работы */}
          {false && !workMode && currentUser && !isLeaveWorktimeAllowed() && !workTimeCheckDisabled && (
            <div style={{
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
              textAlign: 'center'
            }}>
              <div style={{ color: '#ffc107', fontWeight: 600, marginBottom: 8 }}>
                ⏰ Таймер отработки отгулов недоступен
              </div>
              <div style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                {(() => {
                  const now = new Date();
                  const minutes = now.getHours() * 60 + now.getMinutes();
                  const workStart = 7 * 60;  // 7:00
                  const workEnd = 23 * 60;   // 23:00
                  const timeAllowed = minutes >= workStart && minutes <= workEnd;
                  
                  if (!timeAllowed) {
                    return "Отработка отгулов доступна с 7:00 до 23:00";
                  } else {
                    return "У вас нет неотработанных отгулов";
                  }
                })()}
              </div>
            </div>
          )}

          {/* Weekend panel - скрыт, используется только режим работы */}
          {false && !workMode && isTodayWeekend() && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
              background: 'rgba(67,233,123,0.08)',
              border: '1px solid rgba(67,233,123,0.35)',
              borderRadius: 12,
              padding: '12px 14px'
            }}>
              <span style={{ color: '#b2ffb2', fontWeight: 700 }}>Отработка по выходным (сегодня):</span>
              <span style={{ color: '#fff' }}>
                накоплено {Math.floor(getTotalWeekendMinutes() / 60)} ч {getTotalWeekendMinutes() % 60} мин
                {weekendRunning && !isWorktimeCompletedForUser(currentUser) && <span style={{ color: '#43e97b', fontWeight: 'bold' }}> ⏱️ работает</span>}
              </span>

              {!weekendRunning ? (
                <button onClick={startWeekend} disabled={isWorktimeCompletedForUser(currentUser)} style={{
                  padding: '8px 12px',
                  borderRadius: 10,
                  border: '1px solid #43e97b88',
                  background: isWorktimeCompletedForUser(currentUser) ? '#4a5568' : '#2a3a2f',
                  color: isWorktimeCompletedForUser(currentUser) ? '#a0aec0' : '#b2ffb2',
                  cursor: isWorktimeCompletedForUser(currentUser) ? 'not-allowed' : 'pointer',
                  opacity: isWorktimeCompletedForUser(currentUser) ? 0.6 : 1
                }}>
                  {isWorktimeCompletedForUser(currentUser) ? 'Отработка завершена' : (getTotalWeekendMinutes() > 0 ? 'Продолжить отработку' : 'Начать отработку')}
                </button>
              ) : (
                <button onClick={stopWeekend} style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid #ffe08288', background: '#3a332a', color: '#ffe082', cursor: 'pointer' }}>Пауза (сохранить)</button>
              )}

              <button onClick={handleAddManualWeekend} style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid #6dd5ed88', background: '#253642', color: '#6dd5ed', cursor: 'pointer' }}>Добавить минуты вручную</button>
            </div>
          )}

          {/* Weekday panel - скрыт, используется только режим работы */}
          {false && !workMode && isTodayWeekday() && hasWorktimeObligation(currentUser) && isWorktimeAllowed() && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
              background: 'rgba(255,193,7,0.08)',
              border: '1px solid rgba(255,193,7,0.35)',
              borderRadius: 12,
              padding: '12px 14px'
            }}>
              <span style={{ color: '#ffd700', fontWeight: 700 }}>Отработка по будням (сегодня):</span>
              <span style={{ color: '#fff' }}>
                накоплено {Math.floor(getTotalWeekdayMinutes() / 60)} ч {getTotalWeekdayMinutes() % 60} мин
                {weekdayRunning && !isWorktimeCompletedForUser(currentUser) && <span style={{ color: '#43e97b', fontWeight: 'bold' }}> ⏱️ работает</span>}
              </span>

              {!weekdayRunning ? (
                <button
                  onClick={async () => {
                    try {
                      await startWeekday();
                    } catch (e) {
                      setNotification({
                        type: 'error',
                        message: 'Ошибка запуска отработки по будням: ' + (e?.message || 'Не удалось начать'),
                        show: true,
                        persistent: false
                      });
                      setTimeout(() => setNotification(null), 2500);
                    }
                  }}
                  disabled={isWorktimeCompletedForUser(currentUser)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 10,
                    border: '1px solid #ffc10788',
                    background: isWorktimeCompletedForUser(currentUser) ? '#4a5568' : '#3a332a',
                    color: isWorktimeCompletedForUser(currentUser) ? '#a0aec0' : '#ffd700',
                    cursor: isWorktimeCompletedForUser(currentUser) ? 'not-allowed' : 'pointer',
                    opacity: isWorktimeCompletedForUser(currentUser) ? 0.6 : 1
                  }}
                >
                  {isWorktimeCompletedForUser(currentUser) ? 'Отработка завершена' : (getTotalWeekdayMinutes() > 0 ? 'Продолжить отработку' : 'Начать отработку')}
                </button>
              ) : (
                <button onClick={stopWeekday} style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid #ffe08288', background: '#3a332a', color: '#ffe082', cursor: 'pointer' }}>Пауза (сохранить)</button>
              )}

              <button onClick={handleAddManualWeekday} style={{ padding: '8px 12px', borderRadius: 10, border: '1px solid #6dd5ed88', background: '#253642', color: '#6dd5ed', cursor: 'pointer' }}>Добавить минуты вручную</button>
            </div>
          )}

          {/* ПАНЕЛЬ ВЫБРАННЫХ ОТГУЛОВ - скрыта, используется только в режимах работы */}
          {false && currentUser && selectedLeaves.size > 0 && (
            <div style={{
              background: 'rgba(67, 233, 123, 0.1)',
              border: '1px solid rgba(67, 233, 123, 0.3)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: 12
              }}>
                <span style={{ color: '#43e97b', fontWeight: 700 }}>
                  {selectedLeaves.size > 0 ? `Выбран отгул для изолированной отработки` : 'Выберите ОДИН отгул для отработки'}
                </span>
                {selectedLeaves.size > 0 && (
                  <button
                    onClick={clearLeaveSelection}
                    style={{
                      background: 'rgba(220, 53, 69, 0.2)',
                      border: '1px solid #dc3545',
                      borderRadius: 6,
                      color: '#dc3545',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    Очистить
                  </button>
                )}
              </div>
              <div style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                {(() => {
                  const required = getSelectedLeavesRequiredMinutes();
                  const worked = getSelectedLeavesWorkedMinutes();
                  const remaining = getSelectedLeavesRemainingMinutes();
                  
                  if (worked > 0) {
                    return (
                      <>
                        <strong style={{ color: '#43e97b' }}>Отработано: {worked} минут ({Math.floor(worked / 60)}ч {worked % 60}м)</strong>
                        {' / '}
                        <strong style={{ color: '#ffc107' }}>Требуется: {required} минут ({Math.floor(required / 60)}ч {required % 60}м)</strong>
                        <br />
                        <span style={{ color: '#ffc107' }}>Осталось отработать: <strong>{remaining} минут ({Math.floor(remaining / 60)}ч {remaining % 60}м)</strong></span>
                      </>
                    );
                  } else {
                    return (
                      <strong style={{ color: '#ffc107' }}>
                        Требуется: {required} минут ({Math.floor(required / 60)}ч {required % 60}м)
                      </strong>
                    );
                  }
                })()}
              </div>
              <div style={{ color: '#6c757d', fontSize: '0.8rem', marginTop: 4 }}>
                ⚠️ Каждый отгул отрабатывается отдельно и изолированно. Вы можете прерывать и продолжать отработку в другой день.
              </div>
            </div>
          )}


          {/* НОВЫЙ ИНТЕРФЕЙС ТАЙМЕРА ОТРАБОТКИ ОТГУЛОВ - скрыт, используются только три режима работы */}
          {false && (
            <div style={{
              background: 'linear-gradient(135deg, #2a3441 0%, #3a4553 100%)',
              borderRadius: 16,
              padding: 20,
              marginBottom: 20,
              border: '1px solid rgba(67, 233, 123, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              {/* Заголовок */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: isTimerRunning ? '#43e97b' : '#6c757d'
                  }} />
                  <span style={{ color: '#43e97b', fontWeight: 700, fontSize: '1.1rem' }}>
                    Таймер отработки
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={() => {
                      const newState = !isTimerMinimized;
                      setIsTimerMinimized(newState);
                      localStorage.setItem('timerMinimized', newState.toString());
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 6,
                      color: '#ffffff',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                    title={isTimerMinimized ? 'Развернуть таймер' : 'Свернуть таймер'}
                  >
                    {isTimerMinimized ? '🔼' : '🔽'}
                  </button>
                  <button
                    onClick={() => setIsTimerRunning(false)}
                    style={{
                      background: isTimerRunning ? 'rgba(67, 233, 123, 0.2)' : 'rgba(108, 117, 125, 0.2)',
                      border: `1px solid ${isTimerRunning ? 'rgba(67, 233, 123, 0.3)' : 'rgba(108, 117, 125, 0.3)'}`,
                      borderRadius: 8,
                      color: isTimerRunning ? '#43e97b' : '#6c757d',
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '0.85rem'
                    }}
                  >
                    {isTimerRunning ? 'Активен' : 'Остановлен'}
                  </button>
                </div>
              </div>

              {/* Компактный режим таймера */}
              {isTimerMinimized && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 8,
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  marginBottom: 20
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: '#ffffff',
                      fontFamily: 'monospace'
                    }}>
                      {formatTimerDisplay(timerSeconds)}
                    </div>
                    {getSelectedLeavesRequiredMinutes() > 0 && (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: 4,
                        padding: '2px 8px',
                        fontSize: '0.8rem',
                        color: '#a0aec0'
                      }}>
                        {Math.floor(timerSeconds / 60)}/{getSelectedLeavesRequiredMinutes()} мин
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {!isTimerRunning ? (
                      <button
                        onClick={startNewTimer}
                        style={{
                          background: 'rgba(67, 233, 123, 0.2)',
                          border: '1px solid #43e97b',
                          borderRadius: 6,
                          color: '#43e97b',
                          padding: '6px 12px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        {getSelectedLeavesWorkedMinutes() > 0 ? '▶ Продолжить отработку' : '▶ Старт'}
                      </button>
                    ) : (
                      <button
                        onClick={stopNewTimer}
                        style={{
                          background: 'rgba(255, 193, 7, 0.2)',
                          border: '1px solid #ffc107',
                          borderRadius: 6,
                          color: '#ffc107',
                          padding: '6px 12px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        ⏸ Стоп
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Контент таймера - скрывается при сворачивании */}
              {!isTimerMinimized && (
                <>
                  {/* Большой таймер */}
                  <div style={{
                    textAlign: 'center',
                    marginBottom: 30
                  }}>
                <div style={{
                  fontSize: '4rem',
                  fontWeight: 'bold',
                  color: getSelectedLeavesRequiredMinutes() > 0 && Math.floor(timerSeconds / 60) >= getSelectedLeavesRequiredMinutes() 
                    ? '#43e97b' 
                    : '#ffffff',
                  fontFamily: 'monospace',
                  letterSpacing: '0.1em',
                  textShadow: getSelectedLeavesRequiredMinutes() > 0 && Math.floor(timerSeconds / 60) >= getSelectedLeavesRequiredMinutes() 
                    ? '0 0 20px rgba(67, 233, 123, 0.5)' 
                    : 'none',
                  transition: 'all 0.3s ease'
                }}>
                  {formatTimerDisplay(timerSeconds)}
                </div>
                {getSelectedLeavesRequiredMinutes() > 0 && Math.floor(timerSeconds / 60) >= getSelectedLeavesRequiredMinutes() && (
                  <div style={{
                    color: '#43e97b',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    marginTop: 10,
                    animation: 'pulse 1.5s infinite'
                  }}>
                    🎉 ЦЕЛЬ ДОСТИГНУТА! 🎉
                  </div>
                )}
              </div>

              {/* Статистика */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 20,
                gap: 20
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#a0aec0', fontSize: '0.9rem', marginBottom: 4 }}>
                    Всего отработано
                  </div>
                  <div style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {Math.floor(timerSeconds / 60)} мин
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#a0aec0', fontSize: '0.9rem', marginBottom: 4 }}>
                    Требуется
                  </div>
                  <div style={{ color: '#ffc107', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {getSelectedLeavesRequiredMinutes()} мин
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#a0aec0', fontSize: '0.9rem', marginBottom: 4 }}>
                    Осталось
                  </div>
                  <div style={{ 
                    color: getSelectedLeavesRequiredMinutes() - Math.floor(timerSeconds / 60) <= 0 ? '#43e97b' : '#ffc107', 
                    fontWeight: 'bold', 
                    fontSize: '1.1rem' 
                  }}>
                    {Math.max(0, getSelectedLeavesRequiredMinutes() - Math.floor(timerSeconds / 60))} мин
                  </div>
                </div>
              </div>

              {/* Прогресс-бар */}
              {getSelectedLeavesRequiredMinutes() > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 10,
                    height: 8,
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      background: `linear-gradient(90deg, ${
                        Math.floor(timerSeconds / 60) >= getSelectedLeavesRequiredMinutes() 
                          ? '#43e97b' 
                          : '#ffc107'
                      } 0%, ${
                        Math.floor(timerSeconds / 60) >= getSelectedLeavesRequiredMinutes() 
                          ? '#38d9a9' 
                          : '#ffb300'
                      } 100%)`,
                      height: '100%',
                      width: `${Math.min(100, (Math.floor(timerSeconds / 60) / getSelectedLeavesRequiredMinutes()) * 100)}%`,
                      transition: 'width 0.3s ease, background 0.3s ease'
                    }} />
                  </div>
                  <div style={{ 
                    textAlign: 'center', 
                    marginTop: 8, 
                    color: '#a0aec0', 
                    fontSize: '0.8rem' 
                  }}>
                    Прогресс: {Math.min(100, Math.round((Math.floor(timerSeconds / 60) / getSelectedLeavesRequiredMinutes()) * 100))}%
                  </div>
                </div>
              )}

              {/* Кнопка управления таймером */}
              <div style={{ textAlign: 'center' }}>
                {!isTimerRunning ? (
                  <button
                    onClick={startNewTimer}
                    style={{
                      background: 'linear-gradient(135deg, #43e97b 0%, #38d9a9 100%)',
                      border: 'none',
                      borderRadius: 12,
                      color: '#ffffff',
                      padding: '12px 24px',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      margin: '0 auto',
                      boxShadow: '0 4px 16px rgba(67, 233, 123, 0.3)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(67, 233, 123, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(67, 233, 123, 0.3)';
                    }}
                  >
                    {getSelectedLeavesWorkedMinutes() > 0 ? '▶ Продолжить отработку' : '▶ Начать отработку'}
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                    <button
                      onClick={stopNewTimer}
                      style={{
                        background: 'rgba(255, 193, 7, 0.2)',
                        border: '1px solid #ffc107',
                        borderRadius: 8,
                        color: '#ffc107',
                        padding: '8px 16px',
                        cursor: 'pointer'
                      }}
                    >
                      ⏸ Пауза
                    </button>
                    <button
                      onClick={resetNewTimer}
                      style={{
                        background: 'rgba(220, 53, 69, 0.2)',
                        border: '1px solid #dc3545',
                        borderRadius: 8,
                        color: '#dc3545',
                        padding: '8px 16px',
                        cursor: 'pointer'
                      }}
                    >
                      🔄 Сброс
                    </button>
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          )}

          {/* Основной контент с таблицей - показываем только если не выбран режим работы */}
          {!workMode && (loading ? <div>Загрузка...</div> : error ? <div>{error}</div> : (
            <div>
              <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                    Показано: <strong style={{ color: '#43e97b' }}>{filteredData.length}</strong> из <strong style={{ color: '#ffe082' }}>{data.length}</strong> записей
                    {filter !== 'pending' && (
                      <span style={{ marginLeft: 8, color: '#ffc107' }}>
                        (фильтр: {filter === 'completed' ? 'Отработанные' : 'На очереди'})
                      </span>
                    )}
                    {nameFilter && <span style={{ marginLeft: 8, color: '#4dabf7' }}> (поиск: "{nameFilter}")</span>}
                  </span>
                  
                  {filter === 'completed' && (userRole === 'hr' || userRole === 'admin') && (
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#a0aec0',
                      fontStyle: 'italic',
                      marginTop: 4,
                      padding: '4px 8px',
                      background: 'rgba(67, 233, 123, 0.1)',
                      borderRadius: 4,
                      border: '1px solid rgba(67, 233, 123, 0.2)'
                    }}>
                      💡 <strong>Архив отработанных отгулов:</strong> Полная история с датами завершения. 
                      Используйте поиск для быстрого поиска по сотрудникам.
                    </div>
                  )}
                  
                  {filter === 'completed' && userRole === 'employee' && (
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#a0aec0',
                      fontStyle: 'italic',
                      marginTop: 4,
                      padding: '4px 8px',
                      background: 'rgba(255, 193, 7, 0.1)',
                      borderRadius: 4,
                      border: '1px solid rgba(255, 193, 7, 0.2)'
                    }}>
                      📚 <strong>Ваши отработанные отгулы</strong> с полной информацией о времени завершения
                    </div>
                  )}

                  {filter === 'pending' && (userRole === 'hr' || userRole === 'admin') && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#a0aec0',
                      fontStyle: 'italic',
                      marginTop: 4,
                      padding: '4px 8px',
                      background: 'rgba(67, 233, 123, 0.1)',
                      borderRadius: 4,
                      border: '1px solid rgba(67, 233, 123, 0.2)'
                    }}>
                      📋 <strong>Отгулы на очереди:</strong> Новые отгулы, готовые к отработке.
                      Показаны только те, которые еще не начинали отрабатываться.
                    </div>
                  )}

                  {filter === 'pending' && userRole === 'employee' && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#a0aec0',
                      fontStyle: 'italic',
                      marginTop: 4,
                      padding: '4px 8px',
                      background: 'rgba(67, 233, 123, 0.1)',
                      borderRadius: 4,
                      border: '1px solid rgba(67, 233, 123, 0.2)'
                    }}>
                      📋 <strong>Ваши отгулы на очереди</strong> - готовые к отработке
                    </div>
                  )}
                </div>
              </div>

              {/* Блок для режима "Отработка в нерабочее время" */}
              {workMode === 'offhours' && (
                <>
                  {/* Таймер для нерабочего времени */}
                  <div style={{
                    background: 'linear-gradient(135deg, #2a3441 0%, #3a4553 100%)',
                    borderRadius: 16,
                    padding: 20,
                    marginBottom: 20,
                    border: '1px solid rgba(109, 213, 237, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                      <div style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: offhoursTimerRunning ? '#6dd5ed' : '#6c757d'
                      }} />
                      <span style={{ color: '#6dd5ed', fontWeight: 700, fontSize: '1.1rem' }}>
                        🌙 Отработка в нерабочее время
                      </span>
                    </div>
                    <div style={{ textAlign: 'center', marginBottom: 30 }}>
                      <div style={{
                        fontSize: '4rem',
                        fontWeight: 'bold',
                        color: '#6dd5ed',
                        fontFamily: 'monospace',
                        letterSpacing: '0.1em',
                        textShadow: '0 0 20px rgba(109, 213, 237, 0.5)',
                        transition: 'all 0.3s ease'
                      }}>
                        {formatTimerDisplay(offhoursTimerSeconds)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, gap: 20 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#a0aec0', fontSize: '0.9rem', marginBottom: 4 }}>Всего отработано</div>
                        <div style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {Math.floor(offhoursTimerSeconds / 60)} мин
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      {!offhoursTimerRunning ? (
                        <button
                          onClick={async () => {
                            // Проверяем доступность времени перед запуском
                            if (!isOffhoursWorktimeAllowed()) {
                              setNotification({
                                type: 'warning',
                                message: '⏰ Отработка в нерабочее время доступна только с 6:00 до 8:59 и с 18:01 до 23:00',
                                show: true,
                                persistent: false
                              });
                              setTimeout(() => setNotification(null), 4000);
                              return;
                            }
                            
                            // Загружаем уже накопленное время из БД для выбранной даты
                            if (currentUser) {
                              try {
                                const day = selectedDate;
                                const worktimeUrl = `/api/worktime-stats/${day}`;
                                const worktimeRes = await fetch(worktimeUrl, { 
                                  headers: { Authorization: `Bearer ${getToken()}` }
                                });
                                
                                if (worktimeRes.ok) {
                                  const worktimeData = await worktimeRes.json().catch(() => ({}));
                                  let savedMinutes = 0;
                                  
                                  if (Array.isArray(worktimeData)) {
                                    const userStat = worktimeData.find(u => u.username === currentUser.username);
                                    if (userStat && userStat.totalMinutes !== undefined) {
                                      savedMinutes = Number(userStat.totalMinutes) || 0;
                                    }
                                  } else if (worktimeData.totalMinutes !== undefined) {
                                    savedMinutes = Number(worktimeData.totalMinutes) || 0;
                                  }
                                  
                                  offhoursSavedSecondsRef.current = savedMinutes * 60;
                                  setOffhoursTimerSeconds(offhoursSavedSecondsRef.current);
                                } else {
                                  // Используем данные из worktimeMap
                                  const savedMinutes = worktimeMap[currentUser.username] || 0;
                                  offhoursSavedSecondsRef.current = savedMinutes * 60;
                                  setOffhoursTimerSeconds(offhoursSavedSecondsRef.current);
                                }
                              } catch (e) {
                                console.error('Ошибка загрузки данных для нерабочего времени:', e);
                                const savedMinutes = worktimeMap[currentUser.username] || 0;
                                offhoursSavedSecondsRef.current = savedMinutes * 60;
                                setOffhoursTimerSeconds(offhoursSavedSecondsRef.current);
                              }
                            }
                            
                            setOffhoursTimerRunning(true);
                            setOffhoursStartTime(Date.now());
                            if (selectedLeaves.size === 1) {
                              const leaveId = Array.from(selectedLeaves)[0];
                              try {
                                const res = await fetch('/api/activity-session/start', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                  body: JSON.stringify({ leaveId })
                                });
                                const json = await res.json().catch(() => ({}));
                                if (res.ok && json.sessionId) {
                                  activitySessionIdRef.current = json.sessionId;
                                  notifyLocalTriggerStart(json.sessionId, leaveId);
                                  console.log('📸 [activity-session] Started (offhours) leave', leaveId, 'sessionId=', json.sessionId);
                                } else {
                                  console.warn('📸 [activity-session] Start failed (offhours) leave', leaveId, 'status=', res.status, json);
                                }
                              } catch (e) { console.warn('Activity session start failed:', e); }
                            }
                          }}
                          style={{
                            background: 'linear-gradient(135deg, #6dd5ed 0%, #5bc0de 100%)',
                            border: 'none',
                            borderRadius: 12,
                            color: '#ffffff',
                            padding: '12px 24px',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            boxShadow: '0 4px 16px rgba(109, 213, 237, 0.3)'
                          }}
                        >
                          ▶ Начать отработку
                        </button>
                      ) : (
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                          <button
                          onClick={async () => {
                            if (activitySessionIdRef.current || selectedLeaves.size === 1) {
                              try {
                                notifyLocalTriggerStop(activitySessionIdRef.current);
                                await fetch('/api/activity-session/stop', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                  body: JSON.stringify(activitySessionIdRef.current ? { sessionId: activitySessionIdRef.current } : { leaveId: Array.from(selectedLeaves)[0] })
                                });
                              } catch (e) { console.warn('Activity session stop failed:', e); }
                              activitySessionIdRef.current = null;
                            }
                            // Сразу останавливаем лайв-таймер у HR: удаляем из фона и шлём паузу
                            removeTimerFromBackgroundService('offhours');
                            if (socket && currentUser && selectedLeaves.size > 0) {
                              socket.emit('worktime_update', {
                                userId: currentUser.id,
                                username: currentUser.username || currentUser.fio,
                                date: selectedDate,
                                workedMinutes: Math.floor(offhoursTimerSeconds / 60),
                                requiredMinutes: 0,
                                status: 'paused',
                                isRunning: false,
                                selectedLeaves: Array.from(selectedLeaves),
                                workType: 'offhours',
                                currentSeconds: offhoursTimerSeconds
                              });
                            }
                            setOffhoursTimerRunning(false);
                            // Сохраняем отработанное время
                            if (currentUser) {
                              try {
                                const workedMinutes = Math.floor((offhoursTimerSeconds - offhoursSavedSecondsRef.current) / 60);
                                if (workedMinutes > 0) {
                                  const day = selectedDate;
                                  await fetch('/api/worktime/add', {
                                    method: 'POST',
                                    headers: { 
                                      'Content-Type': 'application/json', 
                                      Authorization: `Bearer ${getToken()}` 
                                    },
                                    body: JSON.stringify({ 
                                      date: day,
                                      minutes: workedMinutes,
                                      type: 'offhours'
                                    })
                                  });
                                  offhoursSavedSecondsRef.current = offhoursTimerSeconds;
                                  // Перезагружаем данные
                                  await reloadLeavesData();
                                }
                              } catch (e) {
                                console.error('Ошибка сохранения нерабочего времени:', e);
                              }
                            }
                          }}
                            style={{
                              background: 'rgba(255, 193, 7, 0.2)',
                              border: '1px solid #ffc107',
                              borderRadius: 8,
                              color: '#ffc107',
                              padding: '8px 16px',
                              cursor: 'pointer'
                            }}
                          >
                            ⏸ Пауза
                          </button>
                          <button
                          onClick={() => {
                            if (activitySessionIdRef.current || selectedLeaves.size === 1) {
                              try {
                                notifyLocalTriggerStop(activitySessionIdRef.current);
                                fetch('/api/activity-session/stop', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                  body: JSON.stringify(activitySessionIdRef.current ? { sessionId: activitySessionIdRef.current } : { leaveId: Array.from(selectedLeaves)[0] })
                                }).catch(e => console.warn('Activity session stop failed:', e));
                              } catch (e) { console.warn('Activity session stop failed:', e); }
                              activitySessionIdRef.current = null;
                            }
                            const secondsToEmit = offhoursTimerSeconds;
                            removeTimerFromBackgroundService('offhours');
                            if (socket && currentUser && selectedLeaves.size > 0) {
                              socket.emit('worktime_update', {
                                userId: currentUser.id,
                                username: currentUser.username || currentUser.fio,
                                date: selectedDate,
                                workedMinutes: Math.floor(secondsToEmit / 60),
                                requiredMinutes: 0,
                                status: 'paused',
                                isRunning: false,
                                selectedLeaves: Array.from(selectedLeaves),
                                workType: 'offhours',
                                currentSeconds: secondsToEmit
                              });
                            }
                            setOffhoursTimerSeconds(offhoursSavedSecondsRef.current);
                            setOffhoursTimerRunning(false);
                            setOffhoursStartTime(null);
                          }}
                            style={{
                              background: 'rgba(220, 53, 69, 0.2)',
                              border: '1px solid #dc3545',
                              borderRadius: 8,
                              color: '#dc3545',
                              padding: '8px 16px',
                              cursor: 'pointer'
                            }}
                          >
                            🔄 Сброс
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Таблица для нерабочего времени */}
                  {loading ? <div>Загрузка...</div> : error ? <div>{error}</div> : (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ 
                        fontSize: '0.9rem', 
                        color: '#a0aec0', 
                        marginBottom: 12,
                        padding: '8px 12px',
                        background: 'rgba(109, 213, 237, 0.1)',
                        borderRadius: 8,
                        border: '1px solid rgba(109, 213, 237, 0.2)'
                      }}>
                        📊 <strong>Таблица отработки в нерабочее время</strong> - Выбранная дата: {selectedDate}
                      </div>
                      <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
                        <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                          Показано: <strong style={{ color: '#6dd5ed' }}>{dataForWorkTabs.filter(row => row.workType === 'offhours' || (!row.workType && isOffhoursWorktimeAllowed())).length}</strong> из <strong style={{ color: '#ffe082' }}>{dataForWorkTabs.length}</strong> на отработку
                        </span>
                      </div>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                        background: 'transparent',
                        color: '#e6f7ef',
                        borderRadius: 14,
                        overflow: 'hidden',
                        boxShadow: '0 2px 16px rgba(109, 213, 237, 0.22), 0 0 0 2px rgba(109, 213, 237, 0.22)'
                      }}>
                        <thead>
                          <tr style={{ background: '#1f2630' }}>
                            <th style={{ padding: '12px 14px', textAlign: 'left', color: '#6dd5ed', fontWeight: 800 }}>ФИО</th>
                            <th style={{ padding: '12px 14px', textAlign: 'left', color: '#6dd5ed', fontWeight: 800 }}>Отработано</th>
                            <th style={{ padding: '12px 14px', textAlign: 'left', color: '#6dd5ed', fontWeight: 800 }}>Статус</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allUsers.filter(user => {
                            if (userRole === 'employee' && user.username !== currentUser?.username) return false;
                            if (nameFilter && !(user.username || user.fio || '').toLowerCase().includes(nameFilter.toLowerCase())) return false;
                            return true;
                          }).map((user, index) => {
                            const fio = user.fio || user.username || '';
                            const isCurrentUser = fio === currentUser?.username;
                            const dayIsToday = isSameDay(selectedDate);
                            const isThisUserActive = isCurrentUser && offhoursTimerRunning && dayIsToday;
                            const dataRow = (Array.isArray(data) ? data : []).find(r => (r.username === user.username || r.fio === user.fio || r.fio === user.username || r.username === user.fio));
                            const avatarSrc = (dataRow && dataRow.avatar) || user.avatar || '/api/avatars/default.png';
                            
                            // Получаем отработанное время для этого пользователя на выбранную дату
                            const baseWorkedMinutes = worktimeMap[fio] || 0;
                            // Для активного таймера используем общее время из таймера, иначе базовое время из БД
                            const totalSeconds = isThisUserActive ? offhoursTimerSeconds : (baseWorkedMinutes * 60);
                            
                            const rowKey = `offhours-${user.id || fio}-${index}-${isThisUserActive ? Math.floor(offhoursTimerSeconds) : 0}`;
                            
                            return (
                              <tr key={rowKey} style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid #2a323d' }}>
                                <td style={{ padding: '12px 14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <img
                                    src={avatarSrc}
                                    alt={fio}
                                    style={{ width: 50, height: 50, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(109, 213, 237, 0.3)' }}
                                    onError={(e) => { e.currentTarget.src = '/api/avatars/default.png'; }}
                                  />
                                  <span>{fio}</span>
                                </td>
                                <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>
                                  {isThisUserActive ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      <span style={{ 
                                        fontWeight: 'bold',
                                        color: '#6dd5ed',
                                        fontSize: '1.1rem',
                                        fontFamily: 'monospace',
                                        letterSpacing: '0.05em'
                                      }}>
                                        {formatTimerDisplay(totalSeconds)}
                                      </span>
                                      <div style={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        padding: '2px 6px',
                                        borderRadius: 8,
                                        backgroundColor: 'rgba(109, 213, 237, 0.1)',
                                        border: '1px solid rgba(109, 213, 237, 0.3)',
                                        width: 'fit-content'
                                      }}>
                                        <div style={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          backgroundColor: '#6dd5ed',
                                          animation: 'pulse 1.5s infinite'
                                        }} />
                                        <span style={{ color: '#6dd5ed', fontSize: '0.75rem', fontWeight: 'bold' }}>LIVE</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <span>{formatTimeFromHours(baseWorkedMinutes / 60)}</span>
                                  )}
                                </td>
                                <td style={{ padding: '12px 14px' }}>
                                  <span style={{ 
                                    color: isThisUserActive ? '#6dd5ed' : '#a0aec0',
                                    fontWeight: isThisUserActive ? 'bold' : 'normal'
                                  }}>
                                    {isThisUserActive ? '⏱️ В работе' : '⏸ Остановлен'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* Блок для режима "Отработка по будням" */}
              {workMode === 'weekdays' && (
                <>
                  {/* Таймер для будних дней */}
                  <div style={{
                    background: 'linear-gradient(135deg, #2a3441 0%, #3a4553 100%)',
                    borderRadius: 16,
                    padding: 20,
                    marginBottom: 20,
                    border: '1px solid rgba(255, 193, 7, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                      <div style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: weekdaysTimerRunning ? '#ffc107' : '#6c757d'
                      }} />
                      <span style={{ color: '#ffc107', fontWeight: 700, fontSize: '1.1rem' }}>
                        📅 Отработка по будням
                      </span>
                    </div>
                    <div style={{ textAlign: 'center', marginBottom: 30 }}>
                      <div style={{
                        fontSize: '4rem',
                        fontWeight: 'bold',
                        color: '#ffc107',
                        fontFamily: 'monospace',
                        letterSpacing: '0.1em',
                        textShadow: '0 0 20px rgba(255, 193, 7, 0.5)',
                        transition: 'all 0.3s ease'
                      }}>
                        {formatTimerDisplay(weekdaysTimerSeconds)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, gap: 20 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#a0aec0', fontSize: '0.9rem', marginBottom: 4 }}>Всего отработано</div>
                        <div style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {Math.floor(weekdaysTimerSeconds / 60)} мин
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      {!weekdaysTimerRunning ? (
                        <button
                          onClick={async () => {
                            // Проверяем доступность времени перед запуском
                            if (!isWeekdaysWorktimeAllowed()) {
                              setNotification({
                                type: 'warning',
                                message: '⏰ Отработка по будням доступна только с 6:00 до 23:00',
                                show: true,
                                persistent: false
                              });
                              setTimeout(() => setNotification(null), 4000);
                              return;
                            }
                            
                            // Загружаем уже накопленное время из БД для выбранной даты
                            if (currentUser) {
                              try {
                                const day = selectedDate;
                                const worktimeUrl = `/api/worktime-stats/${day}`;
                                const worktimeRes = await fetch(worktimeUrl, { 
                                  headers: { Authorization: `Bearer ${getToken()}` }
                                });
                                
                                if (worktimeRes.ok) {
                                  const worktimeData = await worktimeRes.json().catch(() => ({}));
                                  let savedMinutes = 0;
                                  
                                  if (Array.isArray(worktimeData)) {
                                    const userStat = worktimeData.find(u => u.username === currentUser.username);
                                    if (userStat && userStat.totalMinutes !== undefined) {
                                      savedMinutes = Number(userStat.totalMinutes) || 0;
                                    }
                                  } else if (worktimeData.totalMinutes !== undefined) {
                                    savedMinutes = Number(worktimeData.totalMinutes) || 0;
                                  }
                                  
                                  weekdaysSavedSecondsRef.current = savedMinutes * 60;
                                  setWeekdaysTimerSeconds(weekdaysSavedSecondsRef.current);
                                } else {
                                  const savedMinutes = worktimeMap[currentUser.username] || 0;
                                  weekdaysSavedSecondsRef.current = savedMinutes * 60;
                                  setWeekdaysTimerSeconds(weekdaysSavedSecondsRef.current);
                                }
                              } catch (e) {
                                console.error('Ошибка загрузки данных для будних дней:', e);
                                const savedMinutes = worktimeMap[currentUser.username] || 0;
                                weekdaysSavedSecondsRef.current = savedMinutes * 60;
                                setWeekdaysTimerSeconds(weekdaysSavedSecondsRef.current);
                              }
                            }
                            
                            setWeekdaysTimerRunning(true);
                            setWeekdaysStartTime(Date.now());
                            if (selectedLeaves.size === 1) {
                              const leaveId = Array.from(selectedLeaves)[0];
                              try {
                                const res = await fetch('/api/activity-session/start', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                  body: JSON.stringify({ leaveId })
                                });
                                const json = await res.json().catch(() => ({}));
                                if (res.ok && json.sessionId) {
                                  activitySessionIdRef.current = json.sessionId;
                                  notifyLocalTriggerStart(json.sessionId, leaveId);
                                  console.log('📸 [activity-session] Started (weekdays) leave', leaveId, 'sessionId=', json.sessionId);
                                } else {
                                  console.warn('📸 [activity-session] Start failed (weekdays) leave', leaveId, 'status=', res.status, json);
                                }
                              } catch (e) { console.warn('Activity session start failed:', e); }
                            }
                          }}
                          style={{
                            background: 'linear-gradient(135deg, #ffc107 0%, #ffb300 100%)',
                            border: 'none',
                            borderRadius: 12,
                            color: '#ffffff',
                            padding: '12px 24px',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            boxShadow: '0 4px 16px rgba(255, 193, 7, 0.3)'
                          }}
                        >
                          ▶ Начать отработку
                        </button>
                      ) : (
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                          <button
                          onClick={async () => {
                            if (activitySessionIdRef.current || selectedLeaves.size === 1) {
                              try {
                                notifyLocalTriggerStop(activitySessionIdRef.current);
                                await fetch('/api/activity-session/stop', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                  body: JSON.stringify(activitySessionIdRef.current ? { sessionId: activitySessionIdRef.current } : { leaveId: Array.from(selectedLeaves)[0] })
                                });
                              } catch (e) { console.warn('Activity session stop failed:', e); }
                              activitySessionIdRef.current = null;
                            }
                            removeTimerFromBackgroundService('weekdays');
                            if (socket && currentUser && selectedLeaves.size > 0) {
                              socket.emit('worktime_update', {
                                userId: currentUser.id,
                                username: currentUser.username || currentUser.fio,
                                date: selectedDate,
                                workedMinutes: Math.floor(weekdaysTimerSeconds / 60),
                                requiredMinutes: 0,
                                status: 'paused',
                                isRunning: false,
                                selectedLeaves: Array.from(selectedLeaves),
                                workType: 'weekdays',
                                currentSeconds: weekdaysTimerSeconds
                              });
                            }
                            setWeekdaysTimerRunning(false);
                            // Сохраняем отработанное время
                            if (currentUser) {
                              try {
                                const workedMinutes = Math.floor((weekdaysTimerSeconds - weekdaysSavedSecondsRef.current) / 60);
                                if (workedMinutes > 0) {
                                  const day = selectedDate;
                                  await fetch('/api/worktime/add', {
                                    method: 'POST',
                                    headers: { 
                                      'Content-Type': 'application/json', 
                                      Authorization: `Bearer ${getToken()}` 
                                    },
                                    body: JSON.stringify({ 
                                      date: day,
                                      minutes: workedMinutes,
                                      type: 'weekdays'
                                    })
                                  });
                                  weekdaysSavedSecondsRef.current = weekdaysTimerSeconds;
                                  await reloadLeavesData();
                                }
                              } catch (e) {
                                console.error('Ошибка сохранения будних дней:', e);
                              }
                            }
                          }}
                            style={{
                              background: 'rgba(255, 193, 7, 0.2)',
                              border: '1px solid #ffc107',
                              borderRadius: 8,
                              color: '#ffc107',
                              padding: '8px 16px',
                              cursor: 'pointer'
                            }}
                          >
                            ⏸ Пауза
                          </button>
                          <button
                          onClick={() => {
                            if (activitySessionIdRef.current || selectedLeaves.size === 1) {
                              try {
                                notifyLocalTriggerStop(activitySessionIdRef.current);
                                fetch('/api/activity-session/stop', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                  body: JSON.stringify(activitySessionIdRef.current ? { sessionId: activitySessionIdRef.current } : { leaveId: Array.from(selectedLeaves)[0] })
                                }).catch(e => console.warn('Activity session stop failed:', e));
                              } catch (e) { console.warn('Activity session stop failed:', e); }
                              activitySessionIdRef.current = null;
                            }
                            const secondsToEmit = weekdaysTimerSeconds;
                            removeTimerFromBackgroundService('weekdays');
                            if (socket && currentUser && selectedLeaves.size > 0) {
                              socket.emit('worktime_update', {
                                userId: currentUser.id,
                                username: currentUser.username || currentUser.fio,
                                date: selectedDate,
                                workedMinutes: Math.floor(secondsToEmit / 60),
                                requiredMinutes: 0,
                                status: 'paused',
                                isRunning: false,
                                selectedLeaves: Array.from(selectedLeaves),
                                workType: 'weekdays',
                                currentSeconds: secondsToEmit
                              });
                            }
                            setWeekdaysTimerSeconds(weekdaysSavedSecondsRef.current);
                            setWeekdaysTimerRunning(false);
                            setWeekdaysStartTime(null);
                          }}
                            style={{
                              background: 'rgba(220, 53, 69, 0.2)',
                              border: '1px solid #dc3545',
                              borderRadius: 8,
                              color: '#dc3545',
                              padding: '8px 16px',
                              cursor: 'pointer'
                            }}
                          >
                            🔄 Сброс
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Таблица для будних дней */}
                  {loading ? <div>Загрузка...</div> : error ? <div>{error}</div> : (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ 
                        fontSize: '0.9rem', 
                        color: '#a0aec0', 
                        marginBottom: 12,
                        padding: '8px 12px',
                        background: 'rgba(255, 193, 7, 0.1)',
                        borderRadius: 8,
                        border: '1px solid rgba(255, 193, 7, 0.2)'
                      }}>
                        📊 <strong>Таблица отработки по будням</strong> - Выбранная дата: {selectedDate}
                      </div>
                      <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
                        <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                          Показано: <strong style={{ color: '#ffc107' }}>{dataForWorkTabs.filter(row => row.workType === 'weekdays' || (!row.workType && isWeekdaysWorktimeAllowed())).length}</strong> из <strong style={{ color: '#ffe082' }}>{dataForWorkTabs.length}</strong> на отработку
                        </span>
                      </div>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                        background: 'transparent',
                        color: '#e6f7ef',
                        borderRadius: 14,
                        overflow: 'hidden',
                        boxShadow: '0 2px 16px rgba(255, 193, 7, 0.22), 0 0 0 2px rgba(255, 193, 7, 0.22)'
                      }}>
                        <thead>
                          <tr style={{ background: '#1f2630' }}>
                            <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffc107', fontWeight: 800 }}>ФИО</th>
                            <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffc107', fontWeight: 800 }}>Отработано</th>
                            <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffc107', fontWeight: 800 }}>Статус</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allUsers.filter(user => {
                            if (userRole === 'employee' && user.username !== currentUser?.username) return false;
                            if (nameFilter && !(user.username || user.fio || '').toLowerCase().includes(nameFilter.toLowerCase())) return false;
                            return true;
                          }).map((user, index) => {
                            const fio = user.fio || user.username || '';
                            const isCurrentUser = fio === currentUser?.username;
                            const dayIsToday = isSameDay(selectedDate);
                            const isThisUserActive = isCurrentUser && weekdaysTimerRunning && dayIsToday;
                            
                            // Получаем отработанное время для этого пользователя на выбранную дату
                            const dataRowW = (Array.isArray(data) ? data : []).find(r => (r.username === user.username || r.fio === user.fio || r.fio === user.username || r.username === user.fio));
                            const avatarSrcW = (dataRowW && dataRowW.avatar) || user.avatar || '/api/avatars/default.png';
                            const baseWorkedMinutes = worktimeMap[fio] || 0;
                            // Для активного таймера используем общее время из таймера, иначе базовое время из БД
                            const totalSeconds = isThisUserActive ? weekdaysTimerSeconds : (baseWorkedMinutes * 60);
                            
                            return (
                              <tr key={`weekdays-${user.id || fio}-${index}-${isThisUserActive ? Math.floor(weekdaysTimerSeconds) : 0}`} style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid #2a323d' }}>
                                <td style={{ padding: '12px 14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <img
                                    src={avatarSrcW}
                                    alt={fio}
                                    style={{ width: 50, height: 50, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(255, 193, 7, 0.3)' }}
                                    onError={(e) => { e.currentTarget.src = '/api/avatars/default.png'; }}
                                  />
                                  <span>{fio}</span>
                                </td>
                                <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>
                                  {isThisUserActive ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      <span style={{ 
                                        fontWeight: 'bold',
                                        color: '#ffc107',
                                        fontSize: '1.1rem',
                                        fontFamily: 'monospace',
                                        letterSpacing: '0.05em'
                                      }}>
                                        {formatTimerDisplay(totalSeconds)}
                                      </span>
                                      <div style={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        padding: '2px 6px',
                                        borderRadius: 8,
                                        backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                        border: '1px solid rgba(255, 193, 7, 0.3)',
                                        width: 'fit-content'
                                      }}>
                                        <div style={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          backgroundColor: '#ffc107',
                                          animation: 'pulse 1.5s infinite'
                                        }} />
                                        <span style={{ color: '#ffc107', fontSize: '0.75rem', fontWeight: 'bold' }}>LIVE</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <span>{formatTimeFromHours(baseWorkedMinutes / 60)}</span>
                                  )}
                                </td>
                                <td style={{ padding: '12px 14px' }}>
                                  <span style={{ 
                                    color: isThisUserActive ? '#ffc107' : '#a0aec0',
                                    fontWeight: isThisUserActive ? 'bold' : 'normal'
                                  }}>
                                    {isThisUserActive ? '⏱️ В работе' : '⏸ Остановлен'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* Блок для режима "Отработка по выходным" */}
              {workMode === 'weekends' && (
                <>
                  {/* Таймер для выходных */}
                  <div style={{
                    background: 'linear-gradient(135deg, #2a3441 0%, #3a4553 100%)',
                    borderRadius: 16,
                    padding: 20,
                    marginBottom: 20,
                    border: '1px solid rgba(67, 233, 123, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                      <div style={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: weekendsTimerRunning ? '#43e97b' : '#6c757d'
                      }} />
                      <span style={{ color: '#43e97b', fontWeight: 700, fontSize: '1.1rem' }}>
                        🎉 Отработка по выходным
                      </span>
                    </div>
                    <div style={{ textAlign: 'center', marginBottom: 30 }}>
                      <div style={{
                        fontSize: '4rem',
                        fontWeight: 'bold',
                        color: '#43e97b',
                        fontFamily: 'monospace',
                        letterSpacing: '0.1em',
                        textShadow: '0 0 20px rgba(67, 233, 123, 0.5)',
                        transition: 'all 0.3s ease'
                      }}>
                        {formatTimerDisplay(weekendsTimerSeconds)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, gap: 20 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: '#a0aec0', fontSize: '0.9rem', marginBottom: 4 }}>Всего отработано</div>
                        <div style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {Math.floor(weekendsTimerSeconds / 60)} мин
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      {!weekendsTimerRunning ? (
                        <button
                          onClick={async () => {
                            // Проверяем доступность времени перед запуском
                            if (!isWeekendsWorktimeAllowed()) {
                              setNotification({
                                type: 'warning',
                                message: '⏰ Отработка по выходным доступна только с 6:00 до 23:00',
                                show: true,
                                persistent: false
                              });
                              setTimeout(() => setNotification(null), 4000);
                              return;
                            }
                            
                            // Загружаем уже накопленное время из БД для выбранной даты
                            if (currentUser) {
                              try {
                                const day = selectedDate;
                                const worktimeUrl = `/api/worktime-stats/${day}`;
                                const worktimeRes = await fetch(worktimeUrl, { 
                                  headers: { Authorization: `Bearer ${getToken()}` }
                                });
                                
                                if (worktimeRes.ok) {
                                  const worktimeData = await worktimeRes.json().catch(() => ({}));
                                  let savedMinutes = 0;
                                  
                                  if (Array.isArray(worktimeData)) {
                                    const userStat = worktimeData.find(u => u.username === currentUser.username);
                                    if (userStat && userStat.totalMinutes !== undefined) {
                                      savedMinutes = Number(userStat.totalMinutes) || 0;
                                    }
                                  } else if (worktimeData.totalMinutes !== undefined) {
                                    savedMinutes = Number(worktimeData.totalMinutes) || 0;
                                  }
                                  
                                  weekendsSavedSecondsRef.current = savedMinutes * 60;
                                  setWeekendsTimerSeconds(weekendsSavedSecondsRef.current);
                                } else {
                                  const savedMinutes = worktimeMap[currentUser.username] || 0;
                                  weekendsSavedSecondsRef.current = savedMinutes * 60;
                                  setWeekendsTimerSeconds(weekendsSavedSecondsRef.current);
                                }
                              } catch (e) {
                                console.error('Ошибка загрузки данных для выходных:', e);
                                const savedMinutes = worktimeMap[currentUser.username] || 0;
                                weekendsSavedSecondsRef.current = savedMinutes * 60;
                                setWeekendsTimerSeconds(weekendsSavedSecondsRef.current);
                              }
                            }
                            
                            setWeekendsTimerRunning(true);
                            setWeekendsStartTime(Date.now());
                            if (selectedLeaves.size === 1) {
                              const leaveId = Array.from(selectedLeaves)[0];
                              try {
                                const res = await fetch('/api/activity-session/start', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                  body: JSON.stringify({ leaveId })
                                });
                                const json = await res.json().catch(() => ({}));
                                if (res.ok && json.sessionId) {
                                  activitySessionIdRef.current = json.sessionId;
                                  notifyLocalTriggerStart(json.sessionId, leaveId);
                                  console.log('📸 [activity-session] Started (weekends) leave', leaveId, 'sessionId=', json.sessionId);
                                } else {
                                  console.warn('📸 [activity-session] Start failed (weekends) leave', leaveId, 'status=', res.status, json);
                                }
                              } catch (e) { console.warn('Activity session start failed:', e); }
                            }
                          }}
                          style={{
                            background: 'linear-gradient(135deg, #43e97b 0%, #38d9a9 100%)',
                            border: 'none',
                            borderRadius: 12,
                            color: '#ffffff',
                            padding: '12px 24px',
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            boxShadow: '0 4px 16px rgba(67, 233, 123, 0.3)'
                          }}
                        >
                          ▶ Начать отработку
                        </button>
                      ) : (
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                          <button
                          onClick={async () => {
                            if (activitySessionIdRef.current || selectedLeaves.size === 1) {
                              try {
                                notifyLocalTriggerStop(activitySessionIdRef.current);
                                await fetch('/api/activity-session/stop', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                  body: JSON.stringify(activitySessionIdRef.current ? { sessionId: activitySessionIdRef.current } : { leaveId: Array.from(selectedLeaves)[0] })
                                });
                              } catch (e) { console.warn('Activity session stop failed:', e); }
                              activitySessionIdRef.current = null;
                            }
                            removeTimerFromBackgroundService('weekends');
                            if (socket && currentUser && selectedLeaves.size > 0) {
                              socket.emit('worktime_update', {
                                userId: currentUser.id,
                                username: currentUser.username || currentUser.fio,
                                date: selectedDate,
                                workedMinutes: Math.floor(weekendsTimerSeconds / 60),
                                requiredMinutes: 0,
                                status: 'paused',
                                isRunning: false,
                                selectedLeaves: Array.from(selectedLeaves),
                                workType: 'weekends',
                                currentSeconds: weekendsTimerSeconds
                              });
                            }
                            setWeekendsTimerRunning(false);
                            // Сохраняем отработанное время
                            if (currentUser) {
                              try {
                                const workedMinutes = Math.floor((weekendsTimerSeconds - weekendsSavedSecondsRef.current) / 60);
                                if (workedMinutes > 0) {
                                  const day = selectedDate;
                                  await fetch('/api/worktime/add', {
                                    method: 'POST',
                                    headers: { 
                                      'Content-Type': 'application/json', 
                                      Authorization: `Bearer ${getToken()}` 
                                    },
                                    body: JSON.stringify({ 
                                      date: day,
                                      minutes: workedMinutes,
                                      type: 'weekends'
                                    })
                                  });
                                  weekendsSavedSecondsRef.current = weekendsTimerSeconds;
                                  await reloadLeavesData();
                                }
                              } catch (e) {
                                console.error('Ошибка сохранения выходных:', e);
                              }
                            }
                          }}
                            style={{
                              background: 'rgba(255, 193, 7, 0.2)',
                              border: '1px solid #ffc107',
                              borderRadius: 8,
                              color: '#ffc107',
                              padding: '8px 16px',
                              cursor: 'pointer'
                            }}
                          >
                            ⏸ Пауза
                          </button>
                          <button
                          onClick={() => {
                            if (activitySessionIdRef.current || selectedLeaves.size === 1) {
                              try {
                                notifyLocalTriggerStop(activitySessionIdRef.current);
                                fetch('/api/activity-session/stop', {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
                                  body: JSON.stringify(activitySessionIdRef.current ? { sessionId: activitySessionIdRef.current } : { leaveId: Array.from(selectedLeaves)[0] })
                                }).catch(e => console.warn('Activity session stop failed:', e));
                              } catch (e) { console.warn('Activity session stop failed:', e); }
                              activitySessionIdRef.current = null;
                            }
                            const secondsToEmit = weekendsTimerSeconds;
                            removeTimerFromBackgroundService('weekends');
                            if (socket && currentUser && selectedLeaves.size > 0) {
                              socket.emit('worktime_update', {
                                userId: currentUser.id,
                                username: currentUser.username || currentUser.fio,
                                date: selectedDate,
                                workedMinutes: Math.floor(secondsToEmit / 60),
                                requiredMinutes: 0,
                                status: 'paused',
                                isRunning: false,
                                selectedLeaves: Array.from(selectedLeaves),
                                workType: 'weekends',
                                currentSeconds: secondsToEmit
                              });
                            }
                            setWeekendsTimerSeconds(weekendsSavedSecondsRef.current);
                            setWeekendsTimerRunning(false);
                            setWeekendsStartTime(null);
                          }}
                            style={{
                              background: 'rgba(220, 53, 69, 0.2)',
                              border: '1px solid #dc3545',
                              borderRadius: 8,
                              color: '#dc3545',
                              padding: '8px 16px',
                              cursor: 'pointer'
                            }}
                          >
                            🔄 Сброс
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Таблица для выходных */}
                  {loading ? <div>Загрузка...</div> : error ? <div>{error}</div> : (
                    <div style={{ marginTop: 20 }}>
                      <div style={{ 
                        fontSize: '0.9rem', 
                        color: '#a0aec0', 
                        marginBottom: 12,
                        padding: '8px 12px',
                        background: 'rgba(67, 233, 123, 0.1)',
                        borderRadius: 8,
                        border: '1px solid rgba(67, 233, 123, 0.2)'
                      }}>
                        📊 <strong>Таблица отработки по выходным</strong> - Выбранная дата: {selectedDate}
                      </div>
                      <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
                        <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                          Показано: <strong style={{ color: '#43e97b' }}>{dataForWorkTabs.filter(row => row.workType === 'weekends' || (!row.workType && isWeekendsWorktimeAllowed())).length}</strong> из <strong style={{ color: '#ffe082' }}>{dataForWorkTabs.length}</strong> на отработку
                        </span>
                      </div>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                        background: 'transparent',
                        color: '#e6f7ef',
                        borderRadius: 14,
                        overflow: 'hidden',
                        boxShadow: '0 2px 16px rgba(67, 233, 123, 0.22), 0 0 0 2px rgba(67, 233, 123, 0.22)'
                      }}>
                        <thead>
                          <tr style={{ background: '#1f2630' }}>
                            <th style={{ padding: '12px 14px', textAlign: 'left', color: '#43e97b', fontWeight: 800 }}>ФИО</th>
                            <th style={{ padding: '12px 14px', textAlign: 'left', color: '#43e97b', fontWeight: 800 }}>Отработано</th>
                            <th style={{ padding: '12px 14px', textAlign: 'left', color: '#43e97b', fontWeight: 800 }}>Статус</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allUsers.filter(user => {
                            if (userRole === 'employee' && user.username !== currentUser?.username) return false;
                            if (nameFilter && !(user.username || user.fio || '').toLowerCase().includes(nameFilter.toLowerCase())) return false;
                            return true;
                          }).map((user, index) => {
                            const fio = user.fio || user.username || '';
                            const isCurrentUser = fio === currentUser?.username;
                            const dayIsToday = isSameDay(selectedDate);
                            const isThisUserActive = isCurrentUser && weekendsTimerRunning && dayIsToday;
                            
                            // Получаем отработанное время для этого пользователя на выбранную дату
                            const baseWorkedMinutes = worktimeMap[fio] || 0;
                            const dataRowWe = (Array.isArray(data) ? data : []).find(r => (r.username === user.username || r.fio === user.fio || r.fio === user.username || r.username === user.fio));
                            const avatarSrcWe = (dataRowWe && dataRowWe.avatar) || user.avatar || '/api/avatars/default.png';
                            // Для активного таймера используем общее время из таймера, иначе базовое время из БД
                            const totalSeconds = isThisUserActive ? weekendsTimerSeconds : (baseWorkedMinutes * 60);
                            
                            return (
                              <tr key={`weekends-${user.id || fio}-${index}-${isThisUserActive ? Math.floor(weekendsTimerSeconds) : 0}`} style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid #2a323d' }}>
                                <td style={{ padding: '12px 14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
                                  <img
                                    src={avatarSrcWe}
                                    alt={fio}
                                    style={{ width: 50, height: 50, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(67, 233, 123, 0.3)' }}
                                    onError={(e) => { e.currentTarget.src = '/api/avatars/default.png'; }}
                                  />
                                  <span>{fio}</span>
                                </td>
                                <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>
                                  {isThisUserActive ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                      <span style={{ 
                                        fontWeight: 'bold',
                                        color: '#43e97b',
                                        fontSize: '1.1rem',
                                        fontFamily: 'monospace',
                                        letterSpacing: '0.05em'
                                      }}>
                                        {formatTimerDisplay(totalSeconds)}
                                      </span>
                                      <div style={{ 
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        padding: '2px 6px',
                                        borderRadius: 8,
                                        backgroundColor: 'rgba(67, 233, 123, 0.1)',
                                        border: '1px solid rgba(67, 233, 123, 0.3)',
                                        width: 'fit-content'
                                      }}>
                                        <div style={{
                                          width: 6,
                                          height: 6,
                                          borderRadius: '50%',
                                          backgroundColor: '#43e97b',
                                          animation: 'pulse 1.5s infinite'
                                        }} />
                                        <span style={{ color: '#43e97b', fontSize: '0.75rem', fontWeight: 'bold' }}>LIVE</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <span>{formatTimeFromHours(baseWorkedMinutes / 60)}</span>
                                  )}
                                </td>
                                <td style={{ padding: '12px 14px' }}>
                                  <span style={{ 
                                    color: isThisUserActive ? '#43e97b' : '#a0aec0',
                                    fontWeight: isThisUserActive ? 'bold' : 'normal'
                                  }}>
                                    {isThisUserActive ? '⏱️ В работе' : '⏸ Остановлен'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          {/* Основная таблица (На очереди / Отработанные) — вертикальный скролл при большом числе записей */}
          {!workMode && (
            <div style={{ background: '#1a202c', borderRadius: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.3)', maxHeight: '70vh', overflowY: 'auto', overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
                background: '#1a202c',
                color: '#e6f7ef',
                borderRadius: 14,
                overflow: 'hidden',
                boxShadow: '0 2px 16px #43e97b22, 0 0 0 2px #2193b022'
              }}>
                <thead>
                  <tr style={{ background: '#1f2630' }}>
                    {filter !== 'completed' && (
                      <th style={{ padding: '12px 14px', textAlign: 'center', color: '#ffe082', fontWeight: 800, width: '50px' }}>
                        <input
                          type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectAllLeaves();
                            } else {
                              clearLeaveSelection();
                            }
                          }}
                          checked={selectedLeaves.size > 0 && selectedLeaves.size === data.filter(row => isRowForCurrentUser(row) && row.status !== 'completed').length}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                    )}
                    <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>ФИО</th>
                    <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Период отгула</th>
                    <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Причина</th>
                    <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Требуется</th>
                    <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Отработано</th>
                    <th style={{ padding: '12px 14px', textAlign: 'center', color: '#ffe082', fontWeight: 800, width: '80px' }}>Тип</th>
                    <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Прогресс (%)</th>
                    {isHROrAdmin && (
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Таймеры</th>
                    )}
                    {isHROrAdmin && (
                      <th style={{ padding: '8px', textAlign: 'center', color: '#ffe082', fontWeight: 800, width: '44px' }} title="Активность за период отработки">👁</th>
                    )}
                    {filter === 'completed' && (
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Дата завершения</th>
                    )}
                    <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Статус</th>
                  </tr>
                </thead>
                <tbody>
                {filteredData.map((row, index) => {
  // Отладочная информация о данных таблицы (только при необходимости)
  // if (index === 0) {
  //   console.log(`📋 Данные таблицы (${filteredData.length} строк):`, {
  //     currentUser: currentUser?.username,
  //     firstRow: row,
  //     allData: filteredData
  //   });
  // }
  
  // Новая логика для отдельных отгулов
  const fio = row.fio || row.username || '';
  const requiredHours = Number(row.requiredHours) || 0;
  const workedHours = Number(row.workedHours) || 0;
  let status = row.status || 'Не начато';
  
  // Используем индивидуальные данные отгула вместо общих данных пользователя
  const isCurrentUser = fio === currentUser?.username;
  
  // Для каждого отгула используем его собственные данные
  const individualWorkedHours = Number(row.workedHours) || 0;
  const individualWorkedMinutes = Math.round(individualWorkedHours * 60);
  
  // Если это текущий пользователь и сегодняшний день, добавляем время от активного таймера
  const dayIsToday = isSameDay(selectedDate);
  let extraMinutes = 0;
  let isActivelyWorking = false;
  const leaveId = row.id || row.leaveId;
  
  // Получаем данные реального времени для всех пользователей (нужно для HR столбца)
  const realtimeData = realtimeUpdates[fio];
  
  if (dayIsToday) {
    if (isCurrentUser) {
      // Добавляем время от текущего таймера только если этот отгул выбран
      if (selectedLeaves.has(leaveId) && isTimerRunning) {
        // timerSeconds содержит общее время: уже сохраненное из БД + новое время
        // Для отображения в колонке "Отработано" используем timerSeconds напрямую
        // Для расчета прогресса используем общее время в минутах
        const totalWorkedSeconds = Math.floor(timerSeconds);
        extraMinutes = totalWorkedSeconds / 60; // Общее время в минутах
        isActivelyWorking = true;
      }
    } else if ((userRole === 'hr' || userRole === 'admin')) {
      // HR видит реальные данные от других пользователей через WebSocket
      if (realtimeData && realtimeData.isRunning) {
        // Проверяем, работает ли пользователь над этим конкретным отгулом
        if (realtimeData.selectedLeaves && realtimeData.selectedLeaves.includes(leaveId)) {
          // Используем currentSeconds если доступно, иначе workedMinutes
          if (realtimeData.currentSeconds !== undefined) {
            // currentSeconds уже содержит общее время в секундах
            extraMinutes = realtimeData.currentSeconds / 60;
          } else {
            extraMinutes = realtimeData.workedMinutes || 0;
          }
          isActivelyWorking = true;
        }
      }
    }
  }
  
  // Определяем, активен ли таймер для этого отгула
  const isThisLeaveActive = isCurrentUser && isTimerRunning && selectedLeaves.has(leaveId) && dayIsToday;
  
  // Используем точное значение для расчета прогресса
  // Для активного таймера используем общее время из timerSeconds (уже сохраненное + новое)
  // Для неактивного таймера используем individualWorkedMinutes из БД
  let totalWorkedMinutes;
  if (isThisLeaveActive) {
    // Для активного таймера текущего пользователя используем общее время напрямую из timerSeconds
    // timerSeconds содержит уже сохраненное время + новое время
    totalWorkedMinutes = timerSeconds / 60;
  } else if (isActivelyWorking && realtimeData && realtimeData.currentSeconds !== undefined) {
    // Для HR/Admin используем общее время из WebSocket
    totalWorkedMinutes = realtimeData.currentSeconds / 60;
  } else {
    // Для неактивного таймера используем данные из БД
    totalWorkedMinutes = individualWorkedMinutes + extraMinutes;
  }
  const requiredMinutes = Math.round(requiredHours * 60);
  // Ограничиваем отображаемое "Отработано" требуемым (для HR и сотрудника), чтобы не показывать 10 при требуемых 5
  const displayWorkedMinutes = requiredMinutes > 0 ? Math.min(totalWorkedMinutes, requiredMinutes) : totalWorkedMinutes;
  const progressPercent = requiredMinutes > 0 ? Math.min(100, Math.round((displayWorkedMinutes / requiredMinutes) * 100)) : 0;
  
  // Обновляем статус в зависимости от активности таймера и прогресса
  if (isActivelyWorking) {
    status = '⏱️ В работе';
  } else if (progressPercent >= 100) {
    status = row.status === 'completed' ? '✅ Отработано' : '🔍 На проверке';
  } else if (displayWorkedMinutes > 0) {
    status = '📊 В процессе';
  } else {
    status = '❌ Не начато';
  }
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('ru-RU');
    } catch {
      return dateStr;
    }
  };
  
  const leavePeriod = row.leaveStartDate === row.leaveEndDate 
    ? formatDate(row.leaveStartDate)
    : `${formatDate(row.leaveStartDate)} - ${formatDate(row.leaveEndDate)}`;
  const isCurrentUserRow = isRowForCurrentUser(row);
  
  // Отладочная информация для диагностики чекбоксов (только при необходимости)
  // console.log(`🔍 Строка таблицы:`, {
  //   fio,
  //   rowUsername: row.username,
  //   rowFio: row.fio,
  //   isCurrentUserRow,
  //   currentUserName: currentUser?.username,
  //   rowStatus: row.status,
  //   shouldShowCheckbox: isCurrentUserRow && row.status !== 'completed',
  //   rowId: row.id || row.leaveId
  // });
  
  // Добавляем timerSeconds в ключ для принудительного обновления при изменении таймера
  // Это гарантирует, что строка обновляется в реальном времени при работе таймера
  // КРИТИЧЕСКИ ВАЖНО: rowKey должен меняться каждую секунду, чтобы React перерисовывал строку
  // Используем Math.floor(timerSeconds) для активного таймера - это обновляется каждую секунду
  const activeTimerKey = isThisLeaveActive ? Math.floor(timerSeconds) : 0;
  const otherUserTimerKey = (realtimeData && realtimeData.currentSeconds) ? Math.floor(realtimeData.currentSeconds) : 0;
  const rowKey = `${leaveId || index}-${activeTimerKey}-${otherUserTimerKey}`;
  
  return (
    <tr key={rowKey} style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid #2a323d' }}>
      {/* Колонка с чекбоксом - показываем только если это не фильтр "completed" */}
      {filter !== 'completed' && (
        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
          {/* Показываем чекбокс только для неотработанных отгулов */}
          {isCurrentUserRow && row.status !== 'completed' && (
            <input
              type="checkbox"
              checked={selectedLeaves.has(row.id || row.leaveId)}
              onChange={() => toggleLeaveSelection(row.id || row.leaveId)}
              style={{ 
                cursor: 'pointer',
                transform: 'scale(1.2)',
                accentColor: '#43e97b'
              }}
              title="Выберите этот отгул для изолированной отработки"
            />
          )}
          {/* Показываем галочку для отработанных отгулов (не в фильтре completed) */}
          {isCurrentUserRow && row.status === 'completed' && (
            <span style={{ color: '#28a745', fontSize: '1.2rem' }} title="Отгул полностью отработан">
              ✅
            </span>
          )}
        </td>
      )}
      <td style={{ padding: '12px 14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
        <img
          src={row.avatar || '/api/avatars/default.png'}
          alt={fio}
          style={{ width: 50, height: 50, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(67,233,123,0.3)' }}
          onError={(e) => { e.currentTarget.src = '/api/avatars/default.png'; }}
        />
        <span>{fio}</span>
      </td>
      <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>{leavePeriod}</td>
      <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>{row.leaveReason || 'Отгул'}</td>
      <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>{formatTimeFromHours(requiredHours)}</td>
      <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {(() => {
            // ПРОСТАЯ ЛОГИКА: если таймер активен и этот отгул выбран - показываем timerSeconds (как в верхнем счетчике)
            // Это тот же счетчик, который работает вверху, просто дублируем его здесь
            // Используем timerSeconds напрямую - он обновляется каждую секунду через useEffect
            if (isThisLeaveActive) {
              // Прямое использование timerSeconds - тот же источник, что и в верхнем блоке (строка 3142)
              // timerSeconds обновляется каждую секунду, поэтому значение будет в реальном времени
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ 
                    fontWeight: 'bold',
                    color: '#43e97b',
                    fontSize: '1.1rem',
                    fontFamily: 'monospace',
                    letterSpacing: '0.05em'
                  }}>
                    {formatTimerDisplay(timerSeconds)}
                  </span>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 6px',
                    borderRadius: 8,
                    backgroundColor: 'rgba(67, 233, 123, 0.1)',
                    border: '1px solid rgba(67, 233, 123, 0.3)',
                    width: 'fit-content'
                  }}>
                    <div style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: '#43e97b',
                      animation: 'pulse 1.5s infinite'
                    }} />
                    <span style={{ 
                      color: '#43e97b', 
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      LIVE
                    </span>
                  </div>
                </div>
              );
            }
            
            // Для HR/Admin - показываем данные из WebSocket
            const isOtherUserActive = realtimeData && realtimeData.isRunning && realtimeData.selectedLeaves && realtimeData.selectedLeaves.includes(leaveId);
            if (isOtherUserActive && realtimeData && realtimeData.currentSeconds !== undefined) {
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ 
                    fontWeight: 'bold',
                    color: '#43e97b',
                    fontSize: '1.1rem',
                    fontFamily: 'monospace',
                    letterSpacing: '0.05em'
                  }}>
                    {formatTimerDisplay(Math.floor(realtimeData.currentSeconds))}
                  </span>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 6px',
                    borderRadius: 8,
                    backgroundColor: 'rgba(67, 233, 123, 0.1)',
                    border: '1px solid rgba(67, 233, 123, 0.3)',
                    width: 'fit-content'
                  }}>
                    <div style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: '#43e97b',
                      animation: 'pulse 1.5s infinite'
                    }} />
                    <span style={{ 
                      color: '#43e97b', 
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      LIVE
                    </span>
                  </div>
                </div>
              );
            }
            
            // Если таймер не активен, показываем статическое значение (ограничено требуемым, чтобы HR видел 5, а не 10)
            return <span>{formatTimeFromHours(displayWorkedMinutes / 60)}</span>;
          })()}
        </div>
      </td>
      {/* Колонка с типом отработки */}
      <td style={{ padding: '12px 14px', textAlign: 'center' }}>
        {(() => {
          // Определяем тип отработки
          let workType = null;
          
          // ПРИОРИТЕТ 1: Используем тип из данных (если есть) - это самый точный источник
          if (row.workType) {
            workType = row.workType;
          }
          // ПРИОРИТЕТ 2: Если есть активный таймер и это текущий пользователь - используем текущий режим
          else if (isThisLeaveActive && workMode) {
            if (workMode === 'offhours') workType = 'offhours';
            else if (workMode === 'weekdays') workType = 'weekdays';
            else if (workMode === 'weekends') workType = 'weekends';
          }
          // ПРИОРИТЕТ 3: Проверяем активные таймеры других типов отработки для текущего пользователя
          else if (isCurrentUser && dayIsToday) {
            // Проверяем активные таймеры для других режимов отработки
            if (offhoursTimerRunning) workType = 'offhours';
            else if (weekdaysTimerRunning) workType = 'weekdays';
            else if (weekendsTimerRunning) workType = 'weekends';
          }
          // ПРИОРИТЕТ 4: Для отработанных отгулов определяем тип по дате завершения
          else if (row.status === 'completed' && row.completedAt) {
            try {
              const completedDate = new Date(row.completedAt);
              const dayOfWeek = completedDate.getDay(); // 0 = воскресенье, 6 = суббота
              
              // Если выходной день (суббота или воскресенье)
              if (dayOfWeek === 0 || dayOfWeek === 6) {
                workType = 'weekends';
              } else {
                // Будний день - определяем по времени завершения
                const hours = completedDate.getHours();
                const minutes = completedDate.getMinutes();
                const totalMinutes = hours * 60 + minutes;
                
                // Проверяем, было ли это в нерабочее время (6:00-8:59 или 18:01-23:00)
                const period1Start = 6 * 60;
                const period1End = 8 * 60 + 59;
                const period2Start = 18 * 60 + 1;
                const period2End = 23 * 60;
                
                if ((totalMinutes >= period1Start && totalMinutes <= period1End) || 
                    (totalMinutes >= period2Start && totalMinutes <= period2End)) {
                  workType = 'offhours';
                } else {
                  workType = 'weekdays';
                }
              }
            } catch (e) {
              // Если не удалось определить, используем тип по умолчанию
            }
          }
          // ПРИОРИТЕТ 4: Если тип не определен, пытаемся определить по дате отгула
          else if (!workType && row.leaveStartDate) {
            try {
              const leaveDate = new Date(row.leaveStartDate);
              const dayOfWeek = leaveDate.getDay();
              if (dayOfWeek === 0 || dayOfWeek === 6) {
                workType = 'weekends';
              } else {
                workType = 'weekdays';
              }
            } catch (e) {
              // Используем иконку отработки отгула по умолчанию
            }
          }
          
          // Отображаем иконку в зависимости от типа
          if (workType === 'offhours') {
            return (
              <span style={{ fontSize: '1.5rem' }} title="Отработка в нерабочее время (6:00-8:59, 18:01-23:00)">
                🌙
              </span>
            );
          } else if (workType === 'weekdays') {
            return (
              <span style={{ fontSize: '1.5rem' }} title="Отработка по будням (6:00-23:00)">
                📅
              </span>
            );
          } else if (workType === 'weekends') {
            return (
              <span style={{ fontSize: '1.5rem' }} title="Отработка по выходным (6:00-23:00)">
                🎉
              </span>
            );
          } else {
            // Иконка по умолчанию для отработки отгула (когда тип не определен)
            return (
              <span style={{ fontSize: '1.5rem', opacity: 0.5 }} title="Отработка отгула">
                ⏱️
              </span>
            );
          }
        })()}
      </td>
      <td style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ 
            color: isActivelyWorking ? '#43e97b' : 'inherit',
            fontWeight: isActivelyWorking ? 'bold' : 'normal',
            minWidth: '40px'
          }}>
            {progressPercent}%
          </span>
          <div style={{
            width: '60px',
            height: '6px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progressPercent}%`,
              height: '100%',
              backgroundColor: progressPercent >= 100 ? '#43e97b' : 
                             progressPercent >= 75 ? '#ffd93d' : 
                             progressPercent >= 50 ? '#ff9800' : '#ff6b6b',
              borderRadius: '3px',
              transition: 'width 0.3s ease',
              animation: isActivelyWorking ? 'glow 2s infinite' : 'none'
            }} />
          </div>
          {isActivelyWorking && progressPercent < 100 && (
            <span style={{ fontSize: '0.8rem' }}>📈</span>
          )}
        </div>
      </td>
      {isHROrAdmin && (
        <td style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem' }}>
            {isActivelyWorking ? (
              <div style={{
                color: '#43e97b',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}>
                <div style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: '#43e97b',
                  animation: 'pulse 1.5s infinite'
                }} />
                Активен
              </div>
            ) : (
              <span style={{ color: '#666' }}>Остановлен</span>
            )}
            {realtimeData && realtimeData.currentSeconds && (
              <div style={{ color: '#a3ffd1', fontSize: '0.8rem' }}>
                {formatTimerDisplay(realtimeData.currentSeconds)}
              </div>
            )}
          </div>
        </td>
      )}
      {isHROrAdmin && (
        <td style={{ padding: '8px', textAlign: 'center' }}>
          <button
            type="button"
            onClick={async () => {
              const lid = row.id || row.leaveId;
              if (!lid) return;
              setActivityModalLeaveId(lid);
              setActivityModalData(null);
              setActivityModalLoading(true);
              try {
                const res = await fetch(`/api/activity-session/by-leave/${lid}`, {
                  headers: { Authorization: `Bearer ${getToken()}` }
                });
                const json = await res.json().catch(() => ({}));
                if (res.ok && json.success) {
                  setActivityModalData(json);
                  if (json.urls?.length > 0) setActivityModalTab('sites');
                  else if (json.screenshots?.length > 0) setActivityModalTab('screenshots');
                  else if (json.applications?.length > 0) setActivityModalTab('applications');
                } else {
                  setActivityModalData({ session: null, urls: [], applications: [], screenshots: [], user: { fio: row.fio || row.username } });
                }
              } catch (e) {
                setActivityModalData({ session: null, urls: [], applications: [], screenshots: [], user: { fio: row.fio || row.username } });
              } finally {
                setActivityModalLoading(false);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 6,
              borderRadius: 8,
              fontSize: '1.2rem',
              opacity: 0.9
            }}
            title="Активность за период отработки"
          >
            👁
          </button>
        </td>
      )}
      <td style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            color: status.includes('✅') ? '#43e97b' :
                   status.includes('⏱️') ? '#00bcd4' : 
                   status.includes('📊') ? '#ffd93d' : 
                   status.includes('🔍') ? '#ff9800' : '#ff6b6b',
            fontWeight: status.includes('⏱️') ? 'bold' : 'normal',
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}>
            {filter === 'completed' ? (
              row.status === 'completed' && row.completedAt ? 
                new Date(row.completedAt).toLocaleDateString('ru-RU') + ' ' + 
                new Date(row.completedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                : (selectedDate ? new Date(selectedDate).toLocaleDateString('ru-RU') : 'Сегодня')
            ) : status}
            {isActivelyWorking && (
              <div style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#43e97b',
                animation: 'pulse 2s infinite',
                marginLeft: 4
              }} />
            )}
          </span>
        </div>
        {/* Кнопка подтверждения для HR/Admin: меняет статус отгула на «Отработано» */}
        {(userRole === 'hr' || userRole === 'admin') && (status.includes('На проверке') || row.workStatus === 'На проверке') && (
          <button
            onClick={async () => {
              const id = row.id || row.leaveId;
              if (!id) return;
              try {
                await updateLeaveStatus(id, 'completed');
                await reloadLeavesData();
                setNotification({
                  type: 'success',
                  message: `✅ Отработка отгула подтверждена для ${fio}. Статус: Отработано.`,
                  show: true,
                  persistent: false
                });
                setTimeout(() => setNotification(null), 2500);
              } catch (e) {
                console.error('Ошибка подтверждения:', e);
                setNotification({
                  type: 'error',
                  message: e?.message || 'Ошибка при подтверждении отработки',
                  show: true,
                  persistent: false
                });
                setTimeout(() => setNotification(null), 2500);
              }
            }}
            style={{
              marginLeft: 10,
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid #43e97b',
              background: 'rgba(67, 233, 123, 0.15)',
              color: '#43e97b',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '0.85rem'
            }}
            title="Подтвердить отработку отгула (статус станет «Отработано»)"
          >
            Подтвердить
          </button>
        )}
      </td>
        
      {/* Колонка даты завершения для отработанных отгулов */}
      {filter === 'completed' && (
        <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>
            <span style={{ 
              color: status.includes('✅') ? '#43e97b' : 
                     status.includes('⏱️') ? '#00bcd4' : 
                     status.includes('📊') ? '#ffd93d' : 
                     status.includes('🔍') ? '#ff9800' : '#ff6b6b',
              fontWeight: status.includes('⏱️') ? 'bold' : 'normal',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              {status}
              {isActivelyWorking && (
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#43e97b',
                  animation: 'pulse 2s infinite',
                  marginLeft: 4
                }} />
              )}
            </span>
        </td>
      )}
    </tr>
  );
})}
                </tbody>
              </table>
            </div>
          )}

          {/* БЛОКИ ТАЙМЕРОВ ДЛЯ ТРЕХ РЕЖИМОВ РАБОТЫ */}
          
          {/* Блок для режима "Отработка в нерабочее время" */}
          {workMode === 'offhours' && currentUser && (
            <div style={{
              background: 'linear-gradient(135deg, #2a3441 0%, #3a4553 100%)',
              borderRadius: 16,
              padding: 20,
              marginBottom: 20,
              border: '1px solid rgba(109, 213, 237, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: offhoursTimerRunning ? '#6dd5ed' : '#6c757d'
                  }} />
                  <span style={{ color: '#6dd5ed', fontWeight: 700, fontSize: '1.1rem' }}>
                    🌙 Отработка в нерабочее время
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={() => {
                      const newState = !offhoursIsMinimized;
                      setOffhoursIsMinimized(newState);
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 6,
                      color: '#ffffff',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                    title={offhoursIsMinimized ? 'Развернуть таймер' : 'Свернуть таймер'}
                  >
                    {offhoursIsMinimized ? '🔼' : '🔽'}
                  </button>
                </div>
              </div>

              {!offhoursIsMinimized && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 20,
                  padding: '20px 0'
                }}>
                  <div style={{
                    fontSize: '3.5rem',
                    fontWeight: 'bold',
                    color: '#6dd5ed',
                    fontFamily: 'monospace',
                    textShadow: '0 0 20px rgba(109, 213, 237, 0.5)'
                  }}>
                    {formatTimerDisplay(offhoursTimerSeconds)}
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                    {/* Проверка дня недели и времени - показываем сообщение, если недоступно */}
                    {!isOffhoursWorktimeAllowed() && !workTimeCheckDisabled && (
                      <div style={{
                        padding: '12px 20px',
                        borderRadius: 12,
                        background: 'rgba(255, 152, 0, 0.2)',
                        border: '1px solid rgba(255, 152, 0, 0.5)',
                        color: '#ff9800',
                        fontSize: '0.9rem',
                        textAlign: 'center',
                        maxWidth: '400px'
                      }}>
                        {!isTodayWeekday() ? (
                          '⚠️ Отработка в нерабочее время доступна только по будням (понедельник-пятница)'
                        ) : (
                          '⚠️ Отработка в нерабочее время доступна только с 6:00 до 8:59 и с 18:01 до 23:00'
                        )}
                      </div>
                    )}
                    
                    {(() => {
                      const _lid = selectedLeaves.size > 0 ? Array.from(selectedLeaves)[0] : null;
                      const _row = _lid ? data.find(r => (r.id || r.leaveId) === _lid) : null;
                      const _req = _row ? Math.round((Number(_row.requiredHours) || Number(_row.leaveHours) || 0) * 60) : 0;
                      const _worked = _row ? Math.round((Number(_row.workedHours) || 0) * 60) : 0;
                      const _goal = _req > 0 && (_worked >= _req || (offhoursTimerRunning && offhoursTimerSeconds >= _req * 60));
                      return !offhoursTimerRunning ? (_goal ? null : (
                      <button
                        onClick={async () => {
                          // Проверяем день недели и время перед запуском
                          if (!isOffhoursWorktimeAllowed() && !workTimeCheckDisabled) {
                            const now = new Date();
                            const dayOfWeek = now.getDay();
                            const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
                            
                            let errorMessage = '';
                            if (!isWeekday) {
                              errorMessage = 'Отработка в нерабочее время доступна только по будням (понедельник-пятница)';
                            } else {
                              errorMessage = 'Отработка в нерабочее время доступна только с 6:00 до 8:59 и с 18:01 до 23:00';
                            }
                            
                            setNotification({
                              type: 'error',
                              message: errorMessage,
                              show: true,
                              persistent: false
                            });
                            setTimeout(() => setNotification(null), 4000);
                            return;
                          }
                          
                          if (selectedLeaves.size === 0) {
                            setNotification({
                              type: 'error',
                              message: 'Выберите отгул для отработки',
                              show: true,
                              persistent: false
                            });
                            setTimeout(() => setNotification(null), 2500);
                            return;
                          }
                          // Синхронизируемся с данными из БД перед запуском
                          const leaveId = Array.from(selectedLeaves)[0];
                          if (leaveId) {
                            const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
                            if (selectedLeave) {
                              const alreadyWorkedHours = Number(selectedLeave.workedHours) || 0;
                              const alreadyWorkedMinutes = Math.round(alreadyWorkedHours * 60);
                              const alreadyWorkedSeconds = alreadyWorkedMinutes * 60;
                              // Если таймер уже был запущен, используем сохраненное время
                              if (offhoursTimerSeconds > 0) {
                                offhoursSavedSecondsRef.current = offhoursTimerSeconds;
                              } else {
                                offhoursSavedSecondsRef.current = alreadyWorkedSeconds;
                                setOffhoursTimerSeconds(alreadyWorkedSeconds);
                              }
                            }
                          }
                          setOffhoursTimerRunning(true);
                          setOffhoursStartTime(Date.now());
                          
                          // Отправляем событие о запуске таймера сразу для HR/Admin
                          if (socket && currentUser && selectedLeaves.size > 0) {
                            const leaveId = Array.from(selectedLeaves)[0];
                            const username = currentUser.username || currentUser.fio;
                            const currentSeconds = offhoursTimerSeconds || 0;
                            socket.emit('worktime_update', {
                              userId: currentUser.id,
                              username: username,
                              date: selectedDate,
                              workedMinutes: Math.floor(currentSeconds / 60),
                              requiredMinutes: 0,
                              status: 'running',
                              isRunning: true,
                              selectedLeaves: Array.from(selectedLeaves),
                              workType: 'offhours',
                              currentSeconds: currentSeconds
                            });
                            console.log('📤 [CLIENT Offhours] Sent start event:', {
                              username,
                              isRunning: true,
                              currentSeconds,
                              selectedLeaves: Array.from(selectedLeaves)
                            });
                          }
                        }}
                        disabled={!isOffhoursWorktimeAllowed() && !workTimeCheckDisabled}
                        style={{
                          padding: '12px 24px',
                          borderRadius: 12,
                          border: '2px solid #6dd5ed',
                          background: (!isOffhoursWorktimeAllowed() && !workTimeCheckDisabled) ? 'rgba(109, 213, 237, 0.1)' : 'rgba(109, 213, 237, 0.2)',
                          color: (!isOffhoursWorktimeAllowed() && !workTimeCheckDisabled) ? '#6c757d' : '#6dd5ed',
                          cursor: (!isOffhoursWorktimeAllowed() && !workTimeCheckDisabled) ? 'not-allowed' : 'pointer',
                          fontSize: '1rem',
                          fontWeight: 700,
                          opacity: (!isOffhoursWorktimeAllowed() && !workTimeCheckDisabled) ? 0.5 : 1
                        }}
                      >
                        {offhoursTimerSeconds > 0 ? '▶️ Продолжить' : '▶️ Начать'}
                      </button>
                        )) : (
                      <button
                        onClick={async () => {
                          const currentExactSeconds = offhoursTimerSeconds;
                          setOffhoursTimerRunning(false);
                          if (currentExactSeconds >= 60) {
                            const leaveId = Array.from(selectedLeaves)[0];
                            if (leaveId) {
                              const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
                              const requiredMinutes = selectedLeave ? Math.round((Number(selectedLeave.requiredHours) || Number(selectedLeave.leaveHours) || 0) * 60) : 0;
                              const alreadyWorkedMinutes = selectedLeave ? Math.round((Number(selectedLeave.workedHours) || 0) * 60) : 0;
                              const requiredSeconds = requiredMinutes * 60;
                              const newSeconds = currentExactSeconds - offhoursSavedSecondsRef.current;
                              let newWorkedMinutes = Math.floor(newSeconds / 60);
                              if (requiredSeconds > 0 && newSeconds >= requiredSeconds - 1) {
                                newWorkedMinutes = requiredMinutes;
                              }
                              const minutesToSave = Math.min(newWorkedMinutes, Math.max(0, requiredMinutes - alreadyWorkedMinutes));
                              if (minutesToSave > 0) {
                                await saveWorktimeDataForSpecificLeave(leaveId, minutesToSave, 'offhours');
                                offhoursSavedSecondsRef.current = currentExactSeconds;
                                setOffhoursTimerSeconds(currentExactSeconds);
                                if (requiredMinutes > 0 && (alreadyWorkedMinutes + minutesToSave) >= requiredMinutes) {
                                  await updateLeaveStatus(leaveId, 'completed');
                                  await reloadLeavesData();
                                  setFilter('completed');
                                }
                              }
                            }
                          } else {
                            offhoursSavedSecondsRef.current = currentExactSeconds;
                            setOffhoursTimerSeconds(currentExactSeconds);
                          }
                        }}
                        style={{
                          padding: '12px 24px',
                          borderRadius: 12,
                          border: '2px solid #ffc107',
                          background: 'rgba(255, 193, 7, 0.2)',
                          color: '#ffc107',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: 700
                        }}
                      >
                        ⏸️ Пауза
                      </button>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Таблица отработки в нерабочее время — такой же вид, как во вкладке «На очереди» */}
              <div style={{ marginTop: 20, marginBottom: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
                <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                  Показано: <strong style={{ color: '#6dd5ed' }}>{dataForWorkTabs.filter(row => row.workType === 'offhours' || (!row.workType && isOffhoursWorktimeAllowed())).length}</strong> из <strong style={{ color: '#ffe082' }}>{dataForWorkTabs.length}</strong> на отработку
                  {offhoursFilter && <span style={{ marginLeft: 8, color: '#6dd5ed' }}>(фильтр: {offhoursFilter === 'completed' ? 'Отработанные' : 'На очереди'})</span>}
                </span>
              </div>
              <div style={{ background: '#1a202c', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.3)' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  background: '#1a202c',
                  color: '#e6f7ef',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 2px 16px #6dd5ed22, 0 0 0 2px #2193b022'
                }}>
                  <thead>
                    <tr style={{ background: '#1f2630' }}>
                      {offhoursFilter !== 'completed' && (
                        <th style={{ padding: '12px 14px', textAlign: 'center', color: '#ffe082', fontWeight: 800, width: '50px' }}>
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) { selectAllLeaves(); } else { clearLeaveSelection(); }
                            }}
                            checked={selectedLeaves.size > 0 && selectedLeaves.size === data.filter(row => isRowForCurrentUser(row) && row.status !== 'completed').length}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                      )}
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>ФИО</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Период отгула</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Причина</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Требуется</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Отработано</th>
                      <th style={{ padding: '12px 14px', textAlign: 'center', color: '#ffe082', fontWeight: 800, width: '80px' }}>Тип</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Прогресс (%)</th>
                      {(userRole === 'hr' || userRole === 'admin') && (
                        <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Таймеры</th>
                      )}
                      {offhoursFilter === 'completed' && (
                        <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Дата завершения</th>
                      )}
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataForWorkTabs.filter(row => {
                      // Только тип 'offhours' (или без типа в нерабочее время)
                      const workType = row.workType;
                      return workType === 'offhours' || (!workType && isOffhoursWorktimeAllowed());
                    }).map((row, index) => {
                      const leaveId = row.id || row.leaveId;
                      const isCurrentUserRow = isRowForCurrentUser(row);
                      // Для текущего пользователя: проверяем, активен ли его таймер
                      // Для HR/Admin: проверяем realtimeUpdates для других пользователей
                      const fio = row.username || row.fio || '';
                      // Нормализуем username для поиска (убираем лишние пробелы)
                      const normalizedFio = fio.replace(/\s+/g, ' ').trim();
                      const normalizedRowUsername = row.username ? row.username.replace(/\s+/g, ' ').trim() : '';
                      const normalizedRowFio = row.fio ? row.fio.replace(/\s+/g, ' ').trim() : '';
                      
                      // Пробуем найти realtimeData по username или fio - проверяем все возможные варианты (включая нормализованные)
                      let realtimeData = null;
                      // Сначала пробуем точное совпадение
                      if (fio && realtimeUpdates[fio]) {
                        realtimeData = realtimeUpdates[fio];
                      } else if (row.username && realtimeUpdates[row.username]) {
                        realtimeData = realtimeUpdates[row.username];
                      } else if (row.fio && realtimeUpdates[row.fio]) {
                        realtimeData = realtimeUpdates[row.fio];
                      }
                      // Затем пробуем нормализованные версии
                      if (!realtimeData) {
                        for (const key in realtimeUpdates) {
                          const normalizedKey = key.replace(/\s+/g, ' ').trim();
                          if (normalizedKey === normalizedFio || normalizedKey === normalizedRowUsername || normalizedKey === normalizedRowFio) {
                            realtimeData = realtimeUpdates[key];
                            break;
                          }
                        }
                      }
                      
                      // Упрощенная проверка для HR/Admin: если есть realtimeData с isRunning=true и workType='offhours', показываем лайв таймер
                      // Для HR/Admin: показываем лайв таймер, если есть realtimeData с isRunning=true и workType совпадает
                      // Проверяем, что selectedLeaves содержит leaveId (с учетом преобразования типов)
                      const isThisLeaveActive = isCurrentUserRow 
                        ? (selectedLeaves.has(leaveId) && offhoursTimerRunning)
                        : ((userRole === 'hr' || userRole === 'admin') && realtimeData && realtimeData.isRunning === true && realtimeData.workType === 'offhours' && realtimeData.selectedLeaves && Array.isArray(realtimeData.selectedLeaves) && realtimeData.selectedLeaves.length > 0 && realtimeData.selectedLeaves.some(id => Number(id) === Number(leaveId)));
                      
                      // Логирование для отладки (только для HR/Admin, раз в 5 секунд)
                      if ((userRole === 'hr' || userRole === 'admin') && !isCurrentUserRow) {
                        const shouldLog = Math.floor(Date.now() / 1000) % 5 === 0;
                        if (shouldLog || realtimeData) {
                          console.log(`🔍 [Table Offhours] Row for ${fio}:`, {
                            fio,
                            normalizedFio,
                            rowUsername: row.username,
                            normalizedRowUsername,
                            rowFio: row.fio,
                            normalizedRowFio,
                            leaveId,
                            leaveIdType: typeof leaveId,
                            isThisLeaveActive,
                            userRole,
                            isCurrentUserRow,
                            realtimeData: realtimeData ? {
                              isRunning: realtimeData.isRunning,
                              workType: realtimeData.workType,
                              currentSeconds: realtimeData.currentSeconds,
                              selectedLeaves: realtimeData.selectedLeaves,
                              selectedLeavesTypes: realtimeData.selectedLeaves ? realtimeData.selectedLeaves.map(id => typeof id) : null,
                              selectedLeavesIncludes: realtimeData.selectedLeaves ? realtimeData.selectedLeaves.some(id => Number(id) === Number(leaveId)) : false
                            } : null,
                            realtimeUpdatesKeys: Object.keys(realtimeUpdates),
                            allRealtimeData: realtimeUpdates,
                            checkResult: {
                              isHRAdmin: (userRole === 'hr' || userRole === 'admin'),
                              hasRealtimeData: !!realtimeData,
                              isRunning: realtimeData?.isRunning === true,
                              workTypeMatch: realtimeData?.workType === 'offhours',
                              hasSelectedLeaves: !!realtimeData?.selectedLeaves,
                              isArray: Array.isArray(realtimeData?.selectedLeaves),
                              includesLeaveId: realtimeData?.selectedLeaves?.some(id => Number(id) === Number(leaveId))
                            }
                          });
                        }
                      }
                      const requiredMinutes = row.requiredMinutes ?? Math.round((Number(row.requiredHours) || Number(row.leaveHours) || 0) * 60);
                      const fromDataMinutes = row.workedMinutes ?? Math.round((Number(row.workedHours) || 0) * 60);
                      const currentTimerSeconds = isCurrentUserRow 
                        ? offhoursTimerSeconds 
                        : (realtimeData && realtimeData.currentSeconds !== undefined ? realtimeData.currentSeconds : 0);
                      const fromTimerMinutes = requiredMinutes > 0 ? Math.min(requiredMinutes, Math.floor(currentTimerSeconds / 60)) : Math.floor(currentTimerSeconds / 60);
                      const useSyncOffhours = (isCurrentUserRow && workMode === 'offhours') || (realtimeData && realtimeData.workType === 'offhours');
                      const totalWorkedMinutes = useSyncOffhours ? Math.max(fromDataMinutes, fromTimerMinutes) : fromDataMinutes;
                      const displayWorkedMinutes = requiredMinutes > 0 ? Math.min(totalWorkedMinutes, requiredMinutes) : totalWorkedMinutes;
                      const progress = (row.progressPercent != null && !useSyncOffhours) ? Math.min(100, row.progressPercent) : (requiredMinutes > 0 ? Math.min(100, Math.round((displayWorkedMinutes / requiredMinutes) * 100)) : 0);
                      const user = allUsers.find(u => (u && (u.username === fio || u.fio === fio)));
                      const isActivelyWorking = isThisLeaveActive;
                      const activeTimerKey = isThisLeaveActive ? Math.floor(currentTimerSeconds) : 0;
                      const rowKey = `${leaveId || index}-${activeTimerKey}`;
                      const fmtOff = (s) => { try { return s ? new Date(s).toLocaleDateString('ru-RU') : ''; } catch { return s; } };
                      const leavePeriodOff = row.leaveStartDate && row.leaveEndDate
                        ? (row.leaveStartDate === row.leaveEndDate ? fmtOff(row.leaveStartDate) : `${fmtOff(row.leaveStartDate)} - ${fmtOff(row.leaveEndDate)}`)
                        : 'N/A';
                      let statusStrOff = '❌ Не начато';
                      if (isThisLeaveActive) statusStrOff = '⏱️ В работе';
                      else if (progress >= 100) statusStrOff = row.status === 'completed' ? '✅ Отработано' : '🔍 На проверке';
                      else if (displayWorkedMinutes > 0) statusStrOff = '📊 В процессе';

                      return (
                        <tr key={rowKey} style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid #2a323d' }}>
                          {offhoursFilter !== 'completed' && (
                            <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                              {isCurrentUserRow && row.status !== 'completed' && (
                                <input
                                  type="checkbox"
                                  checked={selectedLeaves.has(leaveId)}
                                  onChange={() => toggleLeaveSelection(leaveId)}
                                  style={{ cursor: 'pointer', transform: 'scale(1.2)', accentColor: '#6dd5ed' }}
                                />
                              )}
                            </td>
                          )}
                          <td style={{ padding: '12px 14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <img
                              src={row.avatar || user?.avatar || '/api/avatars/default.png'}
                              alt={fio}
                              style={{ width: 50, height: 50, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(109, 213, 237, 0.3)' }}
                              onError={(e) => { e.currentTarget.src = '/api/avatars/default.png'; }}
                            />
                            <span>{fio}</span>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>{leavePeriodOff}</td>
                          <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>{row.leaveReason || row.reason || 'Отгул'}</td>
                          <td style={{ padding: '12px 14px', color: '#ffc107' }}>
                            {formatTimeFromHours((Number(row.requiredHours) || Number(row.leaveHours) || 0))}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>
                            {isThisLeaveActive ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <span style={{ 
                                  fontWeight: 'bold',
                                  color: '#6dd5ed',
                                  fontSize: '1.1rem',
                                  fontFamily: 'monospace',
                                  letterSpacing: '0.05em'
                                }}>
                                  {formatTimerDisplay(currentTimerSeconds)}
                                </span>
                                <div style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '2px 6px',
                                  borderRadius: 8,
                                  backgroundColor: 'rgba(109, 213, 237, 0.1)',
                                  border: '1px solid rgba(109, 213, 237, 0.3)',
                                  width: 'fit-content'
                                }}>
                                  <div style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: '#6dd5ed',
                                    animation: 'pulse 1.5s infinite'
                                  }} />
                                  <span style={{ color: '#6dd5ed', fontSize: '0.75rem', fontWeight: 'bold' }}>LIVE</span>
                                </div>
                              </div>
                            ) : (
                              <span>{formatTimeFromHours(displayWorkedMinutes / 60)}</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                            <span style={{ fontSize: '1.5rem' }} title="Отработка в нерабочее время">🌙</span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{
                                flex: 1,
                                minWidth: 60,
                                height: 8,
                                background: '#2a323d',
                                borderRadius: 4,
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${Math.min(100, progress)}%`,
                                  height: '100%',
                                  background: progress >= 100 ? '#43e97b' : progress >= 50 ? '#6dd5ed' : '#ffc107',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <span style={{ color: '#a0aec0', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{progress}%</span>
                            </div>
                          </td>
                          {(userRole === 'hr' || userRole === 'admin') && (
                            <td style={{ padding: '12px 14px', color: '#a0aec0', fontSize: '0.85rem' }}>
                              {isThisLeaveActive ? (
                                <span style={{ color: '#6dd5ed' }}>{formatTimerDisplay(currentTimerSeconds)}</span>
                              ) : (
                                <span>{formatTimeFromHours(displayWorkedMinutes / 60)}</span>
                              )}
                            </td>
                          )}
                          {offhoursFilter === 'completed' && (
                            <td style={{ padding: '12px 14px', color: '#a0aec0', fontSize: '0.85rem' }}>
                              {row.completedAt ? fmtOff(row.completedAt) : '—'}
                            </td>
                          )}
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>{statusStrOff}</span>
                            {(userRole === 'hr' || userRole === 'admin') && row.status !== 'completed' && progress >= 100 && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!leaveId) return;
                                  try {
                                    await updateLeaveStatus(leaveId, 'completed');
                                    await reloadLeavesData();
                                    setNotification({ type: 'success', message: `✅ Отработка отгула подтверждена для ${fio}. Статус: Отработано.`, show: true, persistent: false });
                                    setTimeout(() => setNotification(null), 2500);
                                  } catch (e) {
                                    setNotification({ type: 'error', message: e?.message || 'Ошибка при подтверждении отработки', show: true, persistent: false });
                                    setTimeout(() => setNotification(null), 2500);
                                  }
                                }}
                                style={{
                                  marginLeft: 8,
                                  padding: '4px 10px',
                                  background: '#43e97b',
                                  color: '#1a202c',
                                  border: 'none',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  fontSize: '0.85rem'
                                }}
                              >
                                Подтвердить
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Блок для режима "Отработка по будням" */}
          {workMode === 'weekdays' && currentUser && (
            <div style={{
              background: 'linear-gradient(135deg, #2a3441 0%, #3a4553 100%)',
              borderRadius: 16,
              padding: 20,
              marginBottom: 20,
              border: '1px solid rgba(255, 193, 7, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: weekdaysTimerRunning ? '#ffc107' : '#6c757d'
                  }} />
                  <span style={{ color: '#ffc107', fontWeight: 700, fontSize: '1.1rem' }}>
                    📅 Отработка по будням
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={() => {
                      const newState = !weekdaysIsMinimized;
                      setWeekdaysIsMinimized(newState);
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 6,
                      color: '#ffffff',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                    title={weekdaysIsMinimized ? 'Развернуть таймер' : 'Свернуть таймер'}
                  >
                    {weekdaysIsMinimized ? '🔼' : '🔽'}
                  </button>
                </div>
              </div>

              {!weekdaysIsMinimized && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 20,
                  padding: '20px 0'
                }}>
                  <div style={{
                    fontSize: '3.5rem',
                    fontWeight: 'bold',
                    color: '#ffc107',
                    fontFamily: 'monospace',
                    textShadow: '0 0 20px rgba(255, 193, 7, 0.5)'
                  }}>
                    {formatTimerDisplay(weekdaysTimerSeconds)}
                  </div>
                  
                  <div style={{ display: 'flex', gap: 12 }}>
                    {(() => {
                      const _lid = selectedLeaves.size > 0 ? Array.from(selectedLeaves)[0] : null;
                      const _row = _lid ? data.find(r => (r.id || r.leaveId) === _lid) : null;
                      const _req = _row ? Math.round((Number(_row.requiredHours) || Number(_row.leaveHours) || 0) * 60) : 0;
                      const _worked = _row ? Math.round((Number(_row.workedHours) || 0) * 60) : 0;
                      const _goal = _req > 0 && (_worked >= _req || (weekdaysTimerRunning && weekdaysTimerSeconds >= _req * 60));
                      return !weekdaysTimerRunning ? (_goal ? null : (
                      <button
                        onClick={async () => {
                          if (selectedLeaves.size === 0) {
                            setNotification({
                              type: 'error',
                              message: 'Выберите отгул для отработки',
                              show: true,
                              persistent: false
                            });
                            setTimeout(() => setNotification(null), 2500);
                            return;
                          }
                          // Синхронизируемся с данными из БД перед запуском
                          const leaveId = Array.from(selectedLeaves)[0];
                          if (leaveId) {
                            const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
                            if (selectedLeave) {
                              const alreadyWorkedHours = Number(selectedLeave.workedHours) || 0;
                              const alreadyWorkedMinutes = Math.round(alreadyWorkedHours * 60);
                              const alreadyWorkedSeconds = alreadyWorkedMinutes * 60;
                              // Если таймер уже был запущен, используем сохраненное время
                              if (weekdaysTimerSeconds > 0) {
                                weekdaysSavedSecondsRef.current = weekdaysTimerSeconds;
                              } else {
                                weekdaysSavedSecondsRef.current = alreadyWorkedSeconds;
                                setWeekdaysTimerSeconds(alreadyWorkedSeconds);
                              }
                            }
                          }
                          setWeekdaysTimerRunning(true);
                          setWeekdaysStartTime(Date.now());
                          
                          // Отправляем событие о запуске таймера сразу для HR/Admin
                          if (socket && currentUser && selectedLeaves.size > 0) {
                            const leaveId = Array.from(selectedLeaves)[0];
                            const username = currentUser.username || currentUser.fio;
                            const currentSeconds = weekdaysTimerSeconds || 0;
                            socket.emit('worktime_update', {
                              userId: currentUser.id,
                              username: username,
                              date: selectedDate,
                              workedMinutes: Math.floor(currentSeconds / 60),
                              requiredMinutes: 0,
                              status: 'running',
                              isRunning: true,
                              selectedLeaves: Array.from(selectedLeaves),
                              workType: 'weekdays',
                              currentSeconds: currentSeconds
                            });
                            console.log('📤 [CLIENT Weekdays] Sent start event:', {
                              username,
                              isRunning: true,
                              currentSeconds,
                              selectedLeaves: Array.from(selectedLeaves)
                            });
                          }
                        }}
                        style={{
                          padding: '12px 24px',
                          borderRadius: 12,
                          border: '2px solid #ffc107',
                          background: 'rgba(255, 193, 7, 0.2)',
                          color: '#ffc107',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: 700
                        }}
                      >
                        {weekdaysTimerSeconds > 0 ? '▶️ Продолжить' : '▶️ Начать'}
                      </button>
                        )) : (
                      <button
                        onClick={async () => {
                          // Сохраняем ТОЧНОЕ текущее время перед остановкой
                          const currentExactSeconds = weekdaysTimerSeconds;
                          setWeekdaysTimerRunning(false);
                          
                          if (currentExactSeconds >= 60) {
                            const leaveId = Array.from(selectedLeaves)[0];
                            if (leaveId) {
                              const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
                              const requiredMinutes = selectedLeave ? Math.round((Number(selectedLeave.requiredHours) || Number(selectedLeave.leaveHours) || 0) * 60) : 0;
                              const alreadyWorkedMinutes = selectedLeave ? Math.round((Number(selectedLeave.workedHours) || 0) * 60) : 0;
                              const toSave = (requiredMinutes > 0 && currentExactSeconds >= requiredMinutes * 60)
                                ? Math.max(0, requiredMinutes - alreadyWorkedMinutes)
                                : Math.min(Math.floor((currentExactSeconds - weekdaysSavedSecondsRef.current) / 60), Math.max(0, requiredMinutes - alreadyWorkedMinutes));
                              if (toSave > 0) {
                                await saveWorktimeDataForSpecificLeave(leaveId, toSave, 'weekdays');
                                weekdaysSavedSecondsRef.current = currentExactSeconds;
                                setWeekdaysTimerSeconds(currentExactSeconds);
                                setData(prev => prev.map(r => ((r.id || r.leaveId) === leaveId && isRowForCurrentUser(r)) ? { ...r, workedHours: (alreadyWorkedMinutes + toSave) / 60 } : r));
                                if (requiredMinutes > 0 && (alreadyWorkedMinutes + toSave) >= requiredMinutes) {
                                  await updateLeaveStatus(leaveId, 'completed');
                                  await reloadLeavesData();
                                  setFilter('completed');
                                }
                              }
                            }
                          } else {
                            weekdaysSavedSecondsRef.current = currentExactSeconds;
                            setWeekdaysTimerSeconds(currentExactSeconds);
                          }
                        }}
                        style={{
                          padding: '12px 24px',
                          borderRadius: 12,
                          border: '2px solid #ffc107',
                          background: 'rgba(255, 193, 7, 0.2)',
                          color: '#ffc107',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: 700
                        }}
                      >
                        ⏸️ Пауза
                      </button>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Таблица отработки по будням — такой же вид, как во вкладке «На очереди» */}
              <div style={{ marginTop: 20, marginBottom: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
                <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                  Показано: <strong style={{ color: '#ffc107' }}>{dataForWorkTabs.filter(row => row.workType === 'weekdays' || (!row.workType && isWeekdaysWorktimeAllowed())).length}</strong> из <strong style={{ color: '#ffe082' }}>{dataForWorkTabs.length}</strong> на отработку
                  {weekdaysFilter && <span style={{ marginLeft: 8, color: '#ffc107' }}>(фильтр: {weekdaysFilter === 'completed' ? 'Отработанные' : 'На очереди'})</span>}
                </span>
              </div>
              <div style={{ background: '#1a202c', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.3)' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  background: '#1a202c',
                  color: '#e6f7ef',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 2px 16px #ffc10722, 0 0 0 2px #2193b022'
                }}>
                  <thead>
                    <tr style={{ background: '#1f2630' }}>
                      {weekdaysFilter !== 'completed' && (
                        <th style={{ padding: '12px 14px', textAlign: 'center', color: '#ffe082', fontWeight: 800, width: '50px' }}>
                          <input
                            type="checkbox"
                            onChange={(e) => {
                              if (e.target.checked) {
                                selectAllLeaves();
                              } else {
                                clearLeaveSelection();
                              }
                            }}
                            checked={selectedLeaves.size > 0 && selectedLeaves.size === data.filter(row => isRowForCurrentUser(row) && row.status !== 'completed').length}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                      )}
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>ФИО</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Период отгула</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Причина</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Требуется</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Отработано</th>
                      <th style={{ padding: '12px 14px', textAlign: 'center', color: '#ffe082', fontWeight: 800, width: '80px' }}>Тип</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Прогресс (%)</th>
                      {(userRole === 'hr' || userRole === 'admin') && (
                        <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Таймеры</th>
                      )}
                      {weekdaysFilter === 'completed' && (
                        <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Дата завершения</th>
                      )}
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataForWorkTabs.filter(row => {
                      const workType = row.workType;
                      return workType === 'weekdays' || (!workType && isWeekdaysWorktimeAllowed());
                    }).map((row, index) => {
                      const leaveId = row.id || row.leaveId;
                      const isCurrentUserRow = isRowForCurrentUser(row);
                      const fio = row.username || row.fio || '';
                      // Для админа/HR «моя строка» — по userId или по совпадению ФИО (отчёт может отдать другой ключ)
                      const isMyRowWeekdays = isCurrentUserRow || (Boolean(currentUser && (row.username || row.fio)) && normalizeUsername(row.username || row.fio || '') === normalizeUsername(currentUser.username || currentUser.fio || ''));
                      // Нормализуем username для поиска (убираем лишние пробелы)
                      const normalizedFio = fio.replace(/\s+/g, ' ').trim();
                      const normalizedRowUsername = row.username ? row.username.replace(/\s+/g, ' ').trim() : '';
                      const normalizedRowFio = row.fio ? row.fio.replace(/\s+/g, ' ').trim() : '';
                      
                      // Пробуем найти realtimeData по username или fio - проверяем все возможные варианты (включая нормализованные)
                      let realtimeData = null;
                      // Сначала пробуем точное совпадение
                      if (fio && realtimeUpdates[fio]) {
                        realtimeData = realtimeUpdates[fio];
                      } else if (row.username && realtimeUpdates[row.username]) {
                        realtimeData = realtimeUpdates[row.username];
                      } else if (row.fio && realtimeUpdates[row.fio]) {
                        realtimeData = realtimeUpdates[row.fio];
                      }
                      // Затем пробуем нормализованные версии
                      if (!realtimeData) {
                        for (const key in realtimeUpdates) {
                          const normalizedKey = key.replace(/\s+/g, ' ').trim();
                          if (normalizedKey === normalizedFio || normalizedKey === normalizedRowUsername || normalizedKey === normalizedRowFio) {
                            realtimeData = realtimeUpdates[key];
                            break;
                          }
                        }
                      }
                      const isThisLeaveActive = isMyRowWeekdays 
                        ? (selectedLeaves.has(leaveId) && weekdaysTimerRunning)
                        : ((userRole === 'hr' || userRole === 'admin') && realtimeData && realtimeData.isRunning === true && realtimeData.workType === 'weekdays' && realtimeData.selectedLeaves && Array.isArray(realtimeData.selectedLeaves) && realtimeData.selectedLeaves.some(id => Number(id) === Number(leaveId)));
                      const requiredMinutes = row.requiredMinutes ?? Math.round((Number(row.requiredHours) || Number(row.leaveHours) || 0) * 60);
                      const fromDataMinutes = row.workedMinutes ?? Math.round((Number(row.workedHours) || 0) * 60);
                      const currentTimerSeconds = isMyRowWeekdays 
                        ? weekdaysTimerSeconds 
                        : (realtimeData && realtimeData.currentSeconds !== undefined ? realtimeData.currentSeconds : 0);
                      const fromTimerMinutes = requiredMinutes > 0 ? Math.min(requiredMinutes, Math.floor(currentTimerSeconds / 60)) : Math.floor(currentTimerSeconds / 60);
                      const useSyncWeekdays = (isMyRowWeekdays && workMode === 'weekdays') || (realtimeData && realtimeData.workType === 'weekdays');
                      const totalWorkedMinutes = useSyncWeekdays ? Math.max(fromDataMinutes, fromTimerMinutes) : fromDataMinutes;
                      const displayWorkedMinutes = requiredMinutes > 0 ? Math.min(totalWorkedMinutes, requiredMinutes) : totalWorkedMinutes;
                      const progress = (row.progressPercent != null && !useSyncWeekdays) ? Math.min(100, row.progressPercent) : (requiredMinutes > 0 ? Math.min(100, Math.round((displayWorkedMinutes / requiredMinutes) * 100)) : 0);
                      const user = allUsers.find(u => (u && (u.username === fio || u.fio === fio)));
                      const activeTimerKey = isThisLeaveActive ? Math.floor(currentTimerSeconds) : 0;
                      const rowKey = `${leaveId || index}-${activeTimerKey}`;
                      const leavePeriod = row.leaveStartDate && row.leaveEndDate
                        ? (row.leaveStartDate === row.leaveEndDate
                          ? (() => { try { return new Date(row.leaveStartDate).toLocaleDateString('ru-RU'); } catch { return row.leaveStartDate; } })()
                          : `${(() => { try { return new Date(row.leaveStartDate).toLocaleDateString('ru-RU'); } catch { return row.leaveStartDate; } })()} - ${(() => { try { return new Date(row.leaveEndDate).toLocaleDateString('ru-RU'); } catch { return row.leaveEndDate; } })()}`)
                        : 'N/A';
                      let statusStr = '❌ Не начато';
                      if (isThisLeaveActive) statusStr = '⏱️ В работе';
                      else if (progress >= 100) statusStr = row.status === 'completed' ? '✅ Отработано' : '🔍 На проверке';
                      else if (displayWorkedMinutes > 0) statusStr = '📊 В процессе';

                      return (
                        <tr key={rowKey} style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid #2a323d' }}>
                          {weekdaysFilter !== 'completed' && (
                            <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                              {isCurrentUserRow && row.status !== 'completed' && (
                                <input
                                  type="checkbox"
                                  checked={selectedLeaves.has(leaveId)}
                                  onChange={() => toggleLeaveSelection(leaveId)}
                                  style={{ cursor: 'pointer', transform: 'scale(1.2)', accentColor: '#ffc107' }}
                                />
                              )}
                            </td>
                          )}
                          <td style={{ padding: '12px 14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <img
                              src={row.avatar || user?.avatar || '/api/avatars/default.png'}
                              alt={fio}
                              style={{ width: 50, height: 50, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(255, 193, 7, 0.3)' }}
                              onError={(e) => { e.currentTarget.src = '/api/avatars/default.png'; }}
                            />
                            <span>{fio}</span>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>{leavePeriod}</td>
                          <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>{row.leaveReason || row.reason || 'Отгул'}</td>
                          <td style={{ padding: '12px 14px', color: '#ffc107' }}>
                            {formatTimeFromHours((Number(row.requiredHours) || Number(row.leaveHours) || 0))}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>
                            {isThisLeaveActive ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <span style={{ fontWeight: 'bold', color: '#ffc107', fontSize: '1.1rem', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                                  {formatTimerDisplay(currentTimerSeconds)}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 8, backgroundColor: 'rgba(255, 193, 7, 0.1)', border: '1px solid rgba(255, 193, 7, 0.3)', width: 'fit-content' }}>
                                  <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ffc107', animation: 'pulse 1.5s infinite' }} />
                                  <span style={{ color: '#ffc107', fontSize: '0.75rem', fontWeight: 'bold' }}>LIVE</span>
                                </div>
                              </div>
                            ) : (
                              <span>{formatTimeFromHours(displayWorkedMinutes / 60)}</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                            <span style={{ fontSize: '1.5rem' }} title="Отработка по будням">📅</span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ color: isThisLeaveActive ? '#ffc107' : 'inherit', fontWeight: isThisLeaveActive ? 'bold' : 'normal', minWidth: '40px' }}>{progress}%</span>
                              <div style={{ width: '60px', height: '6px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{
                                  width: `${progress}%`,
                                  height: '100%',
                                  backgroundColor: progress >= 100 ? '#43e97b' : progress >= 75 ? '#ffd93d' : progress >= 50 ? '#ff9800' : '#ff6b6b',
                                  borderRadius: '3px',
                                  transition: 'width 0.3s ease',
                                  animation: isThisLeaveActive ? 'glow 2s infinite' : 'none'
                                }} />
                              </div>
                              {isThisLeaveActive && progress < 100 && <span style={{ fontSize: '0.8rem' }}>📈</span>}
                            </div>
                          </td>
                          {(userRole === 'hr' || userRole === 'admin') && (
                            <td style={{ padding: '12px 14px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem' }}>
                                {isThisLeaveActive ? (
                                  <div style={{ color: '#ffc107', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ffc107', animation: 'pulse 1.5s infinite' }} />
                                    Активен
                                  </div>
                                ) : (
                                  <span style={{ color: '#666' }}>Остановлен</span>
                                )}
                                {realtimeData && realtimeData.currentSeconds != null && (
                                  <div style={{ color: '#a3ffd1', fontSize: '0.8rem' }}>{formatTimerDisplay(realtimeData.currentSeconds)}</div>
                                )}
                              </div>
                            </td>
                          )}
                          {weekdaysFilter === 'completed' && (
                            <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>
                              {row.completedAt ? new Date(row.completedAt).toLocaleDateString('ru-RU') + ' ' + new Date(row.completedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : (selectedDate ? new Date(selectedDate).toLocaleDateString('ru-RU') : '—')}
                            </td>
                          )}
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                color: statusStr.includes('✅') ? '#43e97b' : statusStr.includes('⏱️') ? '#00bcd4' : statusStr.includes('📊') ? '#ffd93d' : statusStr.includes('🔍') ? '#ff9800' : '#ff6b6b',
                                fontWeight: statusStr.includes('⏱️') ? 'bold' : 'normal',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}>
                                {statusStr}
                                {isThisLeaveActive && (
                                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ffc107', animation: 'pulse 2s infinite', marginLeft: 4 }} />
                                )}
                              </span>
                              {(userRole === 'hr' || userRole === 'admin') && (statusStr.includes('На проверке') || row.workStatus === 'На проверке') && (
                                <button
                                  onClick={async () => {
                                    if (!leaveId) return;
                                    try {
                                      await updateLeaveStatus(leaveId, 'completed');
                                      await reloadLeavesData();
                                      setNotification({ type: 'success', message: `✅ Отработка отгула подтверждена для ${fio}. Статус: Отработано.`, show: true, persistent: false });
                                      setTimeout(() => setNotification(null), 2500);
                                    } catch (e) {
                                      setNotification({ type: 'error', message: e?.message || 'Ошибка при подтверждении отработки', show: true, persistent: false });
                                      setTimeout(() => setNotification(null), 2500);
                                    }
                                  }}
                                  style={{ marginLeft: 10, padding: '6px 12px', borderRadius: 8, border: '1px solid #43e97b', background: 'rgba(67, 233, 123, 0.15)', color: '#43e97b', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}
                                  title="Подтвердить отработку отгула (статус станет «Отработано»)"
                                >
                                  Подтвердить
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Блок для режима "Отработка по выходным" */}
          {workMode === 'weekends' && currentUser && (
            <div style={{
              background: 'linear-gradient(135deg, #2a3441 0%, #3a4553 100%)',
              borderRadius: 16,
              padding: 20,
              marginBottom: 20,
              border: '1px solid rgba(67, 233, 123, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 20
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: weekendsTimerRunning ? '#43e97b' : '#6c757d'
                  }} />
                  <span style={{ color: '#43e97b', fontWeight: 700, fontSize: '1.1rem' }}>
                    🎉 Отработка по выходным
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={() => {
                      const newState = !weekendsIsMinimized;
                      setWeekendsIsMinimized(newState);
                    }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 6,
                      color: '#ffffff',
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}
                    title={weekendsIsMinimized ? 'Развернуть таймер' : 'Свернуть таймер'}
                  >
                    {weekendsIsMinimized ? '🔼' : '🔽'}
                  </button>
                </div>
              </div>

              {!weekendsIsMinimized && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 20,
                  padding: '20px 0'
                }}>
                  <div style={{
                    fontSize: '3.5rem',
                    fontWeight: 'bold',
                    color: '#43e97b',
                    fontFamily: 'monospace',
                    textShadow: '0 0 20px rgba(67, 233, 123, 0.5)'
                  }}>
                    {formatTimerDisplay(weekendsTimerSeconds)}
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                    {/* Проверка дня недели - показываем сообщение, если сегодня не выходной */}
                    {!isTodayWeekend() && !workTimeCheckDisabled && (
                      <div style={{
                        padding: '12px 20px',
                        borderRadius: 12,
                        background: 'rgba(255, 152, 0, 0.2)',
                        border: '1px solid rgba(255, 152, 0, 0.5)',
                        color: '#ff9800',
                        fontSize: '0.9rem',
                        textAlign: 'center',
                        maxWidth: '400px'
                      }}>
                        ⚠️ Отработка по выходным доступна только в субботу и воскресенье
                      </div>
                    )}
                    
                    {(() => {
                      const _lid = selectedLeaves.size > 0 ? Array.from(selectedLeaves)[0] : null;
                      const _row = _lid ? data.find(r => (r.id || r.leaveId) === _lid) : null;
                      const _req = _row ? Math.round((Number(_row.requiredHours) || Number(_row.leaveHours) || 0) * 60) : 0;
                      const _worked = _row ? Math.round((Number(_row.workedHours) || 0) * 60) : 0;
                      const _goal = _req > 0 && (_worked >= _req || (weekendsTimerRunning && weekendsTimerSeconds >= _req * 60));
                      return !weekendsTimerRunning ? (_goal ? null : (
                      <button
                        onClick={async () => {
                          // Проверяем день недели перед запуском
                          if (!isTodayWeekend() && !workTimeCheckDisabled) {
                            setNotification({
                              type: 'error',
                              message: 'Отработка по выходным доступна только в субботу и воскресенье',
                              show: true,
                              persistent: false
                            });
                            setTimeout(() => setNotification(null), 4000);
                            return;
                          }
                          
                          if (selectedLeaves.size === 0) {
                            setNotification({
                              type: 'error',
                              message: 'Выберите отгул для отработки',
                              show: true,
                              persistent: false
                            });
                            setTimeout(() => setNotification(null), 2500);
                            return;
                          }
                          // Синхронизируемся с данными из БД перед запуском
                          const leaveId = Array.from(selectedLeaves)[0];
                          if (leaveId) {
                            const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
                            if (selectedLeave) {
                              const alreadyWorkedHours = Number(selectedLeave.workedHours) || 0;
                              const alreadyWorkedMinutes = Math.round(alreadyWorkedHours * 60);
                              const alreadyWorkedSeconds = alreadyWorkedMinutes * 60;
                              // Если таймер уже был запущен, используем сохраненное время
                              if (weekendsTimerSeconds > 0) {
                                weekendsSavedSecondsRef.current = weekendsTimerSeconds;
                              } else {
                                weekendsSavedSecondsRef.current = alreadyWorkedSeconds;
                                setWeekendsTimerSeconds(alreadyWorkedSeconds);
                              }
                            }
                          }
                          setWeekendsTimerRunning(true);
                          setWeekendsStartTime(Date.now());
                          
                          // Отправляем событие о запуске таймера сразу для HR/Admin
                          if (socket && currentUser && selectedLeaves.size > 0) {
                            const leaveId = Array.from(selectedLeaves)[0];
                            const username = currentUser.username || currentUser.fio;
                            const currentSeconds = weekendsTimerSeconds || 0;
                            socket.emit('worktime_update', {
                              userId: currentUser.id,
                              username: username,
                              date: selectedDate,
                              workedMinutes: Math.floor(currentSeconds / 60),
                              requiredMinutes: 0,
                              status: 'running',
                              isRunning: true,
                              selectedLeaves: Array.from(selectedLeaves),
                              workType: 'weekends',
                              currentSeconds: currentSeconds
                            });
                            console.log('📤 [CLIENT Weekends] Sent start event:', {
                              username,
                              isRunning: true,
                              currentSeconds,
                              selectedLeaves: Array.from(selectedLeaves)
                            });
                          }
                        }}
                        disabled={!isTodayWeekend() && !workTimeCheckDisabled}
                        style={{
                          padding: '12px 24px',
                          borderRadius: 12,
                          border: '2px solid #43e97b',
                          background: (!isTodayWeekend() && !workTimeCheckDisabled) ? 'rgba(67, 233, 123, 0.1)' : 'rgba(67, 233, 123, 0.2)',
                          color: (!isTodayWeekend() && !workTimeCheckDisabled) ? '#6c757d' : '#43e97b',
                          cursor: (!isTodayWeekend() && !workTimeCheckDisabled) ? 'not-allowed' : 'pointer',
                          fontSize: '1rem',
                          fontWeight: 700,
                          opacity: (!isTodayWeekend() && !workTimeCheckDisabled) ? 0.5 : 1
                        }}
                      >
                        {weekendsTimerSeconds > 0 ? '▶️ Продолжить' : '▶️ Начать'}
                      </button>
                        )) : (
                      <button
                        onClick={async () => {
                          const currentExactSeconds = weekendsTimerSeconds;
                          setWeekendsTimerRunning(false);
                          if (currentExactSeconds >= 60) {
                            const leaveId = Array.from(selectedLeaves)[0];
                            if (leaveId) {
                              const selectedLeave = data.find(row => (row.id || row.leaveId) === leaveId);
                              const requiredMinutes = selectedLeave ? Math.round((Number(selectedLeave.requiredHours) || Number(selectedLeave.leaveHours) || 0) * 60) : 0;
                              const alreadyWorkedMinutes = selectedLeave ? Math.round((Number(selectedLeave.workedHours) || 0) * 60) : 0;
                              const requiredSeconds = requiredMinutes * 60;
                              const newSeconds = currentExactSeconds - weekendsSavedSecondsRef.current;
                              let newWorkedMinutes = Math.floor(newSeconds / 60);
                              if (requiredSeconds > 0 && newSeconds >= requiredSeconds - 1) {
                                newWorkedMinutes = requiredMinutes;
                              }
                              const minutesToSave = Math.min(newWorkedMinutes, Math.max(0, requiredMinutes - alreadyWorkedMinutes));
                              if (minutesToSave > 0) {
                                await saveWorktimeDataForSpecificLeave(leaveId, minutesToSave, 'weekends');
                                weekendsSavedSecondsRef.current = currentExactSeconds;
                                setWeekendsTimerSeconds(currentExactSeconds);
                                if (requiredMinutes > 0 && (alreadyWorkedMinutes + minutesToSave) >= requiredMinutes) {
                                  await updateLeaveStatus(leaveId, 'completed');
                                  await reloadLeavesData();
                                  setFilter('completed');
                                }
                              }
                            }
                          } else {
                            weekendsSavedSecondsRef.current = currentExactSeconds;
                            setWeekendsTimerSeconds(currentExactSeconds);
                          }
                        }}
                        style={{
                          padding: '12px 24px',
                          borderRadius: 12,
                          border: '2px solid #43e97b',
                          background: 'rgba(67, 233, 123, 0.2)',
                          color: '#43e97b',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: 700
                        }}
                      >
                        ⏸️ Пауза
                      </button>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Таблица отработки по выходным — такой же вид, как во вкладке «На очереди» */}
              <div style={{ marginTop: 20, marginBottom: 12, padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)' }}>
                <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>
                  Показано: <strong style={{ color: '#43e97b' }}>{dataForWorkTabs.filter(row => row.workType === 'weekends' || (!row.workType && isWeekendsWorktimeAllowed())).length}</strong> из <strong style={{ color: '#ffe082' }}>{dataForWorkTabs.length}</strong> на отработку
                  {weekendsFilter && <span style={{ marginLeft: 8, color: '#43e97b' }}>(фильтр: {weekendsFilter === 'completed' ? 'Отработанные' : 'На очереди'})</span>}
                </span>
              </div>
              <div style={{ background: '#1a202c', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.3)' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  background: '#1a202c',
                  color: '#e6f7ef',
                  borderRadius: 14,
                  overflow: 'hidden',
                  boxShadow: '0 2px 16px #43e97b22, 0 0 0 2px rgba(67,233,123,0.2)'
                }}>
                  <thead>
                    <tr style={{ background: '#1f2630' }}>
                      {weekendsFilter !== 'completed' && (
                        <th style={{ padding: '12px 14px', textAlign: 'center', color: '#ffe082', fontWeight: 800, width: '50px' }}>
                          <input
                            type="checkbox"
                            onChange={(e) => { if (e.target.checked) { selectAllLeaves(); } else { clearLeaveSelection(); } }}
                            checked={selectedLeaves.size > 0 && selectedLeaves.size === data.filter(row => isRowForCurrentUser(row) && row.status !== 'completed').length}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                      )}
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>ФИО</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Период отгула</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Причина</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Требуется</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Отработано</th>
                      <th style={{ padding: '12px 14px', textAlign: 'center', color: '#ffe082', fontWeight: 800, width: '80px' }}>Тип</th>
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Прогресс (%)</th>
                      {(userRole === 'hr' || userRole === 'admin') && (
                        <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Таймеры</th>
                      )}
                      {weekendsFilter === 'completed' && (
                        <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Дата завершения</th>
                      )}
                      <th style={{ padding: '12px 14px', textAlign: 'left', color: '#ffe082', fontWeight: 800 }}>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dataForWorkTabs.filter(row => {
                      const workType = row.workType;
                      return workType === 'weekends' || (!workType && isWeekendsWorktimeAllowed());
                    }).map((row, index) => {
                      const leaveId = row.id || row.leaveId;
                      const isCurrentUserRow = isRowForCurrentUser(row);
                      const fio = row.username || row.fio || '';
                      // Нормализуем username для поиска (убираем лишние пробелы)
                      const normalizedFio = fio.replace(/\s+/g, ' ').trim();
                      const normalizedRowUsername = row.username ? row.username.replace(/\s+/g, ' ').trim() : '';
                      const normalizedRowFio = row.fio ? row.fio.replace(/\s+/g, ' ').trim() : '';
                      
                      // Пробуем найти realtimeData по username или fio - проверяем все возможные варианты (включая нормализованные)
                      let realtimeData = null;
                      // Сначала пробуем точное совпадение
                      if (fio && realtimeUpdates[fio]) {
                        realtimeData = realtimeUpdates[fio];
                      } else if (row.username && realtimeUpdates[row.username]) {
                        realtimeData = realtimeUpdates[row.username];
                      } else if (row.fio && realtimeUpdates[row.fio]) {
                        realtimeData = realtimeUpdates[row.fio];
                      }
                      // Затем пробуем нормализованные версии
                      if (!realtimeData) {
                        for (const key in realtimeUpdates) {
                          const normalizedKey = key.replace(/\s+/g, ' ').trim();
                          if (normalizedKey === normalizedFio || normalizedKey === normalizedRowUsername || normalizedKey === normalizedRowFio) {
                            realtimeData = realtimeUpdates[key];
                            break;
                          }
                        }
                      }
                      const isThisLeaveActive = isCurrentUserRow 
                        ? (selectedLeaves.has(leaveId) && weekendsTimerRunning)
                        : ((userRole === 'hr' || userRole === 'admin') && realtimeData && realtimeData.isRunning === true && realtimeData.workType === 'weekends' && realtimeData.selectedLeaves && Array.isArray(realtimeData.selectedLeaves) && realtimeData.selectedLeaves.length > 0 && realtimeData.selectedLeaves.some(id => Number(id) === Number(leaveId)));
                      const requiredMinutes = row.requiredMinutes ?? Math.round((Number(row.requiredHours) || Number(row.leaveHours) || 0) * 60);
                      const fromDataMinutes = row.workedMinutes ?? Math.round((Number(row.workedHours) || 0) * 60);
                      const currentTimerSeconds = isCurrentUserRow 
                        ? weekendsTimerSeconds 
                        : (realtimeData && realtimeData.currentSeconds !== undefined ? realtimeData.currentSeconds : 0);
                      const fromTimerMinutes = requiredMinutes > 0 ? Math.min(requiredMinutes, Math.floor(currentTimerSeconds / 60)) : Math.floor(currentTimerSeconds / 60);
                      const useSyncWeekends = (isCurrentUserRow && workMode === 'weekends') || (realtimeData && realtimeData.workType === 'weekends');
                      const totalWorkedMinutes = useSyncWeekends ? Math.max(fromDataMinutes, fromTimerMinutes) : fromDataMinutes;
                      const displayWorkedMinutes = requiredMinutes > 0 ? Math.min(totalWorkedMinutes, requiredMinutes) : totalWorkedMinutes;
                      const progress = (row.progressPercent != null && !useSyncWeekends) ? Math.min(100, row.progressPercent) : (requiredMinutes > 0 ? Math.min(100, Math.round((displayWorkedMinutes / requiredMinutes) * 100)) : 0);
                      const user = allUsers.find(u => (u && (u.username === fio || u.fio === fio)));
                      const activeTimerKey = isThisLeaveActive ? Math.floor(currentTimerSeconds) : 0;
                      const rowKey = `${leaveId || index}-${activeTimerKey}`;
                      const fmtWe = (s) => { try { return s ? new Date(s).toLocaleDateString('ru-RU') : ''; } catch { return s; } };
                      const leavePeriodWe = row.leaveStartDate && row.leaveEndDate
                        ? (row.leaveStartDate === row.leaveEndDate ? fmtWe(row.leaveStartDate) : `${fmtWe(row.leaveStartDate)} - ${fmtWe(row.leaveEndDate)}`)
                        : 'N/A';
                      let statusStrWe = '❌ Не начато';
                      if (isThisLeaveActive) statusStrWe = '⏱️ В работе';
                      else if (progress >= 100) statusStrWe = row.status === 'completed' ? '✅ Отработано' : '🔍 На проверке';
                      else if (displayWorkedMinutes > 0) statusStrWe = '📊 В процессе';

                      return (
                        <tr key={rowKey} style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid #2a323d' }}>
                          {weekendsFilter !== 'completed' && (
                            <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                              {isCurrentUserRow && row.status !== 'completed' && (
                                <input
                                  type="checkbox"
                                  checked={selectedLeaves.has(leaveId)}
                                  onChange={() => toggleLeaveSelection(leaveId)}
                                  style={{ cursor: 'pointer', transform: 'scale(1.2)', accentColor: '#43e97b' }}
                                />
                              )}
                            </td>
                          )}
                          <td style={{ padding: '12px 14px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 12 }}>
                            <img
                              src={row.avatar || user?.avatar || '/api/avatars/default.png'}
                              alt={fio}
                              style={{ width: 50, height: 50, borderRadius: 12, objectFit: 'cover', border: '2px solid rgba(67, 233, 123, 0.3)' }}
                              onError={(e) => { e.currentTarget.src = '/api/avatars/default.png'; }}
                            />
                            <span>{fio}</span>
                          </td>
                          <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>{leavePeriodWe}</td>
                          <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>{row.leaveReason || row.reason || 'Отгул'}</td>
                          <td style={{ padding: '12px 14px', color: '#ffc107' }}>
                            {formatTimeFromHours((Number(row.requiredHours) || Number(row.leaveHours) || 0))}
                          </td>
                          <td style={{ padding: '12px 14px', color: '#a3ffd1' }}>
                            {isThisLeaveActive ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <span style={{ 
                                  fontWeight: 'bold',
                                  color: '#43e97b',
                                  fontSize: '1.1rem',
                                  fontFamily: 'monospace',
                                  letterSpacing: '0.05em'
                                }}>
                                  {formatTimerDisplay(currentTimerSeconds)}
                                </span>
                                <div style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  padding: '2px 6px',
                                  borderRadius: 8,
                                  backgroundColor: 'rgba(67, 233, 123, 0.1)',
                                  border: '1px solid rgba(67, 233, 123, 0.3)',
                                  width: 'fit-content'
                                }}>
                                  <div style={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: '#43e97b',
                                    animation: 'pulse 1.5s infinite'
                                  }} />
                                  <span style={{ color: '#43e97b', fontSize: '0.75rem', fontWeight: 'bold' }}>LIVE</span>
                                </div>
                              </div>
                            ) : (
                              <span>{formatTimeFromHours(displayWorkedMinutes / 60)}</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                            <span style={{ fontSize: '1.5rem' }} title="Отработка по выходным">🎉</span>
                          </td>
                          <td style={{ padding: '12px 14px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{
                                flex: 1,
                                minWidth: 60,
                                height: 8,
                                background: '#2a323d',
                                borderRadius: 4,
                                overflow: 'hidden'
                              }}>
                                <div style={{
                                  width: `${Math.min(100, progress)}%`,
                                  height: '100%',
                                  background: progress >= 100 ? '#43e97b' : progress >= 50 ? '#43e97b' : '#ffc107',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <span style={{ color: '#a0aec0', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{progress}%</span>
                            </div>
                          </td>
                          {(userRole === 'hr' || userRole === 'admin') && (
                            <td style={{ padding: '12px 14px', color: '#a0aec0', fontSize: '0.85rem' }}>
                              {isThisLeaveActive ? (
                                <span style={{ color: '#43e97b' }}>{formatTimerDisplay(currentTimerSeconds)}</span>
                              ) : (
                                <span>{formatTimeFromHours(displayWorkedMinutes / 60)}</span>
                              )}
                            </td>
                          )}
                          {weekendsFilter === 'completed' && (
                            <td style={{ padding: '12px 14px', color: '#a0aec0', fontSize: '0.85rem' }}>
                              {row.completedAt ? fmtWe(row.completedAt) : '—'}
                            </td>
                          )}
                          <td style={{ padding: '12px 14px' }}>
                            <span style={{ color: '#a0aec0', fontSize: '0.9rem' }}>{statusStrWe}</span>
                            {(userRole === 'hr' || userRole === 'admin') && row.status !== 'completed' && progress >= 100 && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (!leaveId) return;
                                  try {
                                    await updateLeaveStatus(leaveId, 'completed');
                                    await reloadLeavesData();
                                    setNotification({ type: 'success', message: `✅ Отработка отгула подтверждена для ${fio}. Статус: Отработано.`, show: true, persistent: false });
                                    setTimeout(() => setNotification(null), 2500);
                                  } catch (e) {
                                    setNotification({ type: 'error', message: e?.message || 'Ошибка при подтверждении отработки', show: true, persistent: false });
                                    setTimeout(() => setNotification(null), 2500);
                                  }
                                }}
                                style={{
                                  marginLeft: 8,
                                  padding: '4px 10px',
                                  background: '#43e97b',
                                  color: '#1a202c',
                                  border: 'none',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  fontSize: '0.85rem'
                                }}
                              >
                                Подтвердить
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <button onClick={onRequestClose} style={{
            marginTop: 16,
            width: 'var(--modal-worktime-close-button-width, 100%)',
            height: 'var(--modal-worktime-close-button-height, 48px)',
            padding: '0 18px',
            borderRadius: 12,
            border: 'none',
            background: 'var(--modal-worktime-close-button-background, linear-gradient(90deg, #a3ffb0 0%, #ffe082 100%))',
            color: 'var(--modal-worktime-close-button-text-color, #232931)',
            fontWeight: 800,
            fontSize: '1.06rem',
            boxShadow: '0 2px 12px rgba(67,233,123,0.25)',
            cursor: 'pointer'
          }}>Закрыть</button>

          {/* Notification */}
          {notification && notification.show && (
            <div style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: notification.type === 'success' ? 'linear-gradient(135deg, #43e97b 0%, #38d9a9 100%)' : notification.type === 'info' ? 'linear-gradient(135deg, #4dabf7 0%, #339af0 100%)' : 'linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%)',
              color: '#fff',
              padding: '20px 24px',
              borderRadius: 16,
              boxShadow: notification.type === 'success' ? '0 8px 32px rgba(67,233,123,0.4)' : notification.type === 'info' ? '0 8px 32px rgba(77,171,247,0.4)' : '0 8px 32px rgba(255,107,107,0.4)',
              zIndex: 10000,
              fontSize: '1.05rem',
              fontWeight: 700,
              textAlign: 'center',
              animation: 'slideIn 0.3s ease-out',
              border: '2px solid rgba(255,255,255,0.3)',
              minWidth: 360,
              maxWidth: '80%'
            }}>
              <div style={{ marginBottom: 12 }}>{notification.message}</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={closeNotification} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#232931', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>Закрыть уведомление</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>

    {/* Модалка активности сотрудника за период отработки (только для admin/HR) — поверх основной модалки (z-index выше 100002) */}
    {activityModalLeaveId != null && typeof document !== 'undefined' && ReactDOM.createPortal(
      <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 260000
      }} onClick={() => { setActivityModalLeaveId(null); setActivityModalData(null); setActivityModalPreviewUrl(null); }}>
        <div style={{
          background: '#1a202c',
          borderRadius: 16,
          padding: 24,
          width: 'min(calc(100vw - 320px), 1200px)',
          minWidth: 'min(720px, 95vw)',
          maxWidth: '95vw',
          height: '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.4)',
          border: '1px solid rgba(109, 213, 237, 0.3)'
        }} onClick={e => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexShrink: 0 }}>
            <h3 style={{ margin: 0, color: '#6dd5ed', fontSize: '1.4rem' }}>Активность за период отработки</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {!activityModalLoading && activityModalData && (activityModalData.urls?.length > 0 || activityModalData.applications?.length > 0 || activityModalData.screenshots?.length > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    const d = activityModalData;
                    const origin = typeof window !== 'undefined' ? window.location.origin : '';
                    const fullUrl = (url) => (url && url.startsWith('/') ? origin + url : url);
                    const periodStr = d.session ? `${d.session.startedAt ? new Date(d.session.startedAt).toLocaleString('ru-RU') : '—'} — ${d.session.endedAt ? new Date(d.session.endedAt).toLocaleString('ru-RU') : '—'}` : '—';
                    const userStr = d.user ? (d.user.fio || d.user.username || 'Сотрудник') : '—';
                    let html = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Активность за период отработки</title><style>body{font-family:system-ui,sans-serif;padding:20px;color:#333;} h1{font-size:1.2rem;} .section{margin-top:20px;} .section h2{font-size:1rem;margin-bottom:8px;} ul{padding-left:20px;} img{max-width:100%;height:auto;display:block;margin:12px 0;border:1px solid #ddd;} .meta{color:#666;font-size:0.9rem;margin-bottom:16px;}</style></head><body>';
                    html += `<h1>Активность за период отработки</h1><div class="meta">Сотрудник: ${userStr}</div><div class="meta">Период: ${periodStr}</div>`;
                    if (d.urls && d.urls.length > 0) {
                      html += '<div class="section"><h2>Сайты</h2><ul>';
                      d.urls.forEach(u => {
                        const displayName = (u.windowTitle && String(u.windowTitle).trim()) || (u.url || '—');
                        const urlEsc = (u.url || '').replace(/"/g, '&quot;');
                        const nameEsc = String(displayName).replace(/</g, '&lt;').replace(/>/g, '&gt;');
                        html += `<li><strong>${nameEsc}</strong><br/><a href="${urlEsc}">${urlEsc}</a>${u.timestamp ? ' — ' + new Date(u.timestamp).toLocaleString('ru-RU') : ''}</li>`;
                      });
                      html += '</ul></div>';
                    }
                    if (d.applications && d.applications.length > 0) {
                      html += '<div class="section"><h2>Приложения</h2><ul>';
                      d.applications.forEach(a => { html += `<li>${(a.procName || a.windowTitle || '—').replace(/</g, '&lt;')}${a.timestamp ? ' — ' + new Date(a.timestamp).toLocaleString('ru-RU') : ''}</li>`; });
                      html += '</ul></div>';
                    }
                    if (d.screenshots && d.screenshots.length > 0) {
                      html += '<div class="section"><h2>Скриншоты</h2>';
                      [...d.screenshots].sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)).forEach(s => {
                        html += `<div style="margin-bottom:20px;"><img src="${fullUrl(s.url)}" alt="" /><div class="meta">${s.timestamp ? new Date(s.timestamp).toLocaleString('ru-RU') : ''}</div></div>`;
                      });
                      html += '</div>';
                    }
                    html += '</body></html>';
                    const w = window.open('', '_blank');
                    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
                  }}
                  style={{ padding: '6px 12px', background: 'rgba(109, 213, 237, 0.2)', border: '1px solid #6dd5ed', borderRadius: 8, color: '#6dd5ed', cursor: 'pointer', fontSize: '0.9rem' }}
                >
                  🖨 Печать
                </button>
              )}
              <button type="button" onClick={() => { setActivityModalLeaveId(null); setActivityModalData(null); setActivityModalPreviewUrl(null); }} style={{ background: 'transparent', border: 'none', color: '#a0aec0', cursor: 'pointer', fontSize: '1.5rem' }}>×</button>
            </div>
          </div>
          {activityModalLoading && <div style={{ color: '#a0aec0', padding: 20 }}>Загрузка…</div>}
          {!activityModalLoading && !activityModalData && <div style={{ color: '#a0aec0', padding: 20 }}>Нет данных или ошибка загрузки.</div>}
          {!activityModalLoading && activityModalData && (
            <>
              {activityModalData.user && (
                <div style={{ color: '#e6f7ef', marginBottom: 8, flexShrink: 0 }}>{activityModalData.user.fio || activityModalData.user.username || 'Сотрудник'}</div>
              )}
              {activityModalData.session && (
                <div style={{ color: '#a0aec0', fontSize: '0.9rem', marginBottom: 12, flexShrink: 0 }}>
                  Период: {activityModalData.session.startedAt ? new Date(activityModalData.session.startedAt).toLocaleString('ru-RU') : '—'} — {activityModalData.session.endedAt ? new Date(activityModalData.session.endedAt).toLocaleString('ru-RU') : '—'}
                </div>
              )}
              {(!activityModalData.session && !activityModalData.urls?.length && !activityModalData.applications?.length && !activityModalData.screenshots?.length) && (
                <div style={{ color: '#a0aec0', padding: 20 }}>Нет данных за этот период отработки.</div>
              )}
              {(activityModalData.urls?.length > 0 || activityModalData.applications?.length > 0 || activityModalData.screenshots?.length > 0) && (
                <>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #2d3748', flexShrink: 0 }}>
                    {activityModalData.urls && activityModalData.urls.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setActivityModalTab('sites')}
                        style={{
                          padding: '10px 16px',
                          background: activityModalTab === 'sites' ? 'rgba(109, 213, 237, 0.2)' : 'transparent',
                          border: 'none',
                          borderBottom: activityModalTab === 'sites' ? '2px solid #6dd5ed' : '2px solid transparent',
                          color: activityModalTab === 'sites' ? '#6dd5ed' : '#a0aec0',
                          cursor: 'pointer',
                          fontWeight: activityModalTab === 'sites' ? 700 : 500,
                          fontSize: '0.95rem'
                        }}
                      >
                        Сайты {activityModalData.urls.length > 0 && `(${activityModalData.urls.length})`}
                      </button>
                    )}
                    {activityModalData.screenshots && activityModalData.screenshots.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setActivityModalTab('screenshots')}
                        style={{
                          padding: '10px 16px',
                          background: activityModalTab === 'screenshots' ? 'rgba(109, 213, 237, 0.2)' : 'transparent',
                          border: 'none',
                          borderBottom: activityModalTab === 'screenshots' ? '2px solid #6dd5ed' : '2px solid transparent',
                          color: activityModalTab === 'screenshots' ? '#6dd5ed' : '#a0aec0',
                          cursor: 'pointer',
                          fontWeight: activityModalTab === 'screenshots' ? 700 : 500,
                          fontSize: '0.95rem'
                        }}
                      >
                        Скриншоты ({activityModalData.screenshots.length})
                      </button>
                    )}
                    {activityModalData.applications && activityModalData.applications.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setActivityModalTab('applications')}
                        style={{
                          padding: '10px 16px',
                          background: activityModalTab === 'applications' ? 'rgba(109, 213, 237, 0.2)' : 'transparent',
                          border: 'none',
                          borderBottom: activityModalTab === 'applications' ? '2px solid #6dd5ed' : '2px solid transparent',
                          color: activityModalTab === 'applications' ? '#6dd5ed' : '#a0aec0',
                          cursor: 'pointer',
                          fontWeight: activityModalTab === 'applications' ? 700 : 500,
                          fontSize: '0.95rem'
                        }}
                      >
                        Приложения ({activityModalData.applications.length})
                      </button>
                    )}
                  </div>
                  <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                    {activityModalTab === 'sites' && activityModalData.urls && activityModalData.urls.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 20, color: '#a3ffd1', fontSize: '0.95rem', paddingBottom: 20 }}>
                        {activityModalData.urls.map((u, i) => {
                          const displayName = (u.windowTitle && String(u.windowTitle).trim()) || (u.url ? (() => {
                            try {
                              const host = new URL(u.url).hostname || u.url;
                              return host.replace(/^www\./, '');
                            } catch (_) { return u.url; }
                          })() : '—');
                          return (
                            <li key={i} style={{ marginBottom: 10 }}>
                              <a href={u.url} target="_blank" rel="noopener noreferrer" style={{ color: '#6dd5ed', fontWeight: 500 }}>{displayName}</a>
                              {u.url && u.url !== displayName && (
                                <div style={{ color: '#718096', fontSize: '0.85rem', wordBreak: 'break-all', marginTop: 2 }}>{u.url}</div>
                              )}
                              {u.timestamp && <span style={{ color: '#718096', fontSize: '0.85rem', marginLeft: 8 }}>{new Date(u.timestamp).toLocaleString('ru-RU')}</span>}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {activityModalTab === 'screenshots' && activityModalData.screenshots && activityModalData.screenshots.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingBottom: 20 }}>
                        {[...activityModalData.screenshots]
                          .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
                          .map((s) => {
                          const fullImgUrl = (s.url && s.url.startsWith('/')) ? (typeof window !== 'undefined' ? window.location.origin + s.url : s.url) : (s.url || '');
                          return (
                          <div key={s.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <img src={s.url} alt="" style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8, border: '1px solid #2a323d', objectFit: 'contain', cursor: 'pointer' }} onClick={() => setActivityModalPreviewUrl(fullImgUrl)} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
                              <span style={{ color: '#888', fontSize: '0.85rem' }}>{s.timestamp ? new Date(s.timestamp).toLocaleString('ru-RU') : ''}</span>
                              <button type="button" onClick={() => setActivityModalPreviewUrl(fullImgUrl)} style={{ padding: '4px 10px', background: 'rgba(109, 213, 237, 0.2)', border: '1px solid #6dd5ed', borderRadius: 6, color: '#6dd5ed', cursor: 'pointer', fontSize: '0.85rem' }}>Просмотреть</button>
                            </div>
                          </div>
                        );})}
                      </div>
                    )}
                    {activityModalTab === 'applications' && activityModalData.applications && activityModalData.applications.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 20, color: '#a3ffd1', fontSize: '0.95rem', paddingBottom: 20 }}>
                        {activityModalData.applications.map((a, i) => (
                          <li key={i} style={{ marginBottom: 6 }}>{a.procName || a.windowTitle || '—'} {a.timestamp && <span style={{ color: '#666', marginLeft: 8 }}>{new Date(a.timestamp).toLocaleString('ru-RU')}</span>}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Просмотр скриншота в полном размере */}
      {activityModalPreviewUrl && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 260001
          }}
          onClick={() => setActivityModalPreviewUrl(null)}
        >
          <button type="button" onClick={() => setActivityModalPreviewUrl(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer', padding: '8px 16px', borderRadius: 8 }}>× Закрыть</button>
          <img src={activityModalPreviewUrl} alt="" style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
        </div>
      )}
      </>,
      document.body
    )}

    {/* Модальное окно для ввода пароля */}
    {passwordModalOpen && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001
      }}>
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 24,
          minWidth: 320,
          maxWidth: 400,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
        }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: '1.2rem',
            fontWeight: 600,
            color: '#333',
            textAlign: 'center'
          }}>
            🔐 Введите пароль
          </h3>
          
          <p style={{
            margin: '0 0 20px 0',
            fontSize: '0.9rem',
            color: '#666',
            textAlign: 'center',
            lineHeight: 1.4
          }}>
            Для переключения режима тестирования требуется пароль
          </p>

          <input
            type="password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            placeholder="Введите пароль..."
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e9ecef',
              borderRadius: 8,
              fontSize: '1rem',
              marginBottom: 20,
              outline: 'none',
              transition: 'border-color 0.2s ease',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#4dabf7'}
            onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordSubmit();
              }
            }}
            autoFocus
          />

          <div style={{
            display: 'flex',
            gap: 12,
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={closePasswordModal}
              style={{
                padding: '10px 20px',
                border: '1px solid #dee2e6',
                background: '#f8f9fa',
                color: '#6c757d',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e9ecef';
                e.currentTarget.style.borderColor = '#adb5bd';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f8f9fa';
                e.currentTarget.style.borderColor = '#dee2e6';
              }}
            >
              Отмена
            </button>
            
            <button
              onClick={handlePasswordSubmit}
              style={{
                padding: '10px 20px',
                border: '1px solid #4dabf7',
                background: '#4dabf7',
                color: 'white',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#339af0';
                e.currentTarget.style.borderColor = '#339af0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#4dabf7';
                e.currentTarget.style.borderColor = '#4dabf7';
              }}
            >
              Подтвердить
            </button>
          </div>
        </div>
      </div>
    )}
  </>
);
}

export default LeavesWorktimeModal;